import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, FormControl, IconControl, Loading } from "spark-core-dx/components";
import { useCoreContext } from "spark-core-dx/contexts";
import { useCorePermissions, useGenericManager } from "spark-core-dx/hooks";
import { SystemTypes } from "spark-core-dx/services";
import ConvokeCommandBar from "../../../components/convoke/ConvokeCommandBar";
import CardGridControl from "../../resource/components/CardGridControl";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import {
  getBatchManagementTabs,
  getExecutionControlScreen,
  getExecutionControlStubTab,
  getExecutionControlTabs,
} from "./executionControlCenterConfig";
import "./BatchManagement.scss";

const REMOTE_HOST_ACTIONS = [
  { operationName: "StartRemoteBatchHost", label: "Ensure Ready" },
  { operationName: "CheckRemoteBatchHostStatus", label: "Check" },
  { operationName: "DrainRemoteBatchHost", label: "Drain" },
  { operationName: "RestartRemoteBatchHost", label: "Restart" },
  { operationName: "StopRemoteBatchHost", label: "Stop" },
  { operationName: "RunRemoteBatchHostManualTest", label: "Test", isManualTest: true },
];

const PROCESSOR_TYPE_LABELS = {
  1: "RealTime",
  2: "Async",
  3: "Long Running",
  4: "Scheduled",
  5: "Stream",
  6: "Long Running VM",
  7: "Long Running App Svc",
  8: "Remote",
};

const EVENT_STATUS_LABELS = {
  0: "Failed",
  1: "Created",
  2: "In Progress",
  3: "Completed",
  4: "Cancelled",
  5: "Scheduled",
  6: "Paused",
};

const getEventStatusLabel = (eventStatus) => {
  const numericStatus = Number(eventStatus);
  if (
    Number.isInteger(numericStatus)
    && Object.prototype.hasOwnProperty.call(EVENT_STATUS_LABELS, numericStatus)
  ) {
    return EVENT_STATUS_LABELS[numericStatus];
  }

  const normalizedStatus = String(eventStatus ?? "")
    .trim()
    .replace(/[\s_-]+/g, "")
    .toLowerCase();
  if (!normalizedStatus) {
    return null;
  }

  return Object.values(EVENT_STATUS_LABELS)
    .find((label) => label.replace(/[\s_-]+/g, "").toLowerCase() === normalizedStatus)
    ?? String(eventStatus).trim();
};

const DESTINATION_STATUS_LABELS = {
  0: "Unavailable",
  1: "Online",
  2: "Offline",
};

const POOL_TYPE_LABELS = {
  1: "Small",
  2: "Medium",
  3: "Large",
  4: "Extra Large",
};

const REMOTE_HOST_REFERENCE_TYPE_LABELS = {
  0: "Default",
  1: "Workspace",
  2: "Pool Profile",
  3: "Shard",
  4: "Workload",
};

const REMOTE_HOST_STALE_SECONDS = 60;
const AUTO_REFRESH_INTERVAL_SECONDS = 15;
const REMOTE_HOST_STARTUP_RECOVERY_TIMEOUT_MS = 15 * 60 * 1000;
const DEBUG_LOCAL_STARTUP_RECOVERY_TIMEOUT_MS = 30 * 1000;
const MAX_MANUAL_TEST_HISTORY_ITEMS = 50;
const MAX_REMOTE_HOST_STARTUP_MESSAGES = 40;
const BATCH_MANAGEMENT_PREFERENCE_KEY = "BatchManagement";
const ACTIVE_STARTUP_EVENT_STATUSES = new Set([1, 2]);
const DEBUG_LOCAL_HOST_PROJECT_COMMAND = "dotnet run --project Convoke.Core.EventProcessorBatch --";

const unwrapResult = (response) => {
  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  return response ?? null;
};

const getNormalizedSingleResult = (response) => (Array.isArray(response) ? response[0] ?? null : response ?? null);

const getItems = (result) => {
  const items = result?.Items;
  if (!items) {
    return [];
  }

  if (Array.isArray(items)) {
    return items;
  }

  if (items.toArray instanceof Function) {
    return items.toArray();
  }

  if (typeof items[Symbol.iterator] === "function") {
    return Array.from(items);
  }

  if (items.forEach instanceof Function) {
    const collectedItems = [];
    items.forEach((item) => {
      collectedItems.push(item);
    });
    return collectedItems;
  }

  if (items.first instanceof Function) {
    return [items.first()].filter(Boolean);
  }

  return [];
};

const formatDateTime = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "-";
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return parsed.toLocaleString();
};

const formatOverviewValue = (value, fallback = "Not reported") => {
  const raw = String(value ?? "").trim();
  return raw || fallback;
};

const formatPoolProfileNodes = (poolProfile) => {
  if (!poolProfile) {
    return "Not configured";
  }

  const dedicatedNodes = poolProfile?.TargetDedicatedComputeNodes ?? 0;
  const retentionMinutes = poolProfile?.IdleNodeRetentionMinutes ?? "-";
  return `${dedicatedNodes} dedicated node(s), ${retentionMinutes} idle retention minute(s)`;
};

const formatPoolType = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "Unknown type";
  }

  return POOL_TYPE_LABELS[Number(raw)] ?? raw;
};

const formatPoolProfileImage = (poolProfile) => {
  if (!poolProfile) {
    return "Not configured";
  }

  return formatOverviewValue(poolProfile?.ImageId ?? poolProfile?.NodeAgentSkuId);
};

const formatPoolProfileNetwork = (poolProfile) => {
  if (!poolProfile) {
    return "Not configured";
  }

  const values = [
    poolProfile?.SubnetResourceId ? `Subnet: ${poolProfile.SubnetResourceId}` : "",
    poolProfile?.PublicIpResourceId ? `Public IP: ${poolProfile.PublicIpResourceId}` : "",
  ].filter(Boolean);

  return values.length ? values.join(" | ") : "No custom network resources reported";
};

const formatScheduledEndDate = (value) => {
  const parsed = getEntryDateValue(value);
  if (parsed && parsed.getUTCFullYear() > 2100) {
    return "-";
  }

  return formatDateTime(value);
};

const formatDurationCompact = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "";
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const diffMs = Date.now() - parsed.getTime();
  if (diffMs <= 0) {
    return "just now";
  }

  const totalMinutes = Math.floor(diffMs / 60000);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  if (totalDays > 0) {
    const hours = totalHours % 24;
    return hours > 0 ? `${totalDays}d ${hours}h` : `${totalDays}d`;
  }

  if (totalHours > 0) {
    const minutes = totalMinutes % 60;
    return minutes > 0 ? `${totalHours}h ${minutes}m` : `${totalHours}h`;
  }

  if (totalMinutes > 0) {
    return `${totalMinutes}m`;
  }

  return "under 1m";
};

const getEntryDateValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toDateTimeFieldValue = (value) => {
  const date = getEntryDateValue(value);
  if (!date) {
    return "";
  }

  // Spark's DateTime control converts UTC values to local time for display.
  // Supplying an already-localized value causes the offset to be applied twice.
  return date.toISOString();
};

const buildRescheduleEventDraft = (entry) => {
  const now = new Date();
  const startDate = new Date(now.getTime() + (5 * 60000));
  const previousStartDate = getEntryDateValue(entry?.ScheduledStartDate);
  const previousEndDate = getEntryDateValue(entry?.ScheduledEndDate);
  const previousDuration = previousStartDate && previousEndDate
    ? previousEndDate.getTime() - previousStartDate.getTime()
    : 0;
  const duration = previousDuration > 0 ? previousDuration : 30 * 60000;
  const endDate = new Date(startDate.getTime() + duration);
  const interval = Number(entry?.Interval ?? entry?.ScheduleInterval ?? 5);

  return {
    ScheduledStartDate: toDateTimeFieldValue(startDate),
    ScheduledEndDate: toDateTimeFieldValue(endDate),
    ScheduleInterval: Number.isFinite(interval) && interval > 0 ? interval : 5,
  };
};

const getRescheduleEventValidationMessage = (draft) => {
  const startDate = getEntryDateValue(draft?.ScheduledStartDate);
  const endDate = getEntryDateValue(draft?.ScheduledEndDate);
  const interval = Number(draft?.ScheduleInterval);

  if (!startDate) {
    return "Select a valid start date.";
  }
  if (startDate.getTime() <= Date.now()) {
    return "The start date must be in the future.";
  }
  if (!endDate) {
    return "Select a valid end date.";
  }
  if (endDate.getTime() <= startDate.getTime()) {
    return "The end date must be after the start date.";
  }
  if (!Number.isInteger(interval) || interval <= 0) {
    return "The interval must be a whole number greater than zero.";
  }

  return "";
};

const getDestinationStatusLabel = (value) => {
  if (value === undefined || value === null || value === "") {
    return "Unknown";
  }

  const numericValue = Number(value);
  return DESTINATION_STATUS_LABELS[numericValue] ?? String(value);
};

const isRemoteHostStale = (host) => {
  if (Number(host?.Status ?? 0) !== 2) {
    return false;
  }

  const heartbeatDate = getEntryDateValue(host?.LastHeartbeatDateTime);
  if (!heartbeatDate) {
    return false;
  }

  return Date.now() - heartbeatDate.getTime() > REMOTE_HOST_STALE_SECONDS * 1000;
};

const isRemoteHostStartupPending = (host) => Number(host?.Status ?? 0) === 1;

const getRemoteHostStartupAnchorDate = (host) =>
  getEntryDateValue(
    host?.LastStartupRequestedDateTime
    ?? host?.LastStartedDateTime
    ?? host?.UpdatedDate
  );

const isRemoteHostStartupRecoverable = (host) => {
  if (!isRemoteHostStartupPending(host)) {
    return false;
  }

  const anchorDate = getRemoteHostStartupAnchorDate(host);
  if (!anchorDate) {
    return false;
  }

  const recoveryTimeoutMs = host?.IsDebugLocal
    ? DEBUG_LOCAL_STARTUP_RECOVERY_TIMEOUT_MS
    : REMOTE_HOST_STARTUP_RECOVERY_TIMEOUT_MS;

  return Date.now() - anchorDate.getTime() <= recoveryTimeoutMs;
};

const hasRemoteHostStartupEvent = (host) => String(host?.StartupEventId ?? "").trim() !== "";

const isRemoteHostStartupResumable = (host) =>
  isRemoteHostStartupRecoverable(host) && hasRemoteHostStartupEvent(host);

const isStartupEventStatusActive = (eventItem) =>
  ACTIVE_STARTUP_EVENT_STATUSES.has(Number(eventItem?.EventStatus ?? eventItem?.Status ?? -1));

const getRemoteHostStateLabel = (host) => {
  if (!host) {
    return "Unknown";
  }

  if (isRemoteHostStale(host)) {
    return "Stale";
  }

  switch (Number(host?.Status ?? 0)) {
    case 1:
      return "Starting";
    case 2:
      return "Online";
    case 3:
      return "Busy";
    case 4:
      return "Offline";
    case 5:
      return "Error";
    case 6:
      return "Stopping";
    case 7:
      return "Draining";
    default:
      return String(host?.Status ?? "Unknown");
  }
};

const escapePowerShellArgument = (value) => `"${String(value ?? "").replaceAll("\"", "\\\"")}"`;

const getDebugLocalHostCommand = (host) => {
  if (!host?._id) {
    return "";
  }

  const parts = [
    DEBUG_LOCAL_HOST_PROJECT_COMMAND,
    "-host",
    "--enabled", "true",
    "--debug-local", "true",
    "--host-registration-id", escapePowerShellArgument(host._id),
    "--assigned-client-short-name", escapePowerShellArgument(host?.ClientShortName ?? "DEFAULT"),
    "--assigned-reference-type", escapePowerShellArgument(
      Number(host?.ReferenceType ?? 0) === 0
        ? "None"
        : (REMOTE_HOST_REFERENCE_TYPE_LABELS[Number(host?.ReferenceType ?? 0)] ?? "None")
    ),
  ];

  if (host?.ClientId) {
    parts.push("--assigned-client-id", escapePowerShellArgument(host.ClientId));
  }

  if (host?.ReferenceId) {
    parts.push("--assigned-reference-id", escapePowerShellArgument(host.ReferenceId));
  }

  if (host?.Endpoint) {
    parts.push("--advertised-endpoint", escapePowerShellArgument(host.Endpoint));
  }

  return parts.join(" ");
};

const getRemoteHostDisplayStateLabel = (host) => {
  const baseLabel = getRemoteHostStateLabel(host);

  if (host?.IsDebugLocal) {
    switch (baseLabel) {
      case "Starting":
        return "Local Starting";
      case "Online":
        return "Local Online";
      case "Offline":
        return "Local Offline";
      case "Error":
        return "Local Error";
      case "Busy":
        return "Local Busy";
      case "Stopping":
        return "Local Stopping";
      case "Draining":
        return "Local Draining";
      case "Stale":
        return "Local Stale";
      default:
        return `Local ${baseLabel}`;
    }
  }

  return baseLabel;
};

const getRemoteHostRouteLabel = (host) => {
  const referenceTypeNumber = Number(host?.ReferenceType ?? 0);
  const referenceTypeLabel = REMOTE_HOST_REFERENCE_TYPE_LABELS[referenceTypeNumber] ?? "Default";
  const referenceId = String(host?.ReferenceId ?? "").trim();

  if (!referenceId || referenceTypeNumber === 0) {
    return "Client | Default Route";
  }

  return `Client | ${referenceTypeLabel}: ${referenceId}`;
};

const getRemoteHostUptimeLabel = (host) => {
  const uptime = formatDurationCompact(host?.LastStartedDateTime);
  return uptime ? `Up ${uptime}` : "";
};

const getRecoveredRemoteHostStartupEntry = (host) => {
  if (!host || !isRemoteHostStartupResumable(host)) {
    return null;
  }

  const message = String(
    host?.LastStatusDetail
    ?? host?.LastErrorMessage
    ?? "Startup requested. Waiting for host registration."
  ).trim();

  if (!message) {
    return null;
  }

  return {
    id: `${host?._id ?? "host"}:recovered-startup`,
    eventId: "",
    message,
    stage: "waiting",
    uiMessageType: "status",
    timestamp: host?.LastStartupRequestedDateTime ?? host?.UpdatedDate ?? new Date().toISOString(),
    outputType: "text",
  };
};

const isRemoteHostPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return false;
  }

  return [
    "ReferenceType",
    "HostInstanceId",
    "CurrentJobId",
    "CurrentTaskId",
    "Endpoint",
    "LastHeartbeatDateTime",
    "LastStatusDetail",
  ].some((key) => Object.prototype.hasOwnProperty.call(payload, key));
};

const getPayloadStatusLabel = (payload) => {
  if (!payload) {
    return "";
  }

  const rawStatus = payload?.Status;
  if (rawStatus === undefined || rawStatus === null || rawStatus === "") {
    return "";
  }

  if (isRemoteHostPayload(payload)) {
    return getRemoteHostStateLabel(payload);
  }

  return String(rawStatus);
};

const getStatusToneClass = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (
    normalized.includes("error") ||
    normalized.includes("fail") ||
    normalized.includes("offline")
  ) {
    return "is-error";
  }

  if (
    normalized.includes("start") ||
    normalized.includes("progress") ||
    normalized.includes("scheduled") ||
    normalized.includes("stale") ||
    normalized.includes("busy") ||
    normalized.includes("creating") ||
    normalized.includes("not created") ||
    normalized.includes("not checked") ||
    normalized.includes("stop") ||
    normalized.includes("drain")
  ) {
    return "is-warn";
  }

  if (
    normalized.includes("online") ||
    normalized.includes("healthy") ||
    normalized.includes("passed")
  ) {
    return "is-ok";
  }

  return "";
};

const getActiveTaskActionKey = (entry, actionKey) =>
  `${actionKey}:${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? "task"}`;

const normalizeAzureBatchWorkRuntimeStatus = (entry) => String(
  entry?.CardStatus
    ?? entry?.RuntimeStatus
    ?? entry?.SourceStatus
    ?? ""
).trim().replace(/[\s_-]+/g, "").toLowerCase();

const AZURE_BATCH_WORK_TERMINAL_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
  "canceled",
  "terminated",
  "deleted",
]);

const AZURE_BATCH_WORK_ACTIONABLE_STATUSES = new Set([
  "active",
  "created",
  "creating",
  "scheduled",
  "queued",
  "preparing",
  "starting",
  "running",
  "inprogress",
]);

const isAzureBatchWorkCurrent = (entry) =>
  entry?.IsActive !== false
  && !AZURE_BATCH_WORK_TERMINAL_STATUSES.has(normalizeAzureBatchWorkRuntimeStatus(entry));

const isAzureBatchWorkRuntimeActionable = (entry) =>
  AZURE_BATCH_WORK_ACTIONABLE_STATUSES.has(normalizeAzureBatchWorkRuntimeStatus(entry));

const isAzureBatchWorkEventCancellable = (entry) => {
  const status = String(
    entry?.SourceStatus
      ?? entry?.CardStatus
      ?? entry?.RuntimeStatus
      ?? ""
  ).trim().replace(/[\s_-]+/g, "").toLowerCase();

  return ["created", "creating", "starting", "scheduled", "queued", "running", "inprogress", "active"].includes(status);
};

const getActiveTaskRowKey = (entry, index = 0) =>
  `${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? entry?.NodeId ?? index}`;

const getScheduledActionKey = (entry, actionKey) =>
  `${actionKey}:${entry?.EventId ?? entry?._id ?? "scheduled"}`;

const isScheduledWorkReschedulable = (entry) => {
  const scheduleStatus = String(entry?.ScheduleStatus ?? "")
    .trim()
    .replace(/[\s_-]+/g, "")
    .toLowerCase();
  if (["completed", "cancelled", "canceled"].includes(scheduleStatus)) {
    return true;
  }

  const eventStatus = EVENT_STATUS_LABELS[Number(entry?.EventStatus ?? -1)]
    ?? String(entry?.EventStatus ?? "");
  return ["completed", "cancelled", "canceled"].includes(
    eventStatus.trim().replace(/[\s_-]+/g, "").toLowerCase()
  );
};

const isScheduledWorkScheduleActive = (entry) => {
  const scheduleStatus = String(entry?.ScheduleStatus ?? "")
    .trim()
    .replace(/[\s_-]+/g, "")
    .toLowerCase();

  return scheduleStatus === "" || scheduleStatus === "active";
};

const getBackgroundActionKey = (entry, actionKey) =>
  `${actionKey}:${entry?.EventId ?? entry?._id ?? "background"}`;

const normalizeBackgroundEventStatus = (entry) => String(
  entry?.CardStatus
    ?? entry?.RuntimeStatus
    ?? EVENT_STATUS_LABELS[Number(entry?.EventStatus ?? -1)]
    ?? ""
).trim().replace(/[\s_-]+/g, "").toLowerCase();

const isBackgroundEventCancellable = (entry) =>
  ["created", "active", "inprogress", "scheduled", "paused"].includes(normalizeBackgroundEventStatus(entry));

const isBackgroundEventDeletable = (entry) =>
  ["completed", "failed", "cancelled"].includes(normalizeBackgroundEventStatus(entry));

const getBackgroundRowKey = (entry, index = 0) =>
  String(entry?._id ?? entry?.EventId ?? `background:${index}`);

const getScheduledRowKey = (entry, index = 0) =>
  String(entry?.EventId ?? entry?._id ?? `scheduled:${index}`);

const getPoolProfileRowKey = (poolProfile, index = 0) =>
  String(poolProfile?._id ?? poolProfile?.PoolId ?? `pool:${index}`);

const createExecutionCardField = (fieldName, displayName, displaySequence, cardDisplayName = displayName) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  CardDisplayName: cardDisplayName,
  DisplaySequence: displaySequence,
  ShowOnCard: true,
});

const getCardStatusTone = (status) => {
  const normalizedStatus = String(status ?? "").trim().toLowerCase();
  if (
    normalizedStatus.includes("error")
    || normalizedStatus.includes("fail")
    || normalizedStatus.includes("cancel")
    || normalizedStatus.includes("offline")
  ) {
    return "error";
  }

  if (
    normalizedStatus.includes("complete")
    || normalizedStatus.includes("done")
    || normalizedStatus.includes("success")
    || normalizedStatus.includes("pass")
    || normalizedStatus.includes("online")
    || normalizedStatus.includes("healthy")
  ) {
    return "success";
  }

  if (
    normalizedStatus.includes("progress")
    || normalizedStatus.includes("scheduled")
    || normalizedStatus.includes("running")
    || normalizedStatus.includes("creating")
    || normalizedStatus.includes("starting")
    || normalizedStatus.includes("busy")
  ) {
    return "active";
  }

  return "default";
};

const RESCHEDULE_EVENT_FORM_METADATA = {
  Name: "RescheduleEvent",
  DisplayName: "Reschedule Event",
};

const RESCHEDULE_EVENT_FORM_FIELDS = [
  {
    FieldName: "ScheduledStartDate",
    DisplayName: "Start Date",
    Description: "When the new schedule becomes active.",
    FieldType: SystemTypes.UIFieldType.DateTime,
    ValidationType: SystemTypes.UIFieldValidationType.DateTime,
    IsRequired: true,
    DisplaySequence: 1,
  },
  {
    FieldName: "ScheduledEndDate",
    DisplayName: "End Date",
    Description: "When the new schedule stops creating runs.",
    FieldType: SystemTypes.UIFieldType.DateTime,
    ValidationType: SystemTypes.UIFieldValidationType.DateTime,
    IsRequired: true,
    DisplaySequence: 2,
  },
  {
    FieldName: "ScheduleInterval",
    DisplayName: "Interval (minutes)",
    Description: "Number of minutes between scheduled runs.",
    FieldType: SystemTypes.UIFieldType.TextBox,
    ValidationType: SystemTypes.UIFieldValidationType.IntPos,
    IsRequired: true,
    DisplaySequence: 3,
  },
];

const getExecutionCardHeaderStatus = (status) => ({
  label: String(status ?? "Status"),
  title: String(status ?? "Status"),
  tone: getCardStatusTone(status),
});

const getPoolProfileCardHeaderStatus = (poolProfile) => {
  const azureStatus = String(poolProfile?.AzureStatus ?? "Not Checked").trim();
  const normalizedAzureStatus = azureStatus.toLowerCase();
  let tone = "configured";

  if (poolProfile?.IsActive === false) {
    tone = "default";
  } else if (
    normalizedAzureStatus.includes("error")
    || normalizedAzureStatus.includes("fail")
    || normalizedAzureStatus.includes("offline")
  ) {
    tone = "error";
  } else if (normalizedAzureStatus.includes("online")) {
    tone = "success";
  }

  return {
    label: azureStatus,
    title: azureStatus,
    tone,
  };
};

const getManualTestCardHeaderStatus = (status) => {
  const label = String(status ?? "Status");
  return { label, title: label, tone: getCardStatusTone(label) };
};

const normalizeAzurePoolRuntimeStatus = (status) => {
  const statusValue = String(status ?? "").trim();
  const normalizedStatus = statusValue.toLowerCase();
  if (!normalizedStatus) return "Not Checked";
  if (normalizedStatus === "not created") return "Not Created";
  if (normalizedStatus === "creating") return "Creating";
  if (normalizedStatus === "error") return "Error";
  if (normalizedStatus === "online") return "Online";
  return statusValue;
};

const getGuidOperationId = (value) => {
  const raw = String(value ?? "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw) ? raw : "";
};

const renderRowActionButtonContent = (label, isLoading = false) =>
  isLoading ? (
    <span className="row-action-content is-loading">
      <span className="row-action-spinner" />
      <span>{label}</span>
    </span>
  ) : (
    label
  );

const getDisplayMessage = (result, fallbackMessage) =>
  result?.DisplayMessage ??
  result?.Message?.DisplayMessage ??
  fallbackMessage;

const getActionResultPayload = (result) => getItems(result)[0] ?? null;

const toActionResultText = (value) => {
  if (value == null) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "object") {
    const nestedText =
      value.DisplayMessage
      ?? value.displayMessage
      ?? value.Message
      ?? value.message
      ?? value.ErrorMessage
      ?? value.errorMessage;
    if (nestedText != null && nestedText !== value) {
      return toActionResultText(nestedText);
    }
  }

  return String(value).trim();
};

const toActionResultValues = (value) => {
  if (value == null) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
};

const getActionResultDisplayMessage = (result) => {
  const payload = getActionResultPayload(result);
  const candidates = [
    result?.DisplayMessage,
    result?.displayMessage,
    result?.Message,
    result?.message,
    result?.ErrorMessage,
    result?.errorMessage,
    result?.ExceptionMessage,
    result?.exceptionMessage,
    result?.Exception?.Message,
    result?.exception?.message,
    result?.DebugErrorMessage,
    result?.debugErrorMessage,
    ...toActionResultValues(result?.MessageDetails),
    ...toActionResultValues(result?.messageDetails),
    payload?.DisplayMessage,
    payload?.Message,
    payload?.ErrorMessage,
    payload?.Details,
  ];

  const responseMessage = candidates
    .map(toActionResultText)
    .find(Boolean);
  if (responseMessage) {
    return responseMessage;
  }

  return result?.Success === true
    ? "The operation completed."
    : "The operation failed, but no error details were returned. Refresh and try again.";
};

const BACKGROUND_PROCESSING_TEST_OPERATION = "convoketest";
const BACKGROUND_PROCESSING_TEST_PENDING_MESSAGE =
  "Test queued. Waiting for the Azure Batch worker result.";
const BACKGROUND_PROCESSING_TEST_PROOF = [
  { label: "database access", match: (message) => message.includes("database access test success") },
  { label: "configured wait", match: (message) => message.includes("second wait completed") },
  { label: "file access", match: (message) => message.includes("file access test success") },
  { label: "public IP", match: (message) => message.includes("public ip test success") },
  { label: "server statistics", match: (message) => message.includes("server stats:") },
];

const getManualTestEventId = (result) => {
  const candidateValues = [
    result?.EventId,
    result?.eventId,
    result?.CorrelationID,
    result?.CorrelationId,
    result?.correlationId,
    result?.CorrelationEvent?.EventId,
    result?.CorrelationEvent?.UniqueId,
    result?.CorrelationEvent?._id,
    result?.correlationEvent?.EventId,
    result?.correlationEvent?.UniqueId,
    result?.correlationEvent?._id,
    result?.Value?.EventId,
    result?.value?.eventId,
  ];

  return candidateValues
    .map((value) => String(value ?? "").trim())
    .find(Boolean) ?? "";
};

const isBackgroundProcessingDispatchResult = (operationName, result, eventId) =>
  String(operationName ?? "").trim().toLowerCase() === BACKGROUND_PROCESSING_TEST_OPERATION
  && !!eventId
  && result?.Success !== false
  && getDisplayMessage(result, "").toLowerCase().includes("action is in progress");

const getManualTestMessageToneClass = (status) => {
  const normalizedStatus = String(status ?? "").trim().toLowerCase();
  if (normalizedStatus.includes("passed") || normalizedStatus.includes("success")) {
    return "is-ok";
  }

  if (normalizedStatus.includes("failed") || normalizedStatus.includes("error")) {
    return "is-error";
  }

  return "";
};

const getActionResultDetailLines = (result) => {
  const payload = getActionResultPayload(result);
  const displayMessage = getActionResultDisplayMessage(result);
  const detailEntries = [
    ...toActionResultValues(result?.MessageDetails),
    ...toActionResultValues(result?.messageDetails),
    result?.ErrorMessage,
    result?.errorMessage,
    result?.ExceptionMessage,
    result?.exceptionMessage,
    result?.Exception?.Message,
    result?.exception?.message,
    result?.DebugErrorMessage,
    result?.debugErrorMessage,
    ...String(payload?.Details ?? "")
      .split(/\r?\n/g)
      .map((entry) => entry.trim()),
  ].map(toActionResultText);

  return detailEntries.filter(
    (entry, index, array) =>
      !!entry &&
      entry !== displayMessage &&
      entry !== payload?.Message &&
      array.indexOf(entry) === index
  );
};

const buildRemoteHostStartupFailureResult = ({
  hostId = "",
  fallbackMessage = "Remote host startup failed.",
  result = null,
  payload = null,
  host = null,
  eventId = "",
}) => {
  const displayMessage =
    payload?.Message
    ?? host?.LastErrorMessage
    ?? host?.LastStatusDetail
    ?? result?.DisplayMessage
    ?? fallbackMessage;

  const detailEntries = [
    ...(result?.MessageDetails ?? []),
    payload?.Message,
    host?.LastErrorMessage,
    host?.LastStatusDetail,
  ]
    .map((entry) => String(entry ?? "").trim())
    .filter((entry, index, array) => !!entry && array.indexOf(entry) === index);

  const failureResult = {
    Success: false,
    DisplayMessage: displayMessage,
    MessageDetails: detailEntries.length > 0 ? detailEntries : [displayMessage],
    EventId: String(
      eventId
      ?? payload?.EventId
      ?? result?.EventId
      ?? ""
    ).trim(),
  };

  console.error("Remote host startup failed.", {
    hostId,
    failureResult,
    response: result,
    payload,
    host,
  });

  return failureResult;
};

const createManualTestHistoryEntryId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID instanceof Function) {
    return window.crypto.randomUUID();
  }

  return `manual-test:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
};

const parseJsonObject = (value) => {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    try {
      const parsedValue = JSON.parse(value);
      return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
        ? parsedValue
        : {};
    } catch {
      return {};
    }
  }

  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
};

const serializeJsonValue = (value) => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
};

const getManualTestHistoryMetadata = (entry) => {
  return parseJsonObject(entry?.Metadata ?? entry?.metadata ?? {});
};

const normalizeManualTestHistoryEntry = (entry, index = 0) => {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const metadata = getManualTestHistoryMetadata(entry);
  const result = metadata.Result ?? metadata.result ?? entry.Result ?? entry.result ?? null;
  const payload = metadata.Payload ?? metadata.payload ?? entry.Payload ?? entry.payload ?? getActionResultPayload(result);
  const operationName = String(entry.OperationName ?? entry.operationName ?? "");
  const eventId = String(
    entry.EventId
    ?? entry.eventId
    ?? metadata.EventId
    ?? metadata.eventId
    ?? getManualTestEventId(result)
    ?? ""
  ).trim();
  const eventStatus = entry.EventStatus ?? entry.eventStatus ?? metadata.EventStatus ?? metadata.eventStatus ?? null;
  const isPendingEventStatus = eventStatus === null
    || eventStatus === undefined
    || [1, 2, 5, 6].includes(Number(eventStatus));
  const isPendingDispatch = isBackgroundProcessingDispatchResult(operationName, result, eventId)
    && isPendingEventStatus;
  const checkedDateTimeValue =
    entry.CheckedDateTime
    ?? entry.checkedDateTime
    ?? payload?.CheckedDateTime
    ?? metadata.CheckedDateTime
    ?? new Date().toISOString();
  const checkedDateTime =
    getEntryDateValue(checkedDateTimeValue)?.toISOString()
    ?? String(checkedDateTimeValue);
  const rawId =
    entry._id
    ?? entry.UniqueId
    ?? entry.id
    ?? `${entry.HistoryKey ?? entry.key ?? "manual-test"}:${checkedDateTime}:${index}`;

  return {
    id: String(rawId),
    key: String(entry.HistoryKey ?? entry.Key ?? entry.key ?? ""),
    label: String(entry.Label ?? entry.label ?? "Manual Test"),
    scopeLabel: String(entry.ScopeLabel ?? entry.scopeLabel ?? ""),
    targetId: String(entry.TargetId ?? entry.targetId ?? ""),
    operationName,
    result,
    payload,
    eventId,
    eventStatus,
    checkedDateTime,
    status: String(
      isPendingDispatch
        ? "In Progress"
        : entry.Status
          ?? entry.status
          ?? payload?.Status
          ?? (result?.Success ? "Passed" : "Failed")
          ?? ""
    ),
    displayMessage: String(
      isPendingDispatch
        ? BACKGROUND_PROCESSING_TEST_PENDING_MESSAGE
        : entry.DisplayMessage
          ?? entry.displayMessage
          ?? result?.DisplayMessage
          ?? ""
    ),
  };
};

const normalizeManualTestHistory = (entries) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .map((entry, index) => normalizeManualTestHistoryEntry(entry, index))
    .filter(Boolean)
    .sort((left, right) => {
      const leftDate = getEntryDateValue(left?.checkedDateTime)?.getTime() ?? 0;
      const rightDate = getEntryDateValue(right?.checkedDateTime)?.getTime() ?? 0;
      return rightDate - leftDate;
    })
    .slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS);
};

const parseManualTestHistoryJson = (value) => {
  try {
    const parsedValue = typeof value === "string" ? JSON.parse(value) : value;
    return normalizeManualTestHistory(parsedValue);
  } catch {
    return [];
  }
};

const toManualTestHistoryPreferenceItems = (entries) =>
  normalizeManualTestHistory(entries).map((entry) => ({
    UniqueId: entry.id || createManualTestHistoryEntryId(),
    HistoryKey: entry.key ?? "",
    Label: entry.label ?? "Manual Test",
    ScopeLabel: entry.scopeLabel ?? "",
    TargetId: entry.targetId ?? "",
    OperationName: entry.operationName ?? "",
    CheckedDateTime: entry.checkedDateTime ?? new Date().toISOString(),
    Status: entry.status ?? entry.payload?.Status ?? (entry.result?.Success ? "Passed" : "Failed"),
    DisplayMessage: entry.displayMessage ?? entry.result?.DisplayMessage ?? "",
    Metadata: serializeJsonValue({
      Result: entry.result ?? null,
      Payload: entry.payload ?? getActionResultPayload(entry.result),
      EventId: entry.eventId ?? getManualTestEventId(entry.result),
      EventStatus: entry.eventStatus ?? null,
    }),
  }));

const EXECUTION_CONTROL_AZURE_BATCH_WORK_LABEL = "Azure Batch Work";
const EXECUTION_DETAIL_GROUP_SEQUENCE = ["summary", "timing", "configuration", "messages", "identifiers"];
const EXECUTION_DETAIL_GROUP_TITLES = {
  summary: "Summary",
  identifiers: "Identifiers",
  timing: "Timing",
  configuration: "Configuration",
  messages: "Messages",
};
const EXECUTION_DETAIL_SYSTEM_FIELDS = new Set([
  "_id",
  "autoscaleformulavalue",
  "azurebatchdefaultpoolid",
  "azurebatchsubscriptionid",
  "azurepoolcount",
  "checkeddatetime",
  "configuredpoolcount",
  "id",
  "isnew",
  "itemtype",
  "uniqueid",
  "createdby",
  "createdbyid",
  "createddate",
  "createddatetime",
  "updatedby",
  "updatedbyid",
  "updateddate",
  "updateddatetime",
  "ownerprofileid",
  "profiledependencyid",
  "isdeleted",
  "deleteddate",
  "deletedby",
  "deletedbyid",
  "customfields",
]);
const EXECUTION_DETAIL_FIELD_GROUPS = {
  summary: [
    "DisplayName",
    "Name",
    "ScopeType",
    "ProcessorLabel",
    "CardProcessor",
    "CardStatus",
    "SourceStatus",
    "RuntimeStatus",
    "EventStatus",
    "ScheduleStatus",
    "Status",
    "IsBatchBacked",
    "IsActive",
    "IsDefault",
  ],
  identifiers: [
    "ClientKey",
    "ClientShortName",
    "EventId",
    "HostId",
    "HostName",
    "PoolId",
    "PoolProfileId",
    "JobId",
    "TaskId",
    "NodeId",
    "JobScheduleId",
  ],
  timing: [
    "Interval",
    "CardStarted",
    "CardRunDate",
    "CardNextRun",
    "CreatedDateTime",
    "StartedDateTime",
    "CompletedDateTime",
    "ScheduledStartDate",
    "NextRunDateTime",
    "LastRunDateTime",
    "CheckedDateTime",
    "LastHeartbeatDateTime",
  ],
  configuration: [
    "PoolType",
    "VirtualMachineSize",
    "Version",
    "TargetDedicatedNodes",
    "TargetLowPriorityNodes",
    "DedicatedNodeCount",
    "LowPriorityNodeCount",
    "ResizeTimeout",
    "ImagePublisher",
    "ImageOffer",
    "ImageSku",
    "NodeAgentSkuId",
    "SubnetId",
  ],
  messages: [
    "Details",
    "DisplayMessage",
    "Message",
    "MessageDetails",
    "LastStatusDetail",
    "LastErrorMessage",
    "ErrorMessage",
    "ResizeErrors",
  ],
};
const EXECUTION_DETAIL_FIELD_LABELS = {
  CardNextRun: "Next Run",
  CardRunDate: "Run Date",
  CardStarted: "Started",
  EventId: "Event Id",
  HostId: "Host Id",
  IsBatchBacked: "Batch Backed",
  JobId: "Job Id",
  JobScheduleId: "Job Schedule Id",
  NodeId: "Node Id",
  PoolId: "Pool Id",
  PoolProfileId: "Pool Profile Id",
  TaskId: "Task Id",
  VMSize: "VM Size",
};
const EXECUTION_DETAIL_FIELD_GROUP_BY_KEY = Object.entries(EXECUTION_DETAIL_FIELD_GROUPS)
  .reduce((lookup, [groupKey, fields]) => {
    fields.forEach((field) => lookup.set(field.toLowerCase(), groupKey));
    return lookup;
  }, new Map());

const createExecutionControlAction = ({
  key,
  label,
  iconKey,
  loading = false,
  disabled = false,
  title = "",
  danger = false,
  onClick,
}) => ({
  key,
  label,
  iconKey,
  loading,
  disabled,
  title: title || label,
  danger,
  onClick,
});

const normalizeDetailKey = (key) => String(key ?? "").trim();

const isExecutionDetailSystemField = (key) => {
  const normalized = normalizeDetailKey(key).toLowerCase();
  return !normalized || EXECUTION_DETAIL_SYSTEM_FIELDS.has(normalized);
};

const isEmptyExecutionDetailValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    return Object.entries(value).filter(([key]) => !isExecutionDetailSystemField(key)).length === 0;
  }

  return false;
};

const getExecutionDetailGroupKey = (key) => {
  const normalized = normalizeDetailKey(key);
  const configuredGroup = EXECUTION_DETAIL_FIELD_GROUP_BY_KEY.get(normalized.toLowerCase());
  if (configuredGroup) {
    return configuredGroup;
  }

  if (/message|error|detail|reason/i.test(normalized)) {
    return "messages";
  }

  if (/date|time|started|completed|checked|heartbeat|run/i.test(normalized)) {
    return "timing";
  }

  if (/id$|key$|guid$|pool|job|task|host|node/i.test(normalized)) {
    return "identifiers";
  }

  if (/status|state|name|type|scope|processor|active|default/i.test(normalized)) {
    return "summary";
  }

  return "configuration";
};

const formatExecutionDetailLabel = (key) => {
  const normalized = normalizeDetailKey(key);
  if (EXECUTION_DETAIL_FIELD_LABELS[normalized]) {
    return EXECUTION_DETAIL_FIELD_LABELS[normalized];
  }

  return normalized
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\bId\b/g, "ID")
    .replace(/\bApi\b/g, "API")
    .replace(/\bVm\b/g, "VM")
    .trim();
};

const shouldFormatExecutionDetailAsDate = (key, value) => (
  typeof value === "string"
  && /date|time|started|completed|checked|heartbeat|run/i.test(key)
  && !Number.isNaN(Date.parse(value))
);

const formatExecutionDetailValue = (value, key = "", depth = 0) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (shouldFormatExecutionDetailAsDate(key, value)) {
    return formatDateTime(value);
  }

  if (Array.isArray(value)) {
    return value.length
      ? value.map((entry) => formatExecutionDetailValue(entry, key, depth + 1)).join("\n")
      : "-";
  }

  if (typeof value === "object") {
    if (depth > 1) {
      return Object.keys(value).length ? `${Object.keys(value).length} field(s)` : "-";
    }

    return Object.entries(value)
      .filter(([childKey, childValue]) => !isExecutionDetailSystemField(childKey) && !isEmptyExecutionDetailValue(childValue))
      .map(([childKey, childValue]) => `${formatExecutionDetailLabel(childKey)}: ${formatExecutionDetailValue(childValue, childKey, depth + 1)}`)
      .join("\n") || "-";
  }

  return String(value);
};

const buildExecutionDetailGroups = (payload, { mergeIdentifiersIntoSummary = false } = {}) => {
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const groups = EXECUTION_DETAIL_GROUP_SEQUENCE.reduce((lookup, groupKey) => {
    lookup[groupKey] = [];
    return lookup;
  }, {});
  const seenKeys = new Set();

  Object.entries(EXECUTION_DETAIL_FIELD_GROUPS).forEach(([groupKey, fields]) => {
    fields.forEach((fieldKey) => {
      const matchingKey = Object.keys(payload).find((key) => key.toLowerCase() === fieldKey.toLowerCase());
      if (
        !matchingKey
        || seenKeys.has(matchingKey.toLowerCase())
        || isExecutionDetailSystemField(matchingKey)
        || isEmptyExecutionDetailValue(payload[matchingKey])
      ) {
        return;
      }

      seenKeys.add(matchingKey.toLowerCase());
      groups[groupKey].push({
        key: matchingKey,
        label: formatExecutionDetailLabel(matchingKey),
        value: formatExecutionDetailValue(payload[matchingKey], matchingKey),
      });
    });
  });

  Object.entries(payload).forEach(([key, value]) => {
    const normalizedKey = key.toLowerCase();
    if (seenKeys.has(normalizedKey) || isExecutionDetailSystemField(key) || isEmptyExecutionDetailValue(value)) {
      return;
    }

    seenKeys.add(normalizedKey);
    const groupKey = getExecutionDetailGroupKey(key);
    groups[groupKey].push({
      key,
      label: formatExecutionDetailLabel(key),
      value: formatExecutionDetailValue(value, key),
    });
  });

  if (mergeIdentifiersIntoSummary && groups.identifiers.length > 0) {
    groups.summary.push(...groups.identifiers);
    groups.identifiers = [];
  }

  return EXECUTION_DETAIL_GROUP_SEQUENCE
    .map((groupKey) => ({
      key: groupKey,
      title: EXECUTION_DETAIL_GROUP_TITLES[groupKey],
      items: groups[groupKey],
    }))
    .filter((group) => group.items.length > 0);
};

const createExecutionRichFact = (label, value, options = {}) => ({
  label,
  value,
  className: options.className ?? "",
  isMonospace: options.isMonospace ?? false,
  isWide: options.isWide ?? false,
});

const isVisibleExecutionRichFact = (fact) => {
  if (!fact || fact.value === null || fact.value === undefined) {
    return false;
  }

  if (typeof fact.value === "string") {
    return fact.value.trim() !== "";
  }

  return true;
};

const renderPoolProfileFactBand = (facts) => (
  <div className="batch-management-profile-fact-band">
    {facts.filter(isVisibleExecutionRichFact).map((fact) => (
      <div className="batch-management-profile-fact" key={fact.label}>
        <span>{fact.label}: </span>
        <strong className={fact.isMonospace ? "is-monospace" : ""}>{fact.value}</strong>
      </div>
    ))}
  </div>
);

const renderPoolProfileInfoList = (facts) => {
  const visibleFacts = facts.filter(isVisibleExecutionRichFact);
  if (visibleFacts.length === 0) {
    return null;
  }

  return (
    <div className="batch-management-profile-info-list">
      {visibleFacts.map((fact) => (
        <div className={fact.isWide ? "is-wide" : ""} key={fact.label}>
          <span>{fact.label}: </span>
          <strong className={`${fact.isMonospace ? "is-monospace" : ""} ${fact.className ?? ""}`.trim()}>
            {fact.value}
          </strong>
        </div>
      ))}
    </div>
  );
};

const renderPoolProfileSection = ({ key, title, description, facts, emptyText = "" }) => {
  const visibleFacts = facts.filter(isVisibleExecutionRichFact);
  if (visibleFacts.length === 0 && !emptyText) {
    return null;
  }

  return (
    <section className="batch-management-profile-section convoke-panel" key={key}>
      <div className="batch-management-profile-section-copy">
        <h3>{title}</h3>
        {description ? <p>{description}</p> : null}
      </div>
      {visibleFacts.length > 0 ? renderPoolProfileInfoList(visibleFacts) : (
        <div className="batch-management-profile-empty-note">{emptyText}</div>
      )}
    </section>
  );
};

const renderPoolProfileDetails = (poolProfile) => {
  if (!poolProfile || typeof poolProfile !== "object") {
    return <div className="table-empty">No pool profile details are available.</div>;
  }

  const statusLabel = poolProfile?.IsActive === false ? "Disabled" : "Configured";
  const azureStatusLabel = formatOverviewValue(poolProfile?.AzureStatus, "Not Checked");
  const defaultLabel = poolProfile?.IsDefault ? "Default" : "Not Default";
  const poolTypeLabel = formatPoolType(poolProfile?.PoolType);
  const versionLabel = poolProfile?.Version ? `v${poolProfile.Version}` : "No version";
  const poolId = formatOverviewValue(poolProfile?.PoolId, "No pool id reported");
  const dedicatedNodes = poolProfile?.TargetDedicatedComputeNodes ?? 0;
  const idleRetention = poolProfile?.IdleNodeRetentionMinutes ?? 15;
  const vmSize = formatOverviewValue(poolProfile?.VirtualMachineSize, "Not configured");
  const nodeAgent = formatOverviewValue(poolProfile?.NodeAgentSkuId, "Not configured");
  const azureCurrentNodes = poolProfile?.AzureCurrentDedicatedNodes === null || poolProfile?.AzureCurrentDedicatedNodes === undefined
    ? "Not available"
    : `${poolProfile.AzureCurrentDedicatedNodes} dedicated, ${poolProfile?.AzureCurrentLowPriorityNodes ?? 0} low priority`;
  const azureTargetNodes = poolProfile?.AzureTargetDedicatedNodes === null || poolProfile?.AzureTargetDedicatedNodes === undefined
    ? "Not available"
    : `${poolProfile.AzureTargetDedicatedNodes} dedicated, ${poolProfile?.AzureTargetLowPriorityNodes ?? 0} low priority`;
  const hasCustomNetwork = !!poolProfile?.SubnetResourceId || !!poolProfile?.PublicIpResourceId || !!poolProfile?.UseUserManagedPublicIp;
  const hasCustomImage = !!poolProfile?.ImageId;

  const sections = [
    renderPoolProfileSection({
      key: "compute",
      title: "Compute & Scaling",
      description: "Sizing and warm-node policy used when this pool is created or ensured.",
      facts: [
        createExecutionRichFact("Auto Scale Interval", formatExecutionDetailValue(poolProfile?.AutoScaleInterval, "AutoScaleInterval")),
        poolProfile?.AutoScaleFormula
          ? createExecutionRichFact("Auto Scale Formula", formatExecutionDetailValue(poolProfile.AutoScaleFormula, "AutoScaleFormula"), {
            className: "is-code",
            isMonospace: true,
            isWide: true,
          })
          : null,
      ].filter(Boolean),
    }),
    renderPoolProfileSection({
      key: "image",
      title: "Image",
      description: "Operating image and Batch node agent for compute nodes.",
      facts: [
        hasCustomImage
          ? createExecutionRichFact("Custom Image ID", poolProfile.ImageId, { isMonospace: true, isWide: true })
          : null,
      ].filter(Boolean),
      emptyText: "This profile uses the standard Batch image for the selected pool type and node agent.",
    }),
    renderPoolProfileSection({
      key: "network",
      title: "Network",
      description: "Optional network controls applied during pool creation.",
      facts: [
        poolProfile?.EnableSSHAccess ? createExecutionRichFact("SSH Access", "Enabled") : null,
        poolProfile?.UseUserManagedPublicIp ? createExecutionRichFact("User Managed Public IP", "Enabled") : null,
        poolProfile?.PublicIpResourceId ? createExecutionRichFact("Public IP Resource", poolProfile.PublicIpResourceId, {
          isMonospace: true,
          isWide: true,
        }) : null,
        poolProfile?.SubnetResourceId ? createExecutionRichFact("Subnet Resource", poolProfile.SubnetResourceId, {
          isMonospace: true,
          isWide: true,
        }) : null,
      ].filter(Boolean),
      emptyText: hasCustomNetwork ? "" : "No custom subnet or public IP resources are configured for this pool profile.",
    }),
    renderPoolProfileSection({
      key: "identifiers",
      title: "Identifiers",
      description: "Stable values used to locate this profile and its Azure Batch pool.",
      facts: [
        createExecutionRichFact("Pool ID", poolId, { isMonospace: true, isWide: true }),
        createExecutionRichFact("Master Pool ID", formatOverviewValue(poolProfile?.MasterPoolId, "Not reported"), { isMonospace: true, isWide: true }),
        createExecutionRichFact("Client ID", formatOverviewValue(poolProfile?.ClientId, "Current client"), { isMonospace: true, isWide: true }),
      ],
    }),
  ].filter(Boolean);

  return (
    <div className="batch-management-profile-details">
      <section className="batch-management-profile-hero convoke-panel">
        <div className="batch-management-profile-hero-main">
          <div className="batch-management-profile-hero-icon" aria-hidden="true">
            <span className="material-symbols-rounded">desktop_cloud</span>
          </div>
          <div className="batch-management-profile-hero-copy">
            <span>Pool Profile: </span>
            <h3>{formatOverviewValue(poolProfile?.Name, "Unnamed profile")}</h3>
            <p className="is-monospace">{poolId}</p>
          </div>
        </div>
        <div className="batch-management-profile-chip-row">
          <span className={`table-status-chip ${getStatusToneClass(azureStatusLabel)}`}>{`Azure: ${azureStatusLabel}`}</span>
          {" "}
          <span className={`table-status-chip ${getStatusToneClass(statusLabel)}`}>{statusLabel}</span>
          {" "}
          <span className="table-status-chip">{defaultLabel}</span>
          {" "}
          <span className="table-status-chip">{poolTypeLabel}</span>
          {" "}
          <span className="table-status-chip">{versionLabel}</span>
        </div>
      </section>

      <div className="batch-management-profile-spacer" aria-hidden="true">&nbsp;</div>

      {renderPoolProfileFactBand([
        createExecutionRichFact("Azure Status", azureStatusLabel),
        createExecutionRichFact("Allocation State", formatOverviewValue(poolProfile?.AzureAllocationState, "Not available")),
        createExecutionRichFact("Azure Current Nodes", azureCurrentNodes),
        createExecutionRichFact("Azure Target Nodes", azureTargetNodes),
        createExecutionRichFact("VM Size", vmSize, { isMonospace: true }),
        createExecutionRichFact("Target Nodes", `${dedicatedNodes} dedicated`),
        createExecutionRichFact("Idle Retention", `${idleRetention ?? "-"} min`),
        createExecutionRichFact("Node Agent", nodeAgent, { isMonospace: true }),
      ])}

      <div className="batch-management-profile-spacer" aria-hidden="true">&nbsp;</div>

      {sections.map((section, index) => (
        <React.Fragment key={section.key ?? `profile-section:${index}`}>
          {section}
          {index < sections.length - 1 ? (
            <div className="batch-management-profile-section-spacer" aria-hidden="true">&nbsp;</div>
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
};

const renderExecutionDetailGroups = (payload, options = {}) => {
  const detailGroups = buildExecutionDetailGroups(payload, options);
  if (detailGroups.length === 0) {
    return <div className="table-empty">No displayable details are available.</div>;
  }

  return (
    <div className="batch-management-row-details">
      {detailGroups.map((group) => (
        <section className="batch-management-row-details-group" key={group.key}>
          <div className="batch-management-row-details-group-header">
            <h3>{group.title}</h3>
            <span>{`${group.items.length} ${group.items.length === 1 ? "field" : "fields"}`}</span>
          </div>
          <dl className="batch-management-row-details-grid">
            {group.items.map((item) => {
              const isMultiline = String(item.value ?? "").includes("\n");
              const isWide = group.key === "messages" || isMultiline || String(item.value ?? "").length > 96;
              const isMonospace = /id$|key$|url|endpoint|pool|job|task|node/i.test(item.key);
              return (
                <div
                  className={`batch-management-row-detail-card ${isWide ? "is-wide" : ""}`.trim()}
                  key={`${group.key}:${item.key}`}
                >
                  <dt>{item.label}: </dt>
                  <dd className={`${isMonospace ? "is-monospace" : ""} ${isMultiline ? "is-multiline" : ""}`.trim()}>
                    {item.value}
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}
    </div>
  );
};

const renderExecutionDetails = (payload, formatterKey = "") => {
  if (formatterKey === "manual-test-history") {
    return renderManualTestHistoryDetails(payload);
  }

  if (formatterKey === "pool-profile") {
    return renderPoolProfileDetails(payload);
  }

  return renderExecutionDetailGroups(payload);
};

const getDiagnosticSnapshotRecordTitle = (payload, index = 0) => (
  payload?.DisplayName
  ?? payload?.Name
  ?? payload?.APIName
  ?? payload?.HostName
  ?? payload?.PoolId
  ?? payload?.EventId
  ?? payload?.JobId
  ?? `Record ${index + 1}`
);

const getDiagnosticSnapshotRecordMeta = (payload) => [
  payload?.CardStatus,
  payload?.RuntimeStatus,
  payload?.SourceStatus,
  payload?.Status,
  payload?.EventStatus !== undefined && payload?.EventStatus !== null
    ? EVENT_STATUS_LABELS[Number(payload.EventStatus)] ?? payload.EventStatus
    : "",
  payload?.ScheduleStatus,
  payload?.ScopeType,
  payload?.ProcessorLabel,
  payload?.PoolId,
]
  .filter((entry) => entry !== null && entry !== undefined && String(entry).trim() !== "")
  .filter((entry, index, array) => array.indexOf(entry) === index);

const getAzureBatchDiagnosticStateGroup = (payload) => {
  const runtimeStatus = normalizeAzureBatchWorkRuntimeStatus(payload);
  if (Number(payload?.FailedTaskCount ?? 0) > 0 || runtimeStatus === "failed") {
    return "Failed";
  }

  return isAzureBatchWorkCurrent(payload) ? "Current" : "History";
};

const DiagnosticSnapshotRecord = ({ payload, index = 0, collapsible = false }) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const isExpanded = !collapsible || detailsExpanded;
  const title = getDiagnosticSnapshotRecordTitle(payload, index);

  return (
    <article className={`batch-management-diagnostic-snapshot-record convoke-panel${isExpanded ? " is-expanded" : ""}`}>
      <div className="batch-management-diagnostic-snapshot-record-header">
        <div className="batch-management-diagnostic-snapshot-record-heading">
          <h4>{title}</h4>
          {getDiagnosticSnapshotRecordMeta(payload).length ? (
            <span>{getDiagnosticSnapshotRecordMeta(payload).join(" | ")}</span>
          ) : null}
        </div>
        {collapsible ? (
          <ButtonControl
            type="standard"
            className="row-action-button batch-management-diagnostic-snapshot-toggle"
            onClick={() => setDetailsExpanded((current) => !current)}
          >
            {isExpanded ? `Hide details for ${title}` : `Show details for ${title}`}
          </ButtonControl>
        ) : null}
      </div>
      {isExpanded ? renderExecutionDetailGroups(payload) : null}
    </article>
  );
};

const renderDiagnosticSnapshotPayload = (payload, options = {}) => {
  if (!payload || (Array.isArray(payload) && payload.length === 0)) {
    return <div className="table-empty">No snapshot data is available.</div>;
  }

  if (Array.isArray(payload)) {
    const stateCounts = options.isAzureBatchWorkSnapshot
      ? payload.reduce((counts, entry) => ({
        ...counts,
        [getAzureBatchDiagnosticStateGroup(entry)]: counts[getAzureBatchDiagnosticStateGroup(entry)] + 1,
      }), { Current: 0, History: 0, Failed: 0 })
      : null;

    return (
      <div className="batch-management-diagnostic-snapshot-list">
        {stateCounts ? (
          <div className="batch-management-diagnostic-snapshot-summary" aria-label="Azure Batch work state summary">
            {Object.entries(stateCounts).map(([label, count]) => (
              <div className="convoke-fact" key={label}>
                <span className="convoke-fact__label">{label}</span>
                <strong className="convoke-fact__value">{count}</strong>
              </div>
            ))}
          </div>
        ) : null}
        {payload.map((entry, index) => (
          <DiagnosticSnapshotRecord
            key={`${getDiagnosticSnapshotRecordTitle(entry, index)}:${index}`}
            payload={entry}
            index={index}
            collapsible={options.isAzureBatchWorkSnapshot}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="batch-management-diagnostic-snapshot-detail">
      {renderExecutionDetailGroups(payload, options)}
    </div>
  );
};

const getRemoteHostLifecycleDetail = (host) => {
  if (host?.LastStatusDetail) {
    return host.LastStatusDetail;
  }

  if (host?.LastErrorMessage) {
    return host.LastErrorMessage;
  }

  if (host?.IsDebugLocal) {
    if (Number(host?.Status ?? 0) === 1) {
      return "Waiting for debug-local host registration.";
    }

    if (Number(host?.Status ?? 0) === 5 || Number(host?.Status ?? 0) === 4) {
      return "Debug-local host is not running.";
    }
  }

  return host?.Endpoint ? "Host registration is active." : "No recent lifecycle detail.";
};

const createManualTestHistoryEntry = (manualTestOption, result) => {
  const payload = getActionResultPayload(result);
  const checkedDateTime = payload?.CheckedDateTime ?? new Date().toISOString();
  const eventId = getManualTestEventId(result);
  const isBackgroundProcessingTest = manualTestOption?.isBackgroundProcessingTest === true;
  const isQueued = isBackgroundProcessingTest && result?.Success !== false && !!eventId;
  const hasUntrackableDispatch = isBackgroundProcessingTest && result?.Success !== false && !eventId;

  return {
    id: createManualTestHistoryEntryId(),
    key: manualTestOption?.key ?? "",
    label: manualTestOption?.label ?? "Manual Test",
    scopeLabel: manualTestOption?.scopeLabel ?? "",
    targetId: manualTestOption?.targetId ?? "",
    operationName: manualTestOption?.operationName ?? "",
    result,
    payload,
    eventId,
    eventStatus: isQueued ? 1 : null,
    checkedDateTime,
    status: isQueued
      ? "In Progress"
      : hasUntrackableDispatch
        ? "Dispatch Error"
        : payload?.Status ?? (result?.Success ? "Passed" : "Failed"),
    displayMessage: isQueued
      ? BACKGROUND_PROCESSING_TEST_PENDING_MESSAGE
      : hasUntrackableDispatch
        ? "The Batch test was dispatched, but its Event ID was not returned, so completion cannot be tracked."
        : result?.DisplayMessage ?? "",
  };
};

const getBackgroundProcessEventId = (backgroundProcess) => String(
  backgroundProcess?.EventId
  ?? backgroundProcess?._id
  ?? backgroundProcess?.UniqueId
  ?? ""
).trim();

const reconcileBackgroundProcessingHistoryEntry = (entry, backgroundProcess) => {
  if (!entry || !backgroundProcess) {
    return entry;
  }

  const eventStatusValue = backgroundProcess?.EventStatus;
  const numericEventStatus = Number(eventStatusValue);
  const normalizedEventStatus = String(eventStatusValue ?? "")
    .trim()
    .replace(/[\s_-]+/g, "")
    .toLowerCase();
  const matchedEventStatus = Object.entries(EVENT_STATUS_LABELS)
    .find(([, label]) => label.replace(/[\s_-]+/g, "").toLowerCase() === normalizedEventStatus);
  const rawEventStatus = normalizedEventStatus && Number.isFinite(numericEventStatus)
    ? numericEventStatus
    : Number(matchedEventStatus?.[0] ?? -1);
  const scheduleStatus = String(backgroundProcess?.ScheduleStatus ?? "")
    .trim()
    .replace(/[\s_-]+/g, "")
    .toLowerCase();
  let eventStatus = rawEventStatus;
  if ([1, 2, 5, 6].includes(rawEventStatus)) {
    if (["completed", "expired"].includes(scheduleStatus)) {
      eventStatus = 3;
    } else if (["cancelled", "canceled"].includes(scheduleStatus)) {
      eventStatus = 4;
    } else if (["failed", "error"].includes(scheduleStatus)) {
      eventStatus = 0;
    }
  }
  const eventMessages = (Array.isArray(backgroundProcess?.EventProcessMessages)
    ? backgroundProcess.EventProcessMessages
    : [])
    .map((message) => String(message ?? "").trim())
    .filter(Boolean);
  const completedDateTime =
    backgroundProcess?.CompletedDateTime
    ?? backgroundProcess?.CompletedDate
    ?? backgroundProcess?.CancelledDate
    ?? backgroundProcess?.RunDate
    ?? entry.checkedDateTime;

  if ([1, 2, 5, 6].includes(eventStatus)) {
    const status = EVENT_STATUS_LABELS[eventStatus] === "Scheduled"
      ? "Scheduled"
      : EVENT_STATUS_LABELS[eventStatus] === "Paused"
        ? "Paused"
        : "In Progress";

    return {
      ...entry,
      eventStatus,
      status,
      displayMessage: status === "In Progress"
        ? BACKGROUND_PROCESSING_TEST_PENDING_MESSAGE
        : `The background processing test is ${status.toLowerCase()}.`,
    };
  }

  if (eventStatus === 0 || eventStatus === 4) {
    const status = EVENT_STATUS_LABELS[eventStatus];
    const displayMessage = eventStatus === 0
      ? "Background processing test failed."
      : "Background processing test was cancelled.";

    return {
      ...entry,
      eventStatus,
      checkedDateTime: completedDateTime,
      status,
      displayMessage,
      result: {
        Success: false,
        DisplayMessage: displayMessage,
        MessageDetails: eventMessages,
        EventId: entry.eventId,
      },
      payload: {
        TestName: entry.label,
        Status: status,
        CheckedDateTime: completedDateTime,
        Details: eventMessages.join("\n"),
      },
    };
  }

  if (eventStatus !== 3) {
    return entry;
  }

  const normalizedMessages = eventMessages.map((message) => message.toLowerCase());
  const missingProof = BACKGROUND_PROCESSING_TEST_PROOF
    .filter((proof) => !normalizedMessages.some(proof.match))
    .map((proof) => proof.label);
  const passed = missingProof.length === 0;
  const status = passed ? "Passed" : "Needs Review";
  const displayMessage = passed
    ? "Background processing test passed."
    : "The Batch event completed, but the worker did not return complete test evidence.";
  const messageDetails = passed
    ? eventMessages
    : [
      ...eventMessages,
      `Missing completion evidence: ${missingProof.join(", ")}.`,
      "Deploy the updated EventProcessorBatch worker before treating this test as passed.",
    ];

  return {
    ...entry,
    eventStatus,
    checkedDateTime: completedDateTime,
    status,
    displayMessage,
    result: {
      Success: passed,
      DisplayMessage: displayMessage,
      MessageDetails: messageDetails,
      EventId: entry.eventId,
    },
    payload: {
      TestName: entry.label,
      Status: status,
      CheckedDateTime: completedDateTime,
      Details: messageDetails.join("\n"),
    },
  };
};

const getManualTestOptions = (remoteHosts) => [
  {
    key: "azure-batch",
    operationName: "RunAzureBatchManualTest",
    label: "Azure Batch Connection Check",
    description: "Confirms this environment can connect to the configured Azure Batch account and resolve its default pool configuration. It does not create a job or run a task.",
    scopeLabel: "Azure Batch Account",
    targetId: "",
    isHostTest: false,
  },
  ...remoteHosts.map((host) => ({
    key: `host:${host?._id}`,
    operationName: "RunRemoteBatchHostManualTest",
    label: `${host?.Name ?? "Remote Batch Host"} | Test`,
    description: "Runs one end-to-end remote test through the normal dispatch path and executes server-side capability checks on the host.",
    scopeLabel: host?.ClientShortName ?? "DEFAULT",
    targetId: host?._id ?? "",
    isHostTest: true,
    status: getRemoteHostStateLabel(host),
    endpoint: host?.Endpoint ?? "",
  })),
].filter((entry) => !!entry.targetId || !entry.isHostTest);

const getBackgroundProcessingManualTestOptions = (poolProfiles) =>
  poolProfiles
    .filter((poolProfile) => poolProfile?.IsDefault === true && poolProfile?.IsActive !== false)
    .flatMap((poolProfile) => [
      {
        key: `background-processing:${poolProfile?._id}`,
        operationName: "convoketest",
        label: "Background Processing Test",
        description: "Queues a real Azure Batch job on the active default pool and checks database access, a 10-second wait, file access, outbound public IP, and worker system statistics.",
        scopeLabel: "Default Pool",
        targetId: poolProfile?._id ?? "",
        isPoolTest: true,
        isBackgroundProcessingTest: true,
        poolId: poolProfile?.PoolId ?? "",
        processorType: 3,
      },
      {
        key: `background-termination:${poolProfile?._id}`,
        operationName: "convoketest",
        label: "Background Termination Test",
        description: "Queues a real, non-scheduled Azure Batch job on the active default pool and waits three minutes before continuing the standard processing checks. Use the longer running window to test Terminate Job.",
        scopeLabel: "Default Pool",
        targetId: poolProfile?._id ?? "",
        isPoolTest: true,
        isBackgroundProcessingTest: true,
        poolId: poolProfile?.PoolId ?? "",
        processorType: 3,
        queryParams: {
          waitseconds: 180,
        },
      },
      {
        key: `scheduled-processing:${poolProfile?._id}`,
        operationName: "convoketest",
        label: "Scheduled Processing Test",
        description: "Schedules the same Azure Batch processing test as an Event on the active default pool every five minutes and stops it after 30 minutes. It does not create or test a scheduling template.",
        scopeLabel: "Default Pool",
        targetId: poolProfile?._id ?? "",
        isPoolTest: true,
        isBackgroundProcessingTest: true,
        isScheduledProcessingTest: true,
        poolId: poolProfile?.PoolId ?? "",
        processorType: 4,
        scheduleDurationMinutes: 30,
        queryParams: {
          recurrenceinterval: 5,
        },
      },
    ])
    .filter((entry) => !!entry.targetId);

const getPoolManualTestOptions = (poolProfiles) =>
  poolProfiles.map((poolProfile) => ({
    key: `pool:${poolProfile?._id}`,
    operationName: "CheckAzureBatchPoolStatus",
    label: poolProfile?.IsDefault
      ? "Default Azure Pool Status"
      : `${poolProfile?.Name ?? "Azure Pool"} Status`,
    description: `Checks whether Azure Batch pool ${poolProfile?.PoolId ?? "(unknown)"} exists and reports its allocation, node, and resize state. It does not create or resize the pool.`,
    scopeLabel: poolProfile?.IsDefault ? "Default Pool" : "Pool Profile",
    targetId: poolProfile?._id ?? "",
    isPoolTest: true,
    poolId: poolProfile?.PoolId ?? "",
  })).filter((entry) => !!entry.targetId);

const getManualTestOptionTypeLabel = (option) => {
  if (option?.isScheduledProcessingTest) {
    return "Scheduled Batch Test";
  }

  if (option?.isBackgroundProcessingTest) {
    return "End-to-End Batch Test";
  }

  if (option?.isPoolTest) {
    return "Azure Pool Status";
  }

  if (option?.isHostTest) {
    return "Remote Host Test";
  }

  return "Connection Check";
};

const getManualTestTargetLabel = (option) => {
  if (option?.poolId) {
    return option.poolId;
  }

  if (option?.endpoint) {
    return option.endpoint;
  }

  if (option?.targetId) {
    return option.targetId;
  }

  return "Azure Batch account";
};

const sortPoolProfiles = (poolProfiles) =>
  [...poolProfiles].sort((left, right) => {
    const leftActive = left?.IsActive === false ? 0 : 1;
    const rightActive = right?.IsActive === false ? 0 : 1;
    if (leftActive !== rightActive) {
      return rightActive - leftActive;
    }

    const leftDefault = left?.IsDefault === true ? 1 : 0;
    const rightDefault = right?.IsDefault === true ? 1 : 0;
    if (leftDefault !== rightDefault) {
      return rightDefault - leftDefault;
    }

    return String(left?.Name ?? "").localeCompare(String(right?.Name ?? ""), undefined, {
      sensitivity: "base",
    });
  });

const ACTIVE_TASK_CARD_FIELDS = [
  createExecutionCardField("DisplayName", "Job Id", 1),
  createExecutionCardField("CardStatus", "Status", 2),
  createExecutionCardField("PoolId", "Pool", 3),
  createExecutionCardField("CardTasks", "Tasks", 4),
  createExecutionCardField("CardStarted", "Started", 5),
];

const BACKGROUND_CARD_FIELDS = [
  createExecutionCardField("DisplayName", "Name", 1),
  createExecutionCardField("CardStatus", "Status", 2),
  createExecutionCardField("CardProcessor", "Processor", 3),
  createExecutionCardField("CardRunDate", "Run Date", 4, "Run"),
  createExecutionCardField("CardExitCode", "Exit Code", 5),
];

const SCHEDULED_CARD_FIELDS = [
  createExecutionCardField("DisplayName", "Name", 1),
  createExecutionCardField("EventStatus", "Event Status", 2, "Event"),
  createExecutionCardField("ScheduleStatus", "Schedule Status", 3, "Schedule"),
  createExecutionCardField("CardNextRun", "Next Run", 4),
];

const POOL_PROFILE_CARD_FIELDS = [
  createExecutionCardField("PoolId", "Pool", 1),
  createExecutionCardField("CardStatus", "Configuration", 2),
  createExecutionCardField("AzureStatus", "Azure Status", 3, "Azure"),
  createExecutionCardField("IsDefault", "Default", 4),
  createExecutionCardField("VirtualMachineSize", "VM Size", 5, "VM"),
];

const MANUAL_TEST_HISTORY_CARD_FIELDS = [
  createExecutionCardField("DisplayName", "Test", 1),
  createExecutionCardField("CardStatus", "Status", 2),
  createExecutionCardField("CardScope", "Scope", 3),
  createExecutionCardField("CardChecked", "Checked", 4),
];

const MANUAL_TEST_OPTION_CARD_FIELDS = [
  createExecutionCardField("DisplayName", "Test", 1),
  createExecutionCardField("CardType", "Type", 2),
  createExecutionCardField("CardScope", "Scope", 3),
  createExecutionCardField("CardTarget", "Target", 4),
];

const EXECUTION_CARD_GRID_ACTIVE_FILTER_OPTIONS = [
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
  { key: "both", label: "Both" },
];

const AZURE_BATCH_WORK_FILTER_OPTIONS = [
  { key: "current", label: "Current", values: ["Current"] },
  { key: "history", label: "History", values: ["History"] },
  { key: "failed", label: "Failed", values: ["Failed"] },
  { key: "all", label: "All", values: [] },
];

const POOL_PROFILE_CONFIG_FILTER_OPTIONS = [
  { key: "active", label: "Configured" },
  { key: "inactive", label: "Profile Disabled" },
  { key: "both", label: "Both" },
];

const EXECUTION_STATUS_FILTER_OPTIONS = Object.entries(EVENT_STATUS_LABELS).map(([statusKey, label]) => ({
  key: label.toLowerCase().replace(/\s+/g, "-"),
  label,
  values: [Number(statusKey), statusKey, label, label.toLowerCase()],
}));

const EXECUTION_POOL_PROFILE_DEFAULT_FILTER_OPTIONS = [
  { key: "default", label: "Default", values: [true, "true", "True", "Default"] },
  { key: "not-default", label: "Not Default", values: [false, "false", "False", "Not Default"] },
];

const getExecutionCaseInsensitiveFieldValue = (row, fieldName) => {
  if (!row || !fieldName) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(row, fieldName)) {
    return row[fieldName];
  }

  const matchingKey = Object.keys(row).find((key) => String(key).toLowerCase() === String(fieldName).toLowerCase());
  return matchingKey ? row[matchingKey] : undefined;
};

const normalizeExecutionFilterValue = (value) => String(value ?? "").trim();

const hasExecutionFilterField = (rows, fieldName) =>
  rows.some((row) => getExecutionCaseInsensitiveFieldValue(row, fieldName) !== undefined);

const optionMatchesAvailableFilterValues = (option, availableValues) => {
  const rawValues = option?.values ?? option?.Values ?? option?.value ?? option?.Value ?? option?.label ?? option?.Label;
  const values = Array.isArray(rawValues) ? rawValues : [rawValues];
  return values.some((value) => availableValues.has(normalizeExecutionFilterValue(value)));
};

const buildExecutionCardGridFilterOptions = (rows, fieldName, preferredOptions = [], allTitle = "All filters") => {
  const availableValues = new Set(
    rows
      .map((row) => normalizeExecutionFilterValue(getExecutionCaseInsensitiveFieldValue(row, fieldName)))
      .filter(Boolean)
  );

  if (!availableValues.size) {
    return [];
  }

  const matchedValues = new Set();
  const preferred = preferredOptions
    .filter((option) => optionMatchesAvailableFilterValues(option, availableValues))
    .map((option) => {
      const rawValues = option?.values ?? option?.Values ?? option?.value ?? option?.Value ?? option?.label ?? option?.Label;
      const values = Array.isArray(rawValues) ? rawValues : [rawValues];
      values.forEach((value) => matchedValues.add(normalizeExecutionFilterValue(value)));
      return option;
    });

  const unmatched = [...availableValues]
    .filter((value) => !matchedValues.has(value))
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
    .map((value) => ({
      key: value.toLowerCase().replace(/\s+/g, "-"),
      label: value,
      values: [value],
    }));

  const options = [...preferred, ...unmatched];
  return options.length
    ? [{ key: "all", label: "All", title: allTitle }, ...options]
    : [];
};

const renderCapabilityResults = (capabilityResults) => {
  if (!Array.isArray(capabilityResults) || capabilityResults.length === 0) {
    return null;
  }

  return (
    <div className="mini-list convoke-record-list batch-management-capability-results">
      {capabilityResults.map((entry, index) => (
        <div
          key={`${entry?.Name ?? "capability"}:${entry?.CheckedDateTime ?? index}`}
          className="mini-list-row convoke-record-row"
        >
          <div>
            <div className="mini-list-title convoke-record-row__title">{entry?.Name ?? "Capability"}</div>
            <div className="mini-list-detail convoke-record-row__detail">
              {entry?.Message ?? "-"}
              {entry?.DurationMs ? ` | ${entry.DurationMs} ms` : ""}
            </div>
            {entry?.Details ? (
              <div className="mini-list-detail convoke-record-row__detail">{entry.Details}</div>
            ) : null}
          </div>
          <div className={`mini-list-status convoke-record-row__status ${getStatusToneClass(entry?.Status)}`}>
            {entry?.Status ?? "-"}
          </div>
        </div>
      ))}
    </div>
  );
};

function renderManualTestHistoryDetails(entry) {
  if (!entry || typeof entry !== "object") {
    return <div className="table-empty">No manual test details are available.</div>;
  }

  const payload = entry.payload ?? getActionResultPayload(entry.result);
  const detailLines = getActionResultDetailLines(entry.result);

  return (
    <div className="batch-management-test-result">
      <div
        className={`batch-management-message ${getManualTestMessageToneClass(entry.status)}`.trim()}
      >
        <div className="message-title">
          {entry.displayMessage ?? entry.result?.DisplayMessage ?? "Manual test result"}
        </div>
        {payload ? (
          <div className="message-meta">
            {payload?.TestName ? <span>{payload.TestName}</span> : null}
            {getPayloadStatusLabel(payload) ? (
              <span>Status: {getPayloadStatusLabel(payload)}</span>
            ) : null}
            {payload?.CheckedDateTime ? (
              <span>Checked: {formatDateTime(payload.CheckedDateTime)}</span>
            ) : null}
          </div>
        ) : null}
        {detailLines.map((detailLine) => (
          <div key={detailLine} className="message-detail">{detailLine}</div>
        ))}
        {renderCapabilityResults(payload?.CapabilityResults)}
      </div>
    </div>
  );
}

const RowActionButton = ({ children, className = "", ...props }) => (
  <ButtonControl
    {...props}
    type="standard"
    className={`row-action-button ${className}`.trim()}
  >
    {children}
  </ButtonControl>
);

const OverviewEventSnapshotRow = ({ children, eventId, onOpen, title }) => {
  const normalizedEventId = String(eventId ?? "").trim();
  if (!normalizedEventId) {
    return <div className="mini-list-row convoke-record-row">{children}</div>;
  }

  return (
    <ButtonControl
      type="standard"
      className="mini-list-row convoke-record-row batch-management-overview-event-row"
      title={title ?? "Open Event"}
      onClick={() => onOpen?.(normalizedEventId)}
    >
      {children}
    </ButtonControl>
  );
};

const BatchManagement = ({
  experience = "batch-management",
  hideTabs = false,
  initialActiveTab = "overview",
  routeScreenKey = "",
}) => {
  const { canView } = useCorePermissions();
  const coreContext = useCoreContext();
  const activeClientId = String(coreContext?.Profile?.ClientId ?? "").trim();
  const withActiveClientQuery = useCallback((queryParams = {}) => ({
    ...queryParams,
    clientId: activeClientId,
  }), [activeClientId]);
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const genericMgr = useGenericManager("Generic");
  const remoteBatchHostMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.RemoteBatchHost);
  const batchManagementPreferenceMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.BatchManagementPreference);
  const poolProfileMgr = useGenericManager("PoolProfile");
  const eventMgr = useGenericManager("Event");
  const genericMgrRef = useRef(genericMgr);
  const remoteBatchHostMgrRef = useRef(remoteBatchHostMgr);
  const batchManagementPreferenceMgrRef = useRef(batchManagementPreferenceMgr);
  const poolProfileMgrRef = useRef(poolProfileMgr);
  const eventMgrRef = useRef(eventMgr);
  const refreshInFlightRef = useRef(null);
  const activeClientRequestScopeRef = useRef(activeClientId);
  const remoteHostStreamControllersRef = useRef(new Map());

  const [activeTab, setActiveTab] = useState(initialActiveTab || "overview");
  const [summary, setSummary] = useState(null);
  const [activeTasks, setActiveTasks] = useState([]);
  const [remoteHosts, setRemoteHosts] = useState([]);
  const [selectedRemoteHostId, setSelectedRemoteHostId] = useState("");
  const [remoteHostStartupFeeds, setRemoteHostStartupFeeds] = useState({});
  const [remoteHostStartupEventStates, setRemoteHostStartupEventStates] = useState({});
  const [backgroundProcesses, setBackgroundProcesses] = useState([]);
  const [scheduledWork, setScheduledWork] = useState([]);
  const [poolProfiles, setPoolProfiles] = useState([]);
  const [selectedActiveTaskKey, setSelectedActiveTaskKey] = useState("");
  const [selectedBackgroundKey, setSelectedBackgroundKey] = useState("");
  const [selectedScheduledKey, setSelectedScheduledKey] = useState("");
  const [selectedPoolProfileKey, setSelectedPoolProfileKey] = useState("");
  const [rowDetailsDialog, setRowDetailsDialog] = useState(null);
  const [loadingState, setLoadingState] = useState({
    summary: false,
    activeTasks: false,
    remoteHosts: false,
    background: false,
    scheduled: false,
    pools: false,
  });
  const [sectionErrors, setSectionErrors] = useState({
    summary: "",
    activeTasks: "",
    remoteHosts: "",
    background: "",
    scheduled: "",
    pools: "",
  });
  const [headerActionKey, setHeaderActionKey] = useState("");
  const [hostActionKey, setHostActionKey] = useState("");
  const [lastActionResult, setLastActionResult] = useState(null);
  const [isLastActionDialogOpen, setIsLastActionDialogOpen] = useState(false);
  const [poolActionKey, setPoolActionKey] = useState("");
  const [activeTaskActionKey, setActiveTaskActionKey] = useState("");
  const [activeTaskCancelConfirmation, setActiveTaskCancelConfirmation] = useState(null);
  const [activeTaskJobTerminationConfirmation, setActiveTaskJobTerminationConfirmation] = useState(null);
  const [executionActionConfirmation, setExecutionActionConfirmation] = useState(null);
  const [backgroundActionKey, setBackgroundActionKey] = useState("");
  const [scheduledActionKey, setScheduledActionKey] = useState("");
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [rescheduleEventDraft, setRescheduleEventDraft] = useState(null);
  const [rescheduleEventError, setRescheduleEventError] = useState("");
  const [isManualTestDialogOpen, setIsManualTestDialogOpen] = useState(false);
  const [selectedManualTestKey, setSelectedManualTestKey] = useState("azure-batch");
  const [manualTestLauncherKey, setManualTestLauncherKey] = useState("");
  const [activeManualTestActionKey, setActiveManualTestActionKey] = useState("");
  const [batchManagementPreference, setBatchManagementPreference] = useState(null);
  const [hasLoadedBatchManagementPreference, setHasLoadedBatchManagementPreference] = useState(false);
  const [loadedBatchManagementPreferenceScopeKey, setLoadedBatchManagementPreferenceScopeKey] = useState("");
  const [manualTestHistory, setManualTestHistory] = useState([]);
  const [selectedManualTestHistoryId, setSelectedManualTestHistoryId] = useState("");
  const [isRunningManualTest, setIsRunningManualTest] = useState(false);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const [lastRefreshDateTime, setLastRefreshDateTime] = useState("");
  const [copiedDebugLocalCommandHostId, setCopiedDebugLocalCommandHostId] = useState("");
  const [activeDiagnosticSnapshotTab, setActiveDiagnosticSnapshotTab] = useState("summary");
  activeClientRequestScopeRef.current = activeClientId;

  const isExecutionControlCenter = experience === "execution-control";
  const visibleTabs = useMemo(
    () => (isExecutionControlCenter ? getExecutionControlTabs() : getBatchManagementTabs()),
    [isExecutionControlCenter]
  );
  const activeExecutionControlScreen = useMemo(
    () => (isExecutionControlCenter ? getExecutionControlScreen(routeScreenKey) : null),
    [isExecutionControlCenter, routeScreenKey]
  );
  const isCommandCenterScreen = !isExecutionControlCenter || activeExecutionControlScreen?.groupLabel === "Command Center";
  const showCommandCenterBatchControls = isCommandCenterScreen && activeTab !== "connection";
  const isPoolProfilesScreen = isExecutionControlCenter && activeTab === "pools";
  const activeStubTab = useMemo(
    () => (isExecutionControlCenter ? getExecutionControlStubTab(activeTab) : null),
    [activeTab, isExecutionControlCenter]
  );
  const isRouteStubScreen = isExecutionControlCenter && hideTabs && !!activeStubTab;
  const screenCopy = useMemo(
    () => (
      isExecutionControlCenter
        ? {
          eyebrow: activeExecutionControlScreen?.groupLabel
            ? `Execution Control / ${activeExecutionControlScreen.groupLabel}`
            : "Execution Control",
          title: activeExecutionControlScreen?.label ?? "Execution Control Center",
          description: activeExecutionControlScreen?.description
            ?? "Manage background events, Azure Batch work, pool profiles, scheduled events, tests, and diagnostics.",
          deniedTitle: "Execution Control Center is unavailable.",
          deniedDescription: "You do not have permission to view this execution control screen.",
        }
        : {
          eyebrow: "System Management",
          title: "Batch Management",
          description: "Monitor Azure Batch health, remote execution hosts, and background processing from one screen.",
          deniedTitle: "Batch management is unavailable.",
          deniedDescription: "You do not have permission to view this operational screen.",
        }
    ),
    [activeExecutionControlScreen, isExecutionControlCenter]
  );
  const surfacePermission = isExecutionControlCenter
    ? "ExecutionControl"
    : "BatchManagement";
  const canViewBatchManagement = canView(surfacePermission);
  const lastActionPayload = useMemo(
    () => getActionResultPayload(lastActionResult),
    [lastActionResult]
  );
  const lastActionDisplayMessage = useMemo(
    () => getActionResultDisplayMessage(lastActionResult),
    [lastActionResult]
  );
  const lastActionDetailLines = useMemo(
    () => getActionResultDetailLines(lastActionResult),
    [lastActionResult]
  );
  const manualTestOptions = useMemo(
    () => [
      ...getManualTestOptions(isExecutionControlCenter ? [] : remoteHosts),
      ...getBackgroundProcessingManualTestOptions(poolProfiles),
      ...getPoolManualTestOptions(poolProfiles),
    ],
    [isExecutionControlCenter, poolProfiles, remoteHosts]
  );
  const manualTestOptionCardRows = useMemo(
    () => manualTestOptions.map((option) => ({
      ...option,
      CardKey: option.key,
      DisplayName: option.label,
      CardType: getManualTestOptionTypeLabel(option),
      CardScope: option.scopeLabel || "System",
      CardTarget: getManualTestTargetLabel(option),
    })),
    [manualTestOptions]
  );
  const defaultPoolProfile = useMemo(
    () => poolProfiles.find((poolProfile) => !!poolProfile?.IsDefault) ?? null,
    [poolProfiles]
  );
  const activeTaskCardRows = useMemo(
    () => activeTasks.map((entry, index) => {
      const failedTaskCount = Number(entry?.FailedTaskCount ?? 0);
      const isCurrent = isAzureBatchWorkCurrent(entry);
      const isFailedHistory = !isCurrent && failedTaskCount > 0;
      return {
        ...entry,
        IsActive: isCurrent,
        CardKey: getActiveTaskRowKey(entry, index),
        CardStatus: entry?.RuntimeStatus ?? entry?.SourceStatus ?? "-",
        CardHeaderStatus: failedTaskCount > 0 ? "Failed" : entry?.RuntimeStatus ?? entry?.SourceStatus ?? "-",
        CardActivity: isFailedHistory ? "Failed" : isCurrent ? "Current" : "History",
        CardTasks: failedTaskCount > 0
          ? `${Number(entry?.TaskCount ?? 0)} (${failedTaskCount} failed)`
          : Number(entry?.TaskCount ?? 0),
        CardStarted: formatDateTime(entry?.StartedDateTime ?? entry?.CreatedDateTime),
      };
    }),
    [activeTasks]
  );
  const backgroundCardRows = useMemo(
    () => backgroundProcesses.map((entry, index) => ({
      ...entry,
      CardKey: getBackgroundRowKey(entry, index),
      DisplayName: String(entry?.EventDisplayName ?? "").trim() || entry?.DisplayName,
      CardStatus: getEventStatusLabel(entry?.EventStatus) ?? entry?.RuntimeStatus ?? "Unknown",
      CardProcessor: PROCESSOR_TYPE_LABELS[Number(entry?.ProcessorType ?? 0)] ?? "Unknown",
      CardRunDate: formatDateTime(entry?.RunDate ?? entry?.CreatedDate),
      CardExitCode: entry?.AzureBatchTaskExitCode ?? "-",
    })),
    [backgroundProcesses]
  );
  const scheduledCardRows = useMemo(
    () => scheduledWork.map((entry, index) => ({
      ...entry,
      CardKey: getScheduledRowKey(entry, index),
      CardNextRun: formatDateTime(entry?.NextRunDateTime),
    })),
    [scheduledWork]
  );
  const azurePoolRuntimeById = useMemo(
    () => new Map(
      (Array.isArray(summary?.AzurePools) ? summary.AzurePools : [])
        .filter((azurePool) => String(azurePool?.PoolId ?? "").trim())
        .map((azurePool) => [String(azurePool.PoolId).trim().toLowerCase(), azurePool])
    ),
    [summary?.AzurePools]
  );
  const poolProfileCardRows = useMemo(
    () => poolProfiles.map((poolProfile, index) => {
      const runtimePool = azurePoolRuntimeById.get(String(poolProfile?.PoolId ?? "").trim().toLowerCase());
      const azureStatus = loadingState.summary
        ? "Not Checked"
        : sectionErrors.summary
          ? "Error"
          : !summary
            ? "Not Checked"
            : runtimePool
              ? normalizeAzurePoolRuntimeStatus(runtimePool.Status)
              : "Not Created";

      return {
        ...poolProfile,
        CardKey: getPoolProfileRowKey(poolProfile, index),
        CardStatus: poolProfile?.IsActive === false ? "Disabled" : "Configured",
        AzureStatus: azureStatus,
        AzureAllocationState: runtimePool?.AllocationState ?? "",
        AzureCurrentDedicatedNodes: runtimePool?.CurrentDedicatedNodes ?? null,
        AzureCurrentLowPriorityNodes: runtimePool?.CurrentLowPriorityNodes ?? null,
        AzureTargetDedicatedNodes: runtimePool?.TargetDedicatedNodes ?? null,
        AzureTargetLowPriorityNodes: runtimePool?.TargetLowPriorityNodes ?? null,
      };
    }),
    [azurePoolRuntimeById, loadingState.summary, poolProfiles, sectionErrors.summary, summary]
  );
  const backgroundStatusFilterOptions = useMemo(
    () => buildExecutionCardGridFilterOptions(backgroundCardRows, "CardStatus", EXECUTION_STATUS_FILTER_OPTIONS, "All background event statuses"),
    [backgroundCardRows]
  );
  const scheduledStatusFilterOptions = useMemo(
    () => buildExecutionCardGridFilterOptions(scheduledCardRows, "EventStatus", EXECUTION_STATUS_FILTER_OPTIONS, "All scheduled event statuses"),
    [scheduledCardRows]
  );
  const poolProfileDefaultFilterOptions = useMemo(
    () => buildExecutionCardGridFilterOptions(poolProfileCardRows, "IsDefault", EXECUTION_POOL_PROFILE_DEFAULT_FILTER_OPTIONS, "All pool profiles"),
    [poolProfileCardRows]
  );
  const selectedActiveTask = useMemo(
    () => activeTaskCardRows.find((entry) => entry.CardKey === selectedActiveTaskKey) ?? null,
    [activeTaskCardRows, selectedActiveTaskKey]
  );
  const selectedBackgroundWork = useMemo(
    () => backgroundCardRows.find((entry) => entry.CardKey === selectedBackgroundKey) ?? null,
    [backgroundCardRows, selectedBackgroundKey]
  );
  const selectedScheduledWork = useMemo(
    () => scheduledCardRows.find((entry) => entry.CardKey === selectedScheduledKey) ?? null,
    [scheduledCardRows, selectedScheduledKey]
  );
  const selectedPoolProfile = useMemo(
    () => poolProfileCardRows.find((entry) => entry.CardKey === selectedPoolProfileKey) ?? null,
    [poolProfileCardRows, selectedPoolProfileKey]
  );
  const backgroundSnapshotEntries = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);

    return [...backgroundProcesses]
      .filter((entry) => {
        const entryDate = getEntryDateValue(entry?.RunDate ?? entry?.CreatedDate);
        return !!entryDate && entryDate >= cutoff;
      })
      .sort((left, right) => {
        const leftDate = getEntryDateValue(left?.RunDate ?? left?.CreatedDate)?.getTime() ?? 0;
        const rightDate = getEntryDateValue(right?.RunDate ?? right?.CreatedDate)?.getTime() ?? 0;
        return rightDate - leftDate;
      })
      .slice(0, 3);
  }, [backgroundProcesses]);
  const selectedManualTest = useMemo(
    () =>
      manualTestOptions.find((option) => option.key === selectedManualTestKey)
      ?? manualTestOptions[0]
      ?? null,
    [manualTestOptions, selectedManualTestKey]
  );
  const selectedManualTestHistoryEntry = useMemo(
    () =>
      manualTestHistory.find((entry) => entry.id === selectedManualTestHistoryId)
      ?? manualTestHistory[0]
      ?? null,
    [manualTestHistory, selectedManualTestHistoryId]
  );
  const manualTestHistoryCardRows = useMemo(
    () => manualTestHistory.map((entry) => ({
      ...entry,
      CardKey: entry.id,
      DisplayName: entry.label,
      CardStatus: entry.status ?? entry.payload?.Status ?? (entry.result?.Success ? "Passed" : "Failed"),
      CardScope: entry.scopeLabel || "System",
      CardChecked: formatDateTime(entry.payload?.CheckedDateTime ?? entry.checkedDateTime),
    })),
    [manualTestHistory]
  );
  const selectedRemoteHost = useMemo(
    () =>
      remoteHosts.find((host) => host?._id === selectedRemoteHostId)
      ?? remoteHosts[0]
      ?? null,
    [remoteHosts, selectedRemoteHostId]
  );
  const selectedRemoteHostStartupFeed = useMemo(
    () => (selectedRemoteHost?._id ? remoteHostStartupFeeds[selectedRemoteHost._id] ?? null : null),
    [remoteHostStartupFeeds, selectedRemoteHost]
  );
  const filteredRemoteHosts = useMemo(
    () => (selectedRemoteHost ? [selectedRemoteHost] : []),
    [selectedRemoteHost]
  );
  const selectedRemoteHostTasks = useMemo(() => {
    if (!selectedRemoteHost) {
      return [];
    }

    const seen = new Set();

    return activeTasks.filter((entry, index) => {
      const matchesHost =
        entry?.HostId === selectedRemoteHost?._id
        || (!!selectedRemoteHost?.CurrentJobId && entry?.JobId === selectedRemoteHost.CurrentJobId)
        || (!!selectedRemoteHost?.CurrentTaskId && entry?.TaskId === selectedRemoteHost.CurrentTaskId);

      if (!matchesHost) {
        return false;
      }

      const key = `${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? index}`;
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }, [activeTasks, selectedRemoteHost]);
  const selectedRemoteHostDebugLocalCommand = useMemo(
    () => (selectedRemoteHost?.IsDebugLocal ? getDebugLocalHostCommand(selectedRemoteHost) : ""),
    [selectedRemoteHost]
  );
  const getEffectiveHostActionKey = useCallback(
    (host, operationName) => {
      if (!host?._id || !operationName) {
        return "";
      }

      if (hostActionKey) {
        return hostActionKey;
      }

      if (operationName === "StartRemoteBatchHost" && isRemoteHostStartupPending(host)) {
        const startupEventId = String(host?.StartupEventId ?? "").trim();
        const startupEventState = remoteHostStartupEventStates[host._id];

        if (!startupEventId || !startupEventState || startupEventState?.eventId !== startupEventId) {
          return `StartRemoteBatchHost:${host._id}`;
        }

        return startupEventState?.isActive === true
          ? `StartRemoteBatchHost:${host._id}`
          : `StartRemoteBatchHost:${host._id}`;
      }

      return "";
    },
    [hostActionKey, remoteHostStartupEventStates]
  );
  const isAnyActionRunning = !!(
    headerActionKey ||
    hostActionKey ||
    poolActionKey ||
    activeTaskActionKey ||
    scheduledActionKey ||
    activeManualTestActionKey
  );
  const isAutoRefreshBlocked = isManualTestDialogOpen || isRunningManualTest || isAnyActionRunning;
  const staleRemoteHosts = useMemo(
    () => remoteHosts.filter((host) => isRemoteHostStale(host)),
    [remoteHosts]
  );
  const startupDiagnosticHosts = useMemo(
    () => remoteHosts.filter((host) => {
      const state = getRemoteHostStateLabel(host);
      return (
        host?.StartupFailureCount > 0
        || state === "Starting"
        || state === "Error"
        || state === "Draining"
        || !!host?.LastStatusDetail
      );
    }),
    [remoteHosts]
  );
  const diagnosticsOverview = useMemo(
    () => ({
      autoRefreshEnabled: isAutoRefreshEnabled,
      autoRefreshIntervalSeconds: AUTO_REFRESH_INTERVAL_SECONDS,
      autoRefreshBlocked: isAutoRefreshBlocked,
      lastRefreshDateTime,
      staleHostCount: staleRemoteHosts.length,
      staleRemoteHostCount: staleRemoteHosts.length,
      drainingHostCount: remoteHosts.filter((host) => getRemoteHostStateLabel(host) === "Draining").length,
      startupFailureCount: remoteHosts.reduce((total, host) => total + Number(host?.StartupFailureCount ?? 0), 0),
      actionInFlight: isAnyActionRunning,
    }),
    [
      isAnyActionRunning,
      isAutoRefreshBlocked,
      isAutoRefreshEnabled,
      lastRefreshDateTime,
      remoteHosts,
      staleRemoteHosts.length,
    ]
  );
  const diagnosticSnapshotTabs = useMemo(() => {
    const tabs = [
      {
        key: "summary",
        label: "Summary",
        title: "Summary Snapshot",
        meta: "Current aggregate payload",
        payload: summary,
      },
      ...(isExecutionControlCenter
        ? [
          {
            key: "batch-resources",
            label: EXECUTION_CONTROL_AZURE_BATCH_WORK_LABEL,
            title: "Azure Batch Runtime Snapshot",
            meta: `${activeTasks.length} row(s)`,
            payload: activeTasks,
          },
          {
            key: "pool-profiles",
            label: "Pool Profiles",
            title: "Pool Profile Snapshot",
            meta: `${poolProfiles.length} profile(s)`,
            payload: poolProfiles,
          },
        ]
        : [
          {
            key: "remote-host",
            label: "Remote Host",
            title: "Selected Remote Host Snapshot",
            meta: selectedRemoteHost ? selectedRemoteHost.Name ?? "Host" : "No host selected",
            payload: selectedRemoteHost,
          },
        ]),
      {
        key: "last-action",
        label: "Last Action",
        title: "Last Action Result",
        meta: lastActionResult ? "Latest operator action" : "No action yet",
        payload: lastActionResult,
      },
      {
        key: "ui-runtime",
        label: "UI Runtime",
        title: "Screen Diagnostics",
        meta: "UI runtime snapshot",
        payload: diagnosticsOverview,
      },
    ];

    return tabs;
  }, [
    activeTasks,
    diagnosticsOverview,
    isExecutionControlCenter,
    lastActionResult,
    poolProfiles,
    selectedRemoteHost,
    summary,
  ]);
  const activeDiagnosticSnapshot = useMemo(
    () => diagnosticSnapshotTabs.find((tab) => tab.key === activeDiagnosticSnapshotTab) ?? diagnosticSnapshotTabs[0],
    [activeDiagnosticSnapshotTab, diagnosticSnapshotTabs]
  );
  const currentBatchManagementPreferenceScopeKey = useMemo(
    () => `${coreContext?.Profile?.AppId ?? ""}:${coreContext?.Profile?.ClientId ?? "GLOBAL"}`,
    [coreContext?.Profile?.AppId, coreContext?.Profile?.ClientId]
  );

  useEffect(() => {
    genericMgrRef.current = genericMgr;
  }, [genericMgr]);

  useEffect(() => {
    remoteBatchHostMgrRef.current = remoteBatchHostMgr;
  }, [remoteBatchHostMgr]);

  useEffect(() => {
    if (!diagnosticSnapshotTabs.some((tab) => tab.key === activeDiagnosticSnapshotTab)) {
      setActiveDiagnosticSnapshotTab(diagnosticSnapshotTabs[0]?.key ?? "summary");
    }
  }, [activeDiagnosticSnapshotTab, diagnosticSnapshotTabs]);

  useEffect(() => {
    batchManagementPreferenceMgrRef.current = batchManagementPreferenceMgr;
  }, [batchManagementPreferenceMgr]);

  useEffect(() => {
    poolProfileMgrRef.current = poolProfileMgr;
  }, [poolProfileMgr]);

  useEffect(() => {
    eventMgrRef.current = eventMgr;
  }, [eventMgr]);

  useEffect(() => {
    if (!initialActiveTab || !visibleTabs.some((tab) => tab.key === initialActiveTab)) {
      return;
    }

    setActiveTab((current) => (current === initialActiveTab ? current : initialActiveTab));
  }, [initialActiveTab, visibleTabs]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab("overview");
    }
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    if (remoteHosts.length === 0) {
      setSelectedRemoteHostId("");
      return;
    }

    if (!selectedRemoteHostId || !remoteHosts.some((host) => host?._id === selectedRemoteHostId)) {
      setSelectedRemoteHostId(remoteHosts[0]?._id ?? "");
    }
  }, [remoteHosts, selectedRemoteHostId]);

  useEffect(() => {
    if (selectedActiveTaskKey && !activeTaskCardRows.some((entry) => entry.CardKey === selectedActiveTaskKey)) {
      setSelectedActiveTaskKey("");
    }
  }, [activeTaskCardRows, selectedActiveTaskKey]);

  useEffect(() => {
    if (selectedBackgroundKey && !backgroundCardRows.some((entry) => entry.CardKey === selectedBackgroundKey)) {
      setSelectedBackgroundKey("");
    }
  }, [backgroundCardRows, selectedBackgroundKey]);

  useEffect(() => {
    if (selectedScheduledKey && !scheduledCardRows.some((entry) => entry.CardKey === selectedScheduledKey)) {
      setSelectedScheduledKey("");
    }
  }, [scheduledCardRows, selectedScheduledKey]);

  useEffect(() => {
    if (selectedPoolProfileKey && !poolProfileCardRows.some((entry) => entry.CardKey === selectedPoolProfileKey)) {
      setSelectedPoolProfileKey("");
    }
  }, [poolProfileCardRows, selectedPoolProfileKey]);

  useEffect(() => () => {
    remoteHostStreamControllersRef.current.forEach((controller) => {
      try {
        controller?.close?.();
      } catch (error) {
        console.error("Failed to close remote host stream controller:", error);
      }
    });
    remoteHostStreamControllersRef.current.clear();
  }, []);

  useEffect(() => {
    remoteHostStreamControllersRef.current.forEach((controller) => {
      try {
        controller?.close?.();
      } catch (error) {
        console.error("Failed to close remote host stream after an active-client change:", error);
      }
    });
    remoteHostStreamControllersRef.current.clear();
    refreshInFlightRef.current = null;
    setSummary(null);
    setActiveTasks([]);
    setRemoteHosts([]);
    setBackgroundProcesses([]);
    setScheduledWork([]);
    setPoolProfiles([]);
    setSelectedRemoteHostId("");
    setSelectedActiveTaskKey("");
    setSelectedBackgroundKey("");
    setSelectedScheduledKey("");
    setSelectedPoolProfileKey("");
    setHeaderActionKey("");
    setHostActionKey("");
    setPoolActionKey("");
    setActiveTaskActionKey("");
    setActiveTaskCancelConfirmation(null);
    setBackgroundActionKey("");
    setScheduledActionKey("");
    setIsRescheduleDialogOpen(false);
    setRescheduleEventDraft(null);
    setRescheduleEventError("");
    setRemoteHostStartupFeeds({});
    setRemoteHostStartupEventStates({});
    setRowDetailsDialog(null);
    setLastActionResult(null);
    setIsLastActionDialogOpen(false);
    setIsManualTestDialogOpen(false);
    setSelectedManualTestKey("azure-batch");
    setManualTestLauncherKey("");
    setActiveManualTestActionKey("");
    setIsRunningManualTest(false);
    setBatchManagementPreference(null);
    setHasLoadedBatchManagementPreference(false);
    setLoadedBatchManagementPreferenceScopeKey("");
    setManualTestHistory([]);
    setSelectedManualTestHistoryId("");
    setIsAutoRefreshEnabled(true);
    setLastRefreshDateTime("");
    setCopiedDebugLocalCommandHostId("");
    setLoadingState({
      summary: false,
      activeTasks: false,
      remoteHosts: false,
      background: false,
      scheduled: false,
      pools: false,
    });
    setSectionErrors({
      summary: "",
      activeTasks: "",
      remoteHosts: "",
      background: "",
      scheduled: "",
      pools: "",
    });
  }, [activeClientId]);

  const closeRemoteHostStream = useCallback((hostId) => {
    if (!hostId) {
      return;
    }

    const existingController = remoteHostStreamControllersRef.current.get(hostId);
    if (existingController) {
      try {
        existingController.close?.();
      } catch (error) {
        console.error("Failed to close remote host stream:", error);
      }
      remoteHostStreamControllersRef.current.delete(hostId);
    }
  }, []);

  const mergeRemoteHostFromStream = useCallback((hostPayload) => {
    if (!hostPayload?._id) {
      return;
    }

    setRemoteHosts((current) => current.map((host) => (
      host?._id === hostPayload._id
        ? {
          ...host,
          ...hostPayload,
        }
        : host
    )));
  }, []);

  const appendRemoteHostStartupFeedEntry = useCallback((hostId, nextEntry, options = {}) => {
    if (!hostId || !nextEntry) {
      return;
    }

    setRemoteHostStartupFeeds((current) => {
      const existingFeed = current[hostId] ?? {
        eventId: "",
        isStreaming: false,
        isComplete: false,
        success: null,
        messages: [],
        lastUpdated: "",
      };

      const nextMessages = options?.replaceMessages
        ? [nextEntry]
        : [...existingFeed.messages, nextEntry].slice(-MAX_REMOTE_HOST_STARTUP_MESSAGES);

      return {
        ...current,
        [hostId]: {
          ...existingFeed,
          ...options,
          messages: nextMessages,
          lastUpdated: nextEntry.timestamp ?? new Date().toISOString(),
        },
      };
    });
  }, []);

  const setRemoteHostStartupEventState = useCallback((hostId, eventId, isActive, eventStatus = null) => {
    if (!hostId) {
      return;
    }

    setRemoteHostStartupEventStates((current) => ({
      ...current,
      [hostId]: {
        eventId: String(eventId ?? ""),
        isActive: !!isActive,
        eventStatus: eventStatus === null || eventStatus === undefined ? null : Number(eventStatus),
        checkedDateTime: new Date().toISOString(),
      },
    }));
  }, []);

  const getRemoteHostStartupEventStatus = useCallback(async (host) => {
    const hostId = String(host?._id ?? "").trim();
    const startupEventId = String(host?.StartupEventId ?? "").trim();

    if (!hostId || !startupEventId || !eventMgrRef.current?.single) {
      return {
        isActive: false,
        eventId: startupEventId,
        eventStatus: null,
      };
    }

    try {
      const response = await eventMgrRef.current.single(startupEventId);
      const normalizedResponse = getNormalizedSingleResult(response);
      const eventItem = normalizedResponse?.Items?.[0] ?? normalizedResponse?.Item ?? null;
      const eventStatus = eventItem?.EventStatus ?? eventItem?.Status ?? null;
      const isActive = !!eventItem && isStartupEventStatusActive(eventItem);

      setRemoteHostStartupEventState(hostId, startupEventId, isActive, eventStatus);

      return {
        isActive,
        eventId: startupEventId,
        eventStatus,
      };
    } catch (error) {
      console.warn("Failed to load remote host startup event state:", error);
      setRemoteHostStartupEventState(hostId, startupEventId, false, null);
      return {
        isActive: false,
        eventId: startupEventId,
        eventStatus: null,
      };
    }
  }, [setRemoteHostStartupEventState]);

  const handleRemoteHostStreamMessage = useCallback((hostId, message) => {
    if (!hostId || !message) {
      return;
    }

    const timestamp = new Date().toISOString();
    const payload = message?.OutputType === "object" ? message?.Message : null;
    const hostPayload = payload?.Host ?? null;
    const entryMessage = String(
      payload?.Message
      ?? message?.Message
      ?? ""
    ).trim();

    if (hostPayload?._id) {
      mergeRemoteHostFromStream(hostPayload);
    }

    const resolvedEventId = String(payload?.EventId ?? message?.EventId ?? "").trim();
    if (resolvedEventId) {
      const isCompletedMessage = message?.IsComplete === true;
      const resolvedEventStatus = isCompletedMessage
        ? (payload?.Success === false ? 0 : 3)
        : null;
      setRemoteHostStartupEventState(
        hostId,
        resolvedEventId,
        !isCompletedMessage,
        resolvedEventStatus
      );
    }

    if (!entryMessage) {
      return;
    }

    if (
      payload?.Success === false
      || String(message?.UIMessageType ?? "").trim().toLowerCase() === "error"
    ) {
      console.error("Remote host startup stream error.", {
        hostId,
        message,
        payload,
        host: hostPayload,
      });
    }

    appendRemoteHostStartupFeedEntry(hostId, {
      id: `${hostId}:${message?.EventId ?? "event"}:${timestamp}:${Math.random().toString(36).slice(2)}`,
      eventId: payload?.EventId ?? message?.EventId ?? "",
      message: entryMessage,
      stage: payload?.Stage ?? "",
      uiMessageType: message?.UIMessageType ?? "status",
      timestamp,
      outputType: message?.OutputType ?? "text",
    }, {
      eventId: payload?.EventId ?? message?.EventId ?? "",
      isStreaming: message?.IsComplete !== true,
      isComplete: message?.IsComplete === true,
      success: typeof payload?.Success === "boolean" ? payload.Success : undefined,
    });
  }, [appendRemoteHostStartupFeedEntry, mergeRemoteHostFromStream, setRemoteHostStartupEventState]);

  const setSectionLoading = useCallback((key, value) => {
    setLoadingState((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const setSectionError = useCallback((key, value) => {
    setSectionErrors((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const loadSummary = useCallback(async (options = {}) => {
    const requestClientId = activeClientId;
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("summary", true);
      setSectionError("summary", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchManagementSummary",
        queryParams: withActiveClientQuery(),
        isWarning: true,
      });
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      const normalizedResponse = unwrapResult(response);

      if (!normalizedResponse?.Success) {
        setSectionError(
          "summary",
          getDisplayMessage(
            normalizedResponse,
            "Unable to load batch management summary."
          )
        );
      } else {
        setSectionError("summary", "");
      }

      setSummary(getItems(normalizedResponse)[0] ?? null);
    } catch (error) {
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      console.error("Batch summary load failed:", error);
      setSectionError("summary", "Unable to load batch management summary.");
    } finally {
      if (showLoading && activeClientRequestScopeRef.current === requestClientId) {
        setSectionLoading("summary", false);
      }
    }
  }, [activeClientId, setSectionError, setSectionLoading, withActiveClientQuery]);

  const loadActiveTasks = useCallback(async (options = {}) => {
    const requestClientId = activeClientId;
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("activeTasks", true);
      setSectionError("activeTasks", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchActiveTasks",
        queryParams: withActiveClientQuery(),
        isWarning: true,
      });
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      const normalizedResponse = unwrapResult(response);

      if (!normalizedResponse?.Success) {
        setSectionError(
          "activeTasks",
          getDisplayMessage(normalizedResponse, "Unable to load active tasks.")
        );
      } else {
        setSectionError("activeTasks", "");
      }

      setActiveTasks(getItems(normalizedResponse));
    } catch (error) {
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      console.error("Batch active tasks load failed:", error);
      setSectionError("activeTasks", "Unable to load active tasks.");
    } finally {
      if (showLoading && activeClientRequestScopeRef.current === requestClientId) {
        setSectionLoading("activeTasks", false);
      }
    }
  }, [activeClientId, setSectionError, setSectionLoading, withActiveClientQuery]);

  const loadRemoteHosts = useCallback(async (options = {}) => {
    const requestClientId = activeClientId;
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("remoteHosts", true);
      setSectionError("remoteHosts", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchRemoteHosts",
        queryParams: withActiveClientQuery(),
        isWarning: true,
      });
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      const normalizedResponse = unwrapResult(response);
      const operationItems = getItems(normalizedResponse);

      if (normalizedResponse?.Success) {
        setSectionError("remoteHosts", "");
        setRemoteHosts(operationItems);
        return;
      }

      setSectionError(
        "remoteHosts",
        getDisplayMessage(normalizedResponse, "Unable to load remote hosts.")
      );
      setRemoteHosts([]);
    } catch (error) {
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      console.error("Batch remote host load failed:", error);
      setSectionError("remoteHosts", "Unable to load remote hosts.");
    } finally {
      if (showLoading && activeClientRequestScopeRef.current === requestClientId) {
        setSectionLoading("remoteHosts", false);
      }
    }
  }, [activeClientId, setSectionError, setSectionLoading, withActiveClientQuery]);

  const loadPools = useCallback(async (options = {}) => {
    const requestClientId = activeClientId;
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("pools", true);
      setSectionError("pools", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchPools",
        queryParams: withActiveClientQuery(),
        isWarning: true,
      });
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      const normalizedResponse = unwrapResult(response);
      const operationItems = getItems(normalizedResponse);

      if (normalizedResponse?.Success) {
        setSectionError("pools", "");
        setPoolProfiles(sortPoolProfiles(operationItems));
        return;
      }

      setSectionError(
        "pools",
        getDisplayMessage(normalizedResponse, "Unable to load pool profiles.")
      );
      setPoolProfiles([]);
    } catch (error) {
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      console.error("Batch pool load failed:", error);
      setSectionError("pools", "Unable to load pool profiles.");
    } finally {
      if (showLoading && activeClientRequestScopeRef.current === requestClientId) {
        setSectionLoading("pools", false);
      }
    }
  }, [activeClientId, setSectionError, setSectionLoading, withActiveClientQuery]);

  const loadBackgroundProcesses = useCallback(async (options = {}) => {
    const requestClientId = activeClientId;
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("background", true);
      setSectionError("background", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchBackgroundProcesses",
        queryParams: withActiveClientQuery(),
        isWarning: true,
      });
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      const normalizedResponse = unwrapResult(response);

      if (!normalizedResponse?.Success) {
        setSectionError(
          "background",
          getDisplayMessage(normalizedResponse, "Unable to load background events.")
        );
      } else {
        setSectionError("background", "");
      }

      setBackgroundProcesses(getItems(normalizedResponse));
    } catch (error) {
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      console.error("Batch background load failed:", error);
      setSectionError("background", "Unable to load background events.");
    } finally {
      if (showLoading && activeClientRequestScopeRef.current === requestClientId) {
        setSectionLoading("background", false);
      }
    }
  }, [activeClientId, setSectionError, setSectionLoading, withActiveClientQuery]);

  const loadScheduledWork = useCallback(async (options = {}) => {
    const requestClientId = activeClientId;
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("scheduled", true);
      setSectionError("scheduled", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchScheduledWork",
        queryParams: withActiveClientQuery(),
        isWarning: true,
      });
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      const normalizedResponse = unwrapResult(response);

      if (!normalizedResponse?.Success) {
        setSectionError(
          "scheduled",
          getDisplayMessage(normalizedResponse, "Unable to load scheduled events.")
        );
      } else {
        setSectionError("scheduled", "");
      }

      setScheduledWork(getItems(normalizedResponse));
    } catch (error) {
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      console.error("Batch scheduled event load failed:", error);
      setSectionError("scheduled", "Unable to load scheduled events.");
    } finally {
      if (showLoading && activeClientRequestScopeRef.current === requestClientId) {
        setSectionLoading("scheduled", false);
      }
    }
  }, [activeClientId, setSectionError, setSectionLoading, withActiveClientQuery]);

  const loadBatchManagementPreference = useCallback(async () => {
    const currentClientId = coreContext?.Profile?.ClientId;
    const requestClientId = String(currentClientId ?? "").trim();
    const currentAppId = coreContext?.Profile?.AppId;

    if (!batchManagementPreferenceMgrRef.current || !currentAppId) {
      setBatchManagementPreference(null);
      setManualTestHistory([]);
      setIsAutoRefreshEnabled(true);
      setHasLoadedBatchManagementPreference(true);
      setLoadedBatchManagementPreferenceScopeKey(currentBatchManagementPreferenceScopeKey);
      return;
    }

    try {
      const searchModel = currentClientId
        ? "x => x.IsActive == true && x.IsDeleted == false && x.PreferenceKey == preferenceKey && x.ClientId == clientId"
        : "x => x.IsActive == true && x.IsDeleted == false && x.PreferenceKey == preferenceKey && x.ClientId == null";

      const response = await batchManagementPreferenceMgrRef.current.search({
        searchModel,
        params: {
          preferenceKey: BATCH_MANAGEMENT_PREFERENCE_KEY,
          clientId: currentClientId,
        },
      });
      if (activeClientRequestScopeRef.current !== requestClientId) return;

      const preferenceItem = getItems(response)[0] ?? null;
      const manualTestHistoryItems = Array.isArray(preferenceItem?.ManualTestHistory) && preferenceItem.ManualTestHistory.length > 0
        ? normalizeManualTestHistory(preferenceItem.ManualTestHistory)
        : parseManualTestHistoryJson(preferenceItem?.ManualTestHistoryJson);

      setBatchManagementPreference(preferenceItem);
      setManualTestHistory(manualTestHistoryItems);
      setIsAutoRefreshEnabled(preferenceItem?.AutoRefreshEnabled ?? true);
    } catch (error) {
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      console.warn("Batch management preference load failed:", error);
      setBatchManagementPreference(null);
      setManualTestHistory([]);
      setIsAutoRefreshEnabled(true);
    } finally {
      if (activeClientRequestScopeRef.current === requestClientId) {
        setHasLoadedBatchManagementPreference(true);
        setLoadedBatchManagementPreferenceScopeKey(currentBatchManagementPreferenceScopeKey);
      }
    }
  }, [
    coreContext?.Profile?.AppId,
    coreContext?.Profile?.ClientId,
    currentBatchManagementPreferenceScopeKey,
  ]);

  const saveBatchManagementPreference = useCallback(async () => {
    if (
      !hasLoadedBatchManagementPreference
      || loadedBatchManagementPreferenceScopeKey !== currentBatchManagementPreferenceScopeKey
      || !batchManagementPreferenceMgrRef.current
    ) {
      return;
    }

    const currentAppId = coreContext?.Profile?.AppId;
    const requestClientId = String(coreContext?.Profile?.ClientId ?? "").trim();
    if (!currentAppId) {
      return;
    }

    const nextPreferenceItem = {
      ...(batchManagementPreference ?? {}),
      AppId: currentAppId,
      ClientId: coreContext?.Profile?.ClientId ?? null,
      PreferenceKey: BATCH_MANAGEMENT_PREFERENCE_KEY,
      AutoRefreshEnabled: !!isAutoRefreshEnabled,
      ManualTestHistory: toManualTestHistoryPreferenceItems(manualTestHistory),
      ManualTestHistoryJson: null,
    };

    try {
      const response = await batchManagementPreferenceMgrRef.current.saveItem(nextPreferenceItem);
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      const savedPreferenceItem = getItems(response)[0] ?? null;
      if (savedPreferenceItem?._id && savedPreferenceItem._id !== batchManagementPreference?._id) {
        setBatchManagementPreference(savedPreferenceItem);
      }
    } catch (error) {
      if (activeClientRequestScopeRef.current !== requestClientId) return;
      console.warn("Batch management preference save failed:", error);
    }
  }, [
    batchManagementPreference,
    coreContext?.Profile?.AppId,
    coreContext?.Profile?.ClientId,
    hasLoadedBatchManagementPreference,
    isAutoRefreshEnabled,
    loadedBatchManagementPreferenceScopeKey,
    manualTestHistory,
    currentBatchManagementPreferenceScopeKey,
  ]);

  const refreshAllSections = useCallback(async (options = {}) => {
    const requestClientId = activeClientId;
    if (!canViewBatchManagement) {
      return false;
    }

    if (isRouteStubScreen) {
      return true;
    }

    if (refreshInFlightRef.current) {
      if (options?.waitForInFlight !== true) {
        return false;
      }

      await refreshInFlightRef.current;
    }

    let releaseRefresh;
    const refreshCompletion = new Promise((resolve) => {
      releaseRefresh = resolve;
    });
    refreshInFlightRef.current = refreshCompletion;

    try {
      const showLoading = options?.showLoading !== false;
      let refreshOperations = [];

      if (isExecutionControlCenter && hideTabs) {
        if (activeTab === "overview" || activeTab === "diagnostics") {
          refreshOperations = [
            loadSummary({ showLoading }),
            loadActiveTasks({ showLoading }),
            loadPools({ showLoading }),
            loadBackgroundProcesses({ showLoading }),
            loadScheduledWork({ showLoading }),
          ];
        } else if (activeTab === "connection") {
          refreshOperations = [
            loadSummary({ showLoading }),
            loadPools({ showLoading }),
          ];
        } else if (activeTab === "active-tasks") {
          refreshOperations = [loadActiveTasks({ showLoading })];
        } else if (activeTab === "background") {
          refreshOperations = [loadBackgroundProcesses({ showLoading })];
        } else if (activeTab === "scheduled") {
          refreshOperations = [loadScheduledWork({ showLoading })];
        } else if (activeTab === "pools") {
          refreshOperations = [
            loadSummary({ showLoading }),
            loadPools({ showLoading }),
          ];
        } else if (activeTab === "tests") {
          refreshOperations = [
            loadPools({ showLoading }),
            loadBackgroundProcesses({ showLoading }),
            loadScheduledWork({ showLoading }),
          ];
        }
      } else {
        refreshOperations = [
          loadSummary({ showLoading }),
          loadActiveTasks({ showLoading }),
          loadPools({ showLoading }),
          loadBackgroundProcesses({ showLoading }),
          loadScheduledWork({ showLoading }),
        ];

        if (!isExecutionControlCenter) {
          refreshOperations.push(loadRemoteHosts({ showLoading }));
        }
      }

      await Promise.all(refreshOperations);
      if (activeClientRequestScopeRef.current === requestClientId) {
        setLastRefreshDateTime(new Date().toISOString());
      }
      return true;
    } finally {
      releaseRefresh?.();
      if (refreshInFlightRef.current === refreshCompletion) {
        refreshInFlightRef.current = null;
      }
    }
  }, [
    activeClientId,
    canViewBatchManagement,
    activeTab,
    hideTabs,
    isExecutionControlCenter,
    isRouteStubScreen,
    loadActiveTasks,
    loadBackgroundProcesses,
    loadPools,
    loadRemoteHosts,
    loadScheduledWork,
    loadSummary,
  ]);

  useEffect(() => {
    if (!canViewBatchManagement || isRouteStubScreen) {
      return;
    }

    void refreshAllSections();
  }, [canViewBatchManagement, isRouteStubScreen, refreshAllSections]);

  useEffect(() => {
    if (!canViewBatchManagement || isRouteStubScreen) {
      return;
    }

    void loadBatchManagementPreference();
  }, [canViewBatchManagement, isRouteStubScreen, loadBatchManagementPreference]);

  useEffect(() => {
    if (!canViewBatchManagement || isRouteStubScreen) {
      return;
    }

    void saveBatchManagementPreference();
  }, [canViewBatchManagement, isRouteStubScreen, saveBatchManagementPreference]);

  useEffect(() => {
    if (!canViewBatchManagement || isRouteStubScreen || !showCommandCenterBatchControls) {
      return undefined;
    }

    if (!isAutoRefreshEnabled) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (!isAutoRefreshBlocked) {
        void refreshAllSections({ showLoading: false });
      }
    }, AUTO_REFRESH_INTERVAL_SECONDS * 1000);

    return () => window.clearInterval(intervalId);
  }, [canViewBatchManagement, isAutoRefreshBlocked, isAutoRefreshEnabled, isRouteStubScreen, refreshAllSections, showCommandCenterBatchControls]);

  const resumeRemoteHostStartupStream = useCallback(
    async (host) => {
      const requestClientId = activeClientId;
      const hostId = host?._id;
      if (!hostId || remoteHostStreamControllersRef.current.has(hostId)) {
        return;
      }

      try {
        const hubController = await remoteBatchHostMgrRef.current?.GetStream({
          operationName: "StartRemoteBatchHost",
          id: hostId,
          type: "sse",
          queryParams: withActiveClientQuery(),
        });
        if (activeClientRequestScopeRef.current !== requestClientId) {
          hubController?.close?.();
          return;
        }

        if (!hubController?.Success || typeof hubController.onMessage !== "function") {
          return;
        }

        remoteHostStreamControllersRef.current.set(hostId, hubController);
        hubController.onMessage((message) => {
          if (activeClientRequestScopeRef.current === requestClientId) {
            handleRemoteHostStreamMessage(hostId, message);
          }
        });

        void (async () => {
          try {
            const hubCompletion = await hubController.untilDone;
            if (activeClientRequestScopeRef.current !== requestClientId) return;
            if (hubCompletion?.reason !== "done") {
              return;
            }

            const completionMessage = hubCompletion?.data ?? null;
            const completionPayload = completionMessage?.OutputType === "object" ? completionMessage?.Message : null;

            if (completionPayload?.Host?._id) {
              mergeRemoteHostFromStream(completionPayload.Host);
            }

            if (completionPayload?.Success === false) {
              setLastActionResult(buildRemoteHostStartupFailureResult({
                hostId,
                fallbackMessage: "Remote host startup failed.",
                payload: completionPayload,
                host: completionPayload?.Host ?? null,
                eventId: completionPayload?.EventId ?? completionMessage?.EventId ?? "",
              }));
            }

            setRemoteHostStartupEventState(
              hostId,
              completionPayload?.EventId ?? completionMessage?.EventId ?? host?.StartupEventId ?? "",
              false,
              completionPayload?.Success === false ? 0 : 3
            );

            await Promise.all([
              loadSummary({ showLoading: false }),
              loadActiveTasks({ showLoading: false }),
              loadRemoteHosts({ showLoading: false }),
            ]);
            if (activeClientRequestScopeRef.current === requestClientId) {
              setLastRefreshDateTime(new Date().toISOString());
            }
          } catch (streamError) {
            if (activeClientRequestScopeRef.current !== requestClientId) return;
            console.error("Recovered remote host startup stream failed:", streamError);
          } finally {
            if (remoteHostStreamControllersRef.current.get(hostId) === hubController) {
              remoteHostStreamControllersRef.current.delete(hostId);
            }
          }
        })();
      } catch (error) {
        if (activeClientRequestScopeRef.current !== requestClientId) return;
        console.error("Failed to resume remote host startup stream:", error);
      }
    },
    [activeClientId, handleRemoteHostStreamMessage, loadActiveTasks, loadRemoteHosts, loadSummary, mergeRemoteHostFromStream, setRemoteHostStartupEventState, withActiveClientQuery]
  );

  useEffect(() => {
    if (isExecutionControlCenter) {
      return;
    }

    if (!selectedRemoteHost?._id) {
      return;
    }

    const hostId = selectedRemoteHost._id;
    let isCancelled = false;

    const stopRecoveredStartup = () => {
      closeRemoteHostStream(hostId);
      setRemoteHostStartupEventState(hostId, selectedRemoteHost?.StartupEventId ?? "", false);
      setRemoteHostStartupFeeds((current) => {
        const existingFeed = current[hostId];
        if (!existingFeed?.isStreaming) {
          return current;
        }

        return {
          ...current,
          [hostId]: {
            ...existingFeed,
            isStreaming: false,
            isComplete: true,
            success: getRemoteHostStateLabel(selectedRemoteHost) === "Online" ? true : existingFeed.success,
            lastUpdated: new Date().toISOString(),
          },
        };
      });
    };

    const recoverStartup = async () => {
      if (!isRemoteHostStartupResumable(selectedRemoteHost)) {
        stopRecoveredStartup();
        return;
      }

      const startupEventStatus = await getRemoteHostStartupEventStatus(selectedRemoteHost);
      if (isCancelled) {
        return;
      }

      if (!startupEventStatus?.isActive) {
        stopRecoveredStartup();
        return;
      }

      const recoveredEntry = getRecoveredRemoteHostStartupEntry(selectedRemoteHost);
      if (recoveredEntry) {
        setRemoteHostStartupFeeds((current) => {
          const existingFeed = current[hostId];
          if (existingFeed?.messages?.length) {
            return {
              ...current,
              [hostId]: {
                ...existingFeed,
                isStreaming: true,
                isComplete: false,
              },
            };
          }

          return {
            ...current,
            [hostId]: {
              eventId: startupEventStatus?.eventId ?? "",
              isStreaming: true,
              isComplete: false,
              success: null,
              messages: [recoveredEntry],
              lastUpdated: recoveredEntry.timestamp,
            },
          };
        });
      }

      void resumeRemoteHostStartupStream(selectedRemoteHost);
    };

    void recoverStartup();

    return () => {
      isCancelled = true;
    };
  }, [
    closeRemoteHostStream,
    getRemoteHostStartupEventStatus,
    isExecutionControlCenter,
    resumeRemoteHostStartupStream,
    selectedRemoteHost,
    setRemoteHostStartupEventState,
  ]);

  useEffect(() => {
    if (!manualTestOptions.length) {
      setSelectedManualTestKey("azure-batch");
      return;
    }

    if (!manualTestOptions.some((option) => option.key === selectedManualTestKey)) {
      setSelectedManualTestKey(manualTestOptions[0].key);
    }
  }, [manualTestOptions, selectedManualTestKey]);

  useEffect(() => {
    if (!manualTestHistory.length) {
      setSelectedManualTestHistoryId("");
      return;
    }

    if (!manualTestHistory.some((entry) => entry.id === selectedManualTestHistoryId)) {
      setSelectedManualTestHistoryId(manualTestHistory[0].id);
    }
  }, [manualTestHistory, selectedManualTestHistoryId]);

  useEffect(() => {
    const processingEvents = [...backgroundProcesses, ...scheduledWork];
    if (!processingEvents.length) {
      return;
    }

    const processingEventsByEventId = new Map(
      processingEvents
        .map((processingEvent) => [
          getBackgroundProcessEventId(processingEvent).toLowerCase(),
          processingEvent,
        ])
        .filter(([eventId]) => !!eventId)
    );

    setManualTestHistory((currentHistory) => {
      let hasChanges = false;
      const nextHistory = currentHistory.map((entry) => {
        const eventId = entry.eventId || getManualTestEventId(entry.result);
        if (entry.operationName?.toLowerCase() !== BACKGROUND_PROCESSING_TEST_OPERATION || !eventId) {
          return entry;
        }

        const processingEvent = processingEventsByEventId.get(eventId.toLowerCase());
        if (!processingEvent) {
          return entry;
        }

        const nextEntry = reconcileBackgroundProcessingHistoryEntry(
          entry.eventId ? entry : { ...entry, eventId },
          processingEvent
        );
        if (serializeJsonValue(nextEntry) !== serializeJsonValue(entry)) {
          hasChanges = true;
        }
        return nextEntry;
      });

      return hasChanges ? nextHistory : currentHistory;
    });
  }, [backgroundProcesses, scheduledWork]);

  useEffect(() => {
    if (rowDetailsDialog?.formatterKey !== "manual-test-history") {
      return;
    }

    const updatedEntry = manualTestHistory.find((entry) => entry.id === rowDetailsDialog.payload?.id);
    if (!updatedEntry || serializeJsonValue(updatedEntry) === serializeJsonValue(rowDetailsDialog.payload)) {
      return;
    }

    setRowDetailsDialog((currentDialog) => ({
      ...currentDialog,
      payload: updatedEntry,
      subtitle: [updatedEntry.scopeLabel, formatDateTime(updatedEntry.checkedDateTime)]
        .filter(Boolean)
        .join(" | "),
    }));
  }, [manualTestHistory, rowDetailsDialog]);

  useEffect(() => {
    setIsLastActionDialogOpen(!!lastActionResult);
  }, [lastActionResult]);

  const openManualTestDialog = useCallback((manualTestKey = "azure-batch", launcherKey = "") => {
    if (isRunningManualTest) {
      return;
    }

    setSelectedManualTestKey(manualTestKey);
    setManualTestLauncherKey(launcherKey);
    setIsManualTestDialogOpen(true);
  }, [isRunningManualTest]);

  const closeManualTestDialog = useCallback(() => {
    setIsManualTestDialogOpen(false);
  }, []);

  const closeLastActionDialog = useCallback(() => {
    setIsLastActionDialogOpen(false);
  }, []);

  const confirmCloseLastActionDialog = useCallback(async () => {
    setIsLastActionDialogOpen(false);
    return { Success: true };
  }, []);

  const closeRowDetailsDialog = useCallback(() => {
    setRowDetailsDialog(null);
  }, []);

  const confirmCloseRowDetailsDialog = useCallback(async () => {
    setRowDetailsDialog(null);
    return { Success: true };
  }, []);

  const openRowDetailsDialog = useCallback((title, subtitle, payload, formatterKey = "") => {
    setRowDetailsDialog({
      title,
      subtitle,
      payload,
      formatterKey,
    });
  }, []);

  const handleHeaderAction = useCallback(
    async (operationName) => {
      setHeaderActionKey(operationName);
      setLastActionResult(null);

      try {
        const response = await genericMgrRef.current?.RunOperation({
          operationName,
          queryParams: withActiveClientQuery(),
          isWarning: true,
        });

        setLastActionResult(unwrapResult(response));
        await refreshAllSections();
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error(`Batch management header action '${operationName}' failed:`, error);
        setLastActionResult({
          Success: false,
          DisplayMessage: "Batch management action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        });
      } finally {
        setHeaderActionKey("");
      }
    },
    [refreshAllSections, withActiveClientQuery]
  );

  const handleRunManualTest = useCallback(async () => {
    if (!selectedManualTest || isRunningManualTest) {
      return;
    }

    const nextManualTestActionKey = manualTestLauncherKey || `manual-test:${selectedManualTest.key}`;
    setIsRunningManualTest(true);
    setActiveManualTestActionKey(nextManualTestActionKey);

    try {
      const scheduleDurationMinutes = Number(selectedManualTest.scheduleDurationMinutes ?? 0);
      const manualTestQueryParams = {
        ...(selectedManualTest.queryParams ?? {}),
        eventDisplayName: selectedManualTest.label,
        ...(scheduleDurationMinutes > 0
          ? {
            scheduleeventenddate: new Date(
              Date.now() + (scheduleDurationMinutes * 60 * 1000)
            ).toISOString(),
          }
          : {}),
      };
      const response = selectedManualTest.isHostTest
        ? await remoteBatchHostMgrRef.current?.RunOperation({
          operationName: selectedManualTest.operationName,
          id: selectedManualTest.targetId,
          isItem: true,
          queryParams: withActiveClientQuery(manualTestQueryParams),
          isWarning: true,
        })
        : selectedManualTest.isPoolTest
          ? await poolProfileMgrRef.current?.RunOperation({
            operationName: selectedManualTest.operationName,
            id: selectedManualTest.targetId,
            isItem: true,
            isWarning: true,
            ...(selectedManualTest.processorType
              ? { processorType: selectedManualTest.processorType }
              : {}),
            queryParams: withActiveClientQuery(manualTestQueryParams),
          })
        : await genericMgrRef.current?.RunOperation({
          operationName: selectedManualTest.operationName,
          queryParams: withActiveClientQuery(manualTestQueryParams),
          isWarning: true,
        });

      const normalizedResponse = unwrapResult(response);
      const nextHistoryEntry = createManualTestHistoryEntry(selectedManualTest, normalizedResponse);
      setManualTestHistory((current) => [nextHistoryEntry, ...current].slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS));
      setSelectedManualTestHistoryId(nextHistoryEntry.id);
      await refreshAllSections({ showLoading: false, waitForInFlight: true });
      setIsManualTestDialogOpen(false);
      return normalizedResponse;
    } catch (error) {
      console.error(`Manual test '${selectedManualTest.operationName}' failed:`, error);
      const failedResult = {
        Success: false,
        DisplayMessage: "Manual test failed.",
        MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
      };
      const nextHistoryEntry = createManualTestHistoryEntry(selectedManualTest, failedResult);
      setManualTestHistory((current) => [nextHistoryEntry, ...current].slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS));
      setSelectedManualTestHistoryId(nextHistoryEntry.id);
      return failedResult;
    } finally {
      setIsRunningManualTest(false);
      setActiveManualTestActionKey("");
    }
  }, [isRunningManualTest, manualTestLauncherKey, refreshAllSections, selectedManualTest, withActiveClientQuery]);

  const handlePoolManualTest = useCallback(
    async (poolProfile) => {
      const poolId = poolProfile?._id;
      if (!poolId || isRunningManualTest) {
        return;
      }

      const actionKey = `pool-check:${poolId}`;
      const manualTestOption = {
        key: `pool:${poolId}`,
        operationName: "CheckAzureBatchPoolStatus",
        label: `${poolProfile?.Name ?? "Pool Profile"}${poolProfile?.Version ? ` v${poolProfile.Version}` : ""}`,
        scopeLabel: poolProfile?.IsDefault ? "Default Pool" : "Pool Profile",
        targetId: poolId,
      };

      setIsRunningManualTest(true);
      setActiveManualTestActionKey(actionKey);
      setLastActionResult(null);

      try {
        const response = await poolProfileMgrRef.current?.RunOperation({
          operationName: manualTestOption.operationName,
          id: poolId,
          isItem: true,
          queryParams: withActiveClientQuery(),
          isWarning: true,
        });

        const normalizedResponse = unwrapResult(response);
        setLastActionResult(normalizedResponse);

        const nextHistoryEntry = createManualTestHistoryEntry(manualTestOption, normalizedResponse);
        setManualTestHistory((current) => [nextHistoryEntry, ...current].slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS));
        setSelectedManualTestHistoryId(nextHistoryEntry.id);

        await Promise.all([
          loadSummary({ showLoading: false }),
          loadPools({ showLoading: false }),
        ]);
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error("Pool check failed:", error);
        const failedResult = {
          Success: false,
          DisplayMessage: "Pool check failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        };
        setLastActionResult(failedResult);

        const nextHistoryEntry = createManualTestHistoryEntry(manualTestOption, failedResult);
        setManualTestHistory((current) => [nextHistoryEntry, ...current].slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS));
        setSelectedManualTestHistoryId(nextHistoryEntry.id);
      } finally {
        setIsRunningManualTest(false);
        setActiveManualTestActionKey("");
      }
    },
    [isRunningManualTest, loadPools, loadSummary, withActiveClientQuery]
  );

  const handleEnsureReadyHostAction = useCallback(
    async (hostId) => {
      const requestClientId = activeClientId;
      if (!hostId) {
        return;
      }

      const currentHost = remoteHosts.find((entry) => entry?._id === hostId);
      if (hostActionKey || isRemoteHostStartupResumable(currentHost)) {
        return;
      }

      const nextActionKey = `StartRemoteBatchHost:${hostId}`;
      setHostActionKey(nextActionKey);
      setLastActionResult(null);
      closeRemoteHostStream(hostId);

      setRemoteHostStartupFeeds((current) => ({
        ...current,
        [hostId]: {
          eventId: "",
          isStreaming: true,
          isComplete: false,
          success: null,
          messages: [],
          lastUpdated: new Date().toISOString(),
        },
      }));

      let hubController = null;

      try {
        hubController = await remoteBatchHostMgrRef.current?.GetStream({
          operationName: "StartRemoteBatchHost",
          id: hostId,
          type: "sse",
          queryParams: withActiveClientQuery(),
        });
        if (activeClientRequestScopeRef.current !== requestClientId) {
          hubController?.close?.();
          return;
        }

        if (hubController?.Success && typeof hubController.onMessage === "function") {
          remoteHostStreamControllersRef.current.set(hostId, hubController);
          hubController.onMessage((message) => {
            if (activeClientRequestScopeRef.current === requestClientId) {
              handleRemoteHostStreamMessage(hostId, message);
            }
          });
        }

        const response = await remoteBatchHostMgrRef.current?.RunOperation({
          operationName: "StartRemoteBatchHost",
          id: hostId,
          isItem: true,
          queryParams: withActiveClientQuery(),
          isWarning: true,
          processorType: 2,
        });
        if (activeClientRequestScopeRef.current !== requestClientId) {
          hubController?.close?.();
          return;
        }

        const normalizedResponse = unwrapResult(response);
        const isFailedToQueue = normalizedResponse && normalizedResponse.Success === false;
        const queuedEventId = String(normalizedResponse?.EventId ?? "").trim();

        if (isFailedToQueue) {
          const failedResult = buildRemoteHostStartupFailureResult({
            hostId,
            fallbackMessage: "Remote host startup failed to queue.",
            result: normalizedResponse,
            eventId: normalizedResponse?.EventId ?? "",
          });
          setLastActionResult(failedResult);
          appendRemoteHostStartupFeedEntry(hostId, {
            id: `${hostId}:queue-failed:${Date.now()}`,
            eventId: normalizedResponse?.EventId ?? "",
            message: failedResult.DisplayMessage,
            stage: "error",
            uiMessageType: "error",
            timestamp: new Date().toISOString(),
            outputType: "text",
          }, {
            eventId: normalizedResponse?.EventId ?? "",
            isStreaming: false,
            isComplete: true,
            success: false,
          });
          closeRemoteHostStream(hostId);
          return;
        }

        if (queuedEventId) {
          setRemoteHostStartupEventState(hostId, queuedEventId, true, 1);
          setRemoteHosts((current) => current.map((entry) => (
            entry?._id === hostId
              ? {
                ...entry,
                Status: 1,
                StartupEventId: queuedEventId,
                LastStartupRequestedDateTime: new Date().toISOString(),
              }
              : entry
          )));
        }

        if (hubController?.Success && hubController?.untilDone) {
          let hubCompletion = null;

          try {
            hubCompletion = await hubController.untilDone;
            if (activeClientRequestScopeRef.current !== requestClientId) return;
          } catch (streamError) {
            if (activeClientRequestScopeRef.current !== requestClientId) return;
            console.error("Remote host startup stream failed:", streamError);
            setLastActionResult({
              Success: false,
              DisplayMessage: "Remote host startup stream failed.",
              MessageDetails: [String(streamError?.message ?? streamError ?? "Unknown stream error")],
            });
            appendRemoteHostStartupFeedEntry(hostId, {
              id: `${hostId}:stream-error:${Date.now()}`,
              eventId: "",
              message: String(streamError?.message ?? streamError ?? "Unknown stream error"),
              stage: "error",
              uiMessageType: "error",
              timestamp: new Date().toISOString(),
              outputType: "text",
            }, {
              isStreaming: false,
              isComplete: true,
              success: false,
            });
            setRemoteHostStartupEventState(hostId, queuedEventId, false, 0);
            return;
          } finally {
            remoteHostStreamControllersRef.current.delete(hostId);
          }

          const completionMessage = hubCompletion?.data ?? null;
          const completionPayload = completionMessage?.OutputType === "object" ? completionMessage?.Message : null;

          if (completionPayload?.Host?._id) {
            mergeRemoteHostFromStream(completionPayload.Host);
          }

          if (completionPayload?.Success === false) {
            setLastActionResult(buildRemoteHostStartupFailureResult({
              hostId,
              fallbackMessage: "Remote host startup failed.",
              payload: completionPayload,
              host: completionPayload?.Host ?? null,
              eventId: completionPayload?.EventId ?? completionMessage?.EventId ?? "",
            }));
          }

          setRemoteHostStartupEventState(
            hostId,
            completionPayload?.EventId ?? hubCompletion?.data?.EventId ?? queuedEventId,
            false,
            completionPayload?.Success === false ? 0 : 3
          );
        } else {
          if (normalizedResponse?.Success === false) {
            setLastActionResult(buildRemoteHostStartupFailureResult({
              hostId,
              fallbackMessage: "Remote host startup failed.",
              result: normalizedResponse,
              eventId: normalizedResponse?.EventId ?? "",
            }));
          }
        }

        await Promise.all([
          loadSummary({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
          loadRemoteHosts({ showLoading: false }),
        ]);
        if (activeClientRequestScopeRef.current === requestClientId) {
          setLastRefreshDateTime(new Date().toISOString());
        }
      } catch (error) {
        if (activeClientRequestScopeRef.current !== requestClientId) return;
        console.error("Ensure Ready failed:", error);
        setLastActionResult(buildRemoteHostStartupFailureResult({
          hostId,
          fallbackMessage: "Failed to start remote batch host.",
          result: {
            Success: false,
            DisplayMessage: "Failed to start remote batch host.",
            MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
          },
        }));
        appendRemoteHostStartupFeedEntry(hostId, {
          id: `${hostId}:action-error:${Date.now()}`,
          eventId: "",
          message: String(error?.message ?? error ?? "Unknown error"),
          stage: "error",
          uiMessageType: "error",
          timestamp: new Date().toISOString(),
          outputType: "text",
        }, {
          isStreaming: false,
          isComplete: true,
          success: false,
        });
        setRemoteHostStartupEventState(hostId, "", false, 0);
      } finally {
        if (activeClientRequestScopeRef.current === requestClientId) {
          closeRemoteHostStream(hostId);
          setHostActionKey("");
        } else {
          hubController?.close?.();
        }
      }
    },
    [
      activeClientId,
      appendRemoteHostStartupFeedEntry,
      closeRemoteHostStream,
      handleRemoteHostStreamMessage,
      hostActionKey,
      loadActiveTasks,
      loadRemoteHosts,
      loadSummary,
      mergeRemoteHostFromStream,
      remoteHosts,
      setRemoteHostStartupEventState,
      withActiveClientQuery,
    ]
  );

  const handleHostAction = useCallback(
    async (hostId, operationName) => {
      if (!hostId) {
        return;
      }

      const currentHost = remoteHosts.find((entry) => entry?._id === hostId);
      if (operationName !== "StartRemoteBatchHost" && isRemoteHostStartupPending(currentHost)) {
        return;
      }

      if (operationName === "StartRemoteBatchHost") {
        await handleEnsureReadyHostAction(hostId);
        return;
      }

      const nextActionKey = `${operationName}:${hostId}`;
      setHostActionKey(nextActionKey);
      setLastActionResult(null);

      try {
        const response = await remoteBatchHostMgrRef.current?.RunOperation({
          operationName,
          id: hostId,
          isItem: true,
          queryParams: withActiveClientQuery(),
          isWarning: true,
        });

        setLastActionResult(unwrapResult(response));
        await Promise.all([
          loadSummary({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
          loadRemoteHosts({ showLoading: false }),
        ]);
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error(`Remote host action '${operationName}' failed:`, error);
        setLastActionResult({
          Success: false,
          DisplayMessage: "Remote host action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        });
      } finally {
        setHostActionKey("");
      }
    },
    [handleEnsureReadyHostAction, loadActiveTasks, loadRemoteHosts, loadSummary, remoteHosts, withActiveClientQuery]
  );

  const handleCopyDebugLocalCommand = useCallback(async () => {
    if (!selectedRemoteHost?._id || !selectedRemoteHostDebugLocalCommand) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText instanceof Function) {
        await navigator.clipboard.writeText(selectedRemoteHostDebugLocalCommand);
        setCopiedDebugLocalCommandHostId(selectedRemoteHost._id);
        window.setTimeout(() => {
          setCopiedDebugLocalCommandHostId((current) => (current === selectedRemoteHost._id ? "" : current));
        }, 2000);
      }
    } catch (error) {
      console.error("Failed to copy debug-local host command.", error);
    }
  }, [selectedRemoteHost, selectedRemoteHostDebugLocalCommand]);

  const handleOpenBackgroundEvent = useCallback(
    async (eventId) => {
      const normalizedEventId = String(eventId ?? "").trim();
      if (!normalizedEventId) {
        return;
      }

      await eventMgrRef.current?.RunOperation({
        operationName: "ManageEvent",
        queryParams: withActiveClientQuery({
          eventid: normalizedEventId,
          presentation: "panel",
        }),
        isWarning: true,
      });
    },
    [withActiveClientQuery]
  );

  const handlePoolAction = useCallback(
    async (poolProfile, operationName) => {
      const poolId = poolProfile?._id;
      if (!poolId || !operationName) {
        return;
      }

      const nextActionKey = `${operationName}:${poolId}`;
      setPoolActionKey(nextActionKey);
      setLastActionResult(null);

      try {
        const response = await poolProfileMgrRef.current?.RunOperation({
          operationName,
          id: poolId,
          item: poolProfile,
          queryParams: withActiveClientQuery(
            operationName === "createupdatebatchpool" ? { update: "true" } : {}
          ),
          isItem: true,
          isWarning: true,
        });

        setLastActionResult(unwrapResult(response));
        await Promise.all([
          loadSummary({ showLoading: false }),
          loadPools({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
        ]);
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error(`Pool action '${operationName}' failed:`, error);
        setLastActionResult({
          Success: false,
          DisplayMessage: "Pool action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        });
      } finally {
        setPoolActionKey("");
      }
    },
    [loadActiveTasks, loadPools, loadSummary, withActiveClientQuery]
  );

  const handleActiveTaskAction = useCallback(
    async (entry, actionKey, options = {}) => {
      if (!entry || !actionKey) {
        return null;
      }

      if (actionKey === "cancel-event" && !isAzureBatchWorkEventCancellable(entry)) {
        return null;
      }

      if (["terminate-job", "terminate-task"].includes(actionKey)
        && !isAzureBatchWorkRuntimeActionable(entry)) {
        return null;
      }

      const publishResult = options.publishResult !== false;
      const nextActionKey = getActiveTaskActionKey(entry, actionKey);
      setActiveTaskActionKey(nextActionKey);
      setLastActionResult(null);
      let completedActionResult = null;

      try {
        if (actionKey === "cancel-event") {
          if (!entry?.EventId) {
            return;
          }

          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: withActiveClientQuery({
              eventid: entry.EventId,
              status: 4,
              msg: "Cancelled from Batch Management.",
            }),
            isWarning: true,
          });

          completedActionResult = unwrapResult(response);
          const refreshOperations = [
            loadSummary({ showLoading: false }),
            loadActiveTasks({ showLoading: false }),
            loadBackgroundProcesses({ showLoading: false }),
            loadScheduledWork({ showLoading: false }),
          ];

          await Promise.all(refreshOperations);
          setLastRefreshDateTime(new Date().toISOString());
          return completedActionResult;
        }

        if (actionKey === "terminate-job") {
          if (!entry?.JobId) {
            return;
          }

          const response = await genericMgrRef.current?.RunOperation({
            operationName: "ManageAzureBatchJob",
            queryParams: withActiveClientQuery({
              action: "terminate",
              jobId: entry.JobId,
              hostId: entry.HostId ?? "",
              eventid: entry.EventId ?? "",
            }),
            isWarning: true,
          });

          completedActionResult = unwrapResult(response);
          const refreshOperations = [
            loadSummary({ showLoading: false }),
            loadActiveTasks({ showLoading: false }),
          ];

          if (!isExecutionControlCenter) {
            refreshOperations.push(loadRemoteHosts({ showLoading: false }));
          }

          await Promise.all(refreshOperations);
          setLastRefreshDateTime(new Date().toISOString());
          return completedActionResult;
        }

        if (actionKey === "terminate-task") {
          if (!entry?.JobId || !entry?.TaskId) {
            return;
          }

          const response = await genericMgrRef.current?.RunOperation({
            operationName: "ManageAzureBatchTask",
            queryParams: withActiveClientQuery({
              action: "terminate",
              jobId: entry.JobId,
              taskId: entry.TaskId,
              hostId: entry.HostId ?? "",
              eventid: entry.EventId ?? "",
            }),
            isWarning: true,
          });

          completedActionResult = unwrapResult(response);
          const refreshOperations = [
            loadSummary({ showLoading: false }),
            loadActiveTasks({ showLoading: false }),
          ];

          if (!isExecutionControlCenter) {
            refreshOperations.push(loadRemoteHosts({ showLoading: false }));
          }

          await Promise.all(refreshOperations);
          setLastRefreshDateTime(new Date().toISOString());
        }
      } catch (error) {
        console.error(`Active task action '${actionKey}' failed:`, error);
        completedActionResult = {
          Success: false,
          DisplayMessage: "Active task action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        };
      } finally {
        setActiveTaskActionKey("");
        if (publishResult && completedActionResult) {
          setLastActionResult(completedActionResult);
        }
      }

      return completedActionResult;
    },
    [
      isExecutionControlCenter,
      loadActiveTasks,
      loadBackgroundProcesses,
      loadRemoteHosts,
      loadScheduledWork,
      loadSummary,
      withActiveClientQuery,
    ]
  );

  const requestActiveTaskEventCancellation = useCallback((entry) => {
    if (!entry?.EventId || !isAzureBatchWorkEventCancellable(entry)) {
      return;
    }

    setActiveTaskCancelConfirmation(entry);
  }, []);

  const closeActiveTaskEventCancellation = useCallback(() => {
    if (activeTaskActionKey) {
      return;
    }

    setActiveTaskCancelConfirmation(null);
  }, [activeTaskActionKey]);

  const confirmActiveTaskEventCancellation = useCallback(async () => {
    const entry = activeTaskCancelConfirmation;
    if (!entry) {
      return;
    }

    const actionResult = await handleActiveTaskAction(
      entry,
      "cancel-event",
      { publishResult: false }
    );
    setActiveTaskCancelConfirmation(null);

    if (actionResult?.Success === false) {
      setLastActionResult(actionResult);
    }
  }, [activeTaskCancelConfirmation, handleActiveTaskAction]);

  const requestActiveTaskJobTermination = useCallback((entry) => {
    if (!entry?.JobId || !isAzureBatchWorkRuntimeActionable(entry)) {
      return;
    }

    setActiveTaskJobTerminationConfirmation(entry);
  }, []);

  const closeActiveTaskJobTermination = useCallback(() => {
    if (activeTaskActionKey) {
      return;
    }

    setActiveTaskJobTerminationConfirmation(null);
  }, [activeTaskActionKey]);

  const confirmActiveTaskJobTermination = useCallback(async () => {
    const entry = activeTaskJobTerminationConfirmation;
    if (!entry) {
      return;
    }

    await handleActiveTaskAction(entry, "terminate-job");
    setActiveTaskJobTerminationConfirmation(null);
  }, [activeTaskJobTerminationConfirmation, handleActiveTaskAction]);

  const handleBackgroundAction = useCallback(
    async (entry, actionKey, options = {}) => {
      if (!entry?.EventId || !actionKey) {
        return null;
      }

      if ((actionKey === "cancel" && !isBackgroundEventCancellable(entry))
        || (actionKey === "delete" && !isBackgroundEventDeletable(entry))) {
        return null;
      }

      const publishResult = options.publishResult !== false;
      const nextActionKey = getBackgroundActionKey(entry, actionKey);
      setBackgroundActionKey(nextActionKey);
      setLastActionResult(null);
      let completedActionResult = null;

      try {
        if (actionKey === "cancel") {
          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: withActiveClientQuery({
              eventid: entry.EventId,
              status: 4,
              msg: "Cancelled from Background Events.",
            }),
            isWarning: true,
          });

          completedActionResult = unwrapResult(response);
        } else if (actionKey === "delete") {
          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: withActiveClientQuery({
              eventid: entry.EventId,
              harddelete: true,
            }),
            isWarning: true,
          });

          completedActionResult = unwrapResult(response);
          if (completedActionResult?.Success !== false) {
            setSelectedBackgroundKey("");
          }
        }

        const refreshOperations = [
          loadSummary({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
          loadBackgroundProcesses({ showLoading: false }),
          loadScheduledWork({ showLoading: false }),
        ];
        setBackgroundActionKey("");
        void Promise.allSettled(refreshOperations).then(() => {
          setLastRefreshDateTime(new Date().toISOString());
        });
      } catch (error) {
        console.error(`Background event action '${actionKey}' failed:`, error);
        completedActionResult = {
          Success: false,
          DisplayMessage: "Background event action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        };
      } finally {
        setBackgroundActionKey("");
        if (publishResult && completedActionResult) {
          setLastActionResult(completedActionResult);
        }
      }

      return completedActionResult;
    },
    [
      loadActiveTasks,
      loadBackgroundProcesses,
      loadScheduledWork,
      loadSummary,
      withActiveClientQuery,
    ]
  );

  const openRescheduleEventDialog = useCallback((entry) => {
    if (!entry?.EventId || !isScheduledWorkReschedulable(entry)) {
      return;
    }

    setRescheduleEventDraft({
      ...buildRescheduleEventDraft(entry),
      EventId: entry.EventId,
      EventDisplayName: entry.EventDisplayName ?? entry.DisplayName ?? "Scheduled Event",
    });
    setRescheduleEventError("");
    setIsRescheduleDialogOpen(true);
  }, []);

  const closeRescheduleEventDialog = useCallback(() => {
    if (scheduledActionKey.startsWith("reschedule:")) {
      return;
    }

    setIsRescheduleDialogOpen(false);
    setRescheduleEventDraft(null);
    setRescheduleEventError("");
  }, [scheduledActionKey]);

  const handleRescheduleEventFormChange = useCallback((value, fieldName) => {
    setRescheduleEventDraft((current) => ({
      ...current,
      [fieldName]: value,
    }));
    setRescheduleEventError("");
  }, []);

  const handleRescheduleEvent = useCallback(async () => {
    const eventId = String(rescheduleEventDraft?.EventId ?? "").trim();
    const validationMessage = getRescheduleEventValidationMessage(rescheduleEventDraft);
    if (!eventId || validationMessage) {
      setRescheduleEventError(validationMessage || "Select a scheduled event to reschedule.");
      return;
    }

    const actionKey = `reschedule:${eventId}`;
    setScheduledActionKey(actionKey);
    setRescheduleEventError("");

    try {
      const startDate = new Date(rescheduleEventDraft.ScheduledStartDate);
      const endDate = new Date(rescheduleEventDraft.ScheduledEndDate);
      const response = await eventMgrRef.current?.RunOperation({
        operationName: "RescheduleEvent",
        queryParams: withActiveClientQuery({
          eventid: eventId,
          scheduledstartdate: startDate.toISOString(),
          scheduledenddate: endDate.toISOString(),
          scheduleinterval: Number(rescheduleEventDraft.ScheduleInterval),
        }),
        isWarning: true,
      });
      const result = unwrapResult(response);
      if (result?.Success === false) {
        setRescheduleEventError(result?.DisplayMessage ?? "The event could not be rescheduled.");
        return;
      }

      await Promise.all([
        loadSummary({ showLoading: false }),
        loadActiveTasks({ showLoading: false }),
        loadBackgroundProcesses({ showLoading: false }),
        loadScheduledWork({ showLoading: false }),
      ]);
      const newEventId = String(result?.Value ?? result?.EventId ?? "").trim();
      if (newEventId) {
        setSelectedScheduledKey(newEventId);
      }
      setIsRescheduleDialogOpen(false);
      setRescheduleEventDraft(null);
      setLastActionResult(result);
      setLastRefreshDateTime(new Date().toISOString());
    } catch (error) {
      console.error("Scheduled event reschedule failed:", error);
      setRescheduleEventError(String(error?.message ?? error ?? "The event could not be rescheduled."));
    } finally {
      setScheduledActionKey("");
    }
  }, [
    loadActiveTasks,
    loadBackgroundProcesses,
    loadScheduledWork,
    loadSummary,
    rescheduleEventDraft,
    withActiveClientQuery,
  ]);

  const handleScheduledAction = useCallback(
    async (entry, actionKey, options = {}) => {
      if (!entry?.EventId || !actionKey) {
        return null;
      }
      if (["pause", "cancel"].includes(actionKey) && !isScheduledWorkScheduleActive(entry)) {
        return null;
      }

      const publishResult = options.publishResult !== false;
      const nextActionKey = getScheduledActionKey(entry, actionKey);
      setScheduledActionKey(nextActionKey);
      setLastActionResult(null);
      let completedActionResult = null;

      try {
        let actionResult = null;
        if (actionKey === "pause") {
          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: withActiveClientQuery({
              eventid: entry.EventId,
              status: 6,
            }),
            isWarning: true,
          });

          actionResult = unwrapResult(response);
        } else if (actionKey === "run-now") {
          const response = await eventMgrRef.current?.RunOperation({
            operationName: "RunEventNow",
            queryParams: withActiveClientQuery({
              eventid: entry.EventId,
            }),
            isWarning: true,
          });

          actionResult = unwrapResult(response);
        } else if (actionKey === "resume") {
          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: withActiveClientQuery({
              eventid: entry.EventId,
              status: 5,
            }),
            isWarning: true,
          });

          actionResult = unwrapResult(response);
        } else if (actionKey === "cancel") {
          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: withActiveClientQuery({
              eventid: entry.EventId,
              status: 4,
              msg: "Cancelled from Batch Management.",
            }),
            isWarning: true,
          });

          actionResult = unwrapResult(response);
        }

        if (actionResult?.Success === false) {
          completedActionResult = actionResult;
          return completedActionResult;
        }

        await Promise.all([
          loadSummary({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
          loadBackgroundProcesses({ showLoading: false }),
          loadScheduledWork({ showLoading: false }),
        ]);
        completedActionResult = actionResult;
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error(`Scheduled action '${actionKey}' failed:`, error);
        completedActionResult = {
          Success: false,
          DisplayMessage: "Scheduled action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        };
      } finally {
        setScheduledActionKey("");
        if (publishResult && completedActionResult) {
          setLastActionResult(completedActionResult);
        }
      }

      return completedActionResult;
    },
    [
      loadActiveTasks,
      loadBackgroundProcesses,
      loadScheduledWork,
      loadSummary,
      withActiveClientQuery,
    ]
  );

  const requestExecutionActionConfirmation = useCallback((scope, entry, actionKey) => {
    if (scope === "background") {
      if (!entry?.EventId
        || (actionKey === "cancel" && !isBackgroundEventCancellable(entry))
        || (actionKey === "delete" && !isBackgroundEventDeletable(entry))) {
        return;
      }
    } else if (scope === "scheduled") {
      if (actionKey !== "cancel"
        || !entry?.EventId
        || !isScheduledWorkScheduleActive(entry)) {
        return;
      }
    } else if (scope === "active-task") {
      if (actionKey !== "terminate-task"
        || !entry?.JobId
        || !entry?.TaskId
        || !isAzureBatchWorkRuntimeActionable(entry)) {
        return;
      }
    } else {
      return;
    }

    setExecutionActionConfirmation({ scope, entry, actionKey });
  }, []);

  const closeExecutionActionConfirmation = useCallback(() => {
    if (activeTaskActionKey || backgroundActionKey || scheduledActionKey) {
      return;
    }

    setExecutionActionConfirmation(null);
  }, [activeTaskActionKey, backgroundActionKey, scheduledActionKey]);

  const confirmExecutionAction = useCallback(async () => {
    const request = executionActionConfirmation;
    if (!request) {
      return;
    }

    let actionResult = null;
    if (request.scope === "background") {
      actionResult = await handleBackgroundAction(
        request.entry,
        request.actionKey,
        { publishResult: false }
      );
    } else if (request.scope === "scheduled") {
      actionResult = await handleScheduledAction(
        request.entry,
        request.actionKey,
        { publishResult: false }
      );
    } else if (request.scope === "active-task") {
      actionResult = await handleActiveTaskAction(request.entry, request.actionKey);
    }

    setExecutionActionConfirmation(null);
    if (request.scope !== "active-task" && actionResult?.Success === false) {
      setLastActionResult(actionResult);
    }
  }, [
    executionActionConfirmation,
    handleActiveTaskAction,
    handleBackgroundAction,
    handleScheduledAction,
  ]);

  const executionControlScreenActions = useMemo(() => {
    if (!isExecutionControlCenter || isRouteStubScreen) {
      return [];
    }

    if (activeTab === "tests") {
      return [
        createExecutionControlAction({
          key: "manual-tests-open-runner",
          label: "Run Manual Test",
          iconKey: "play",
          disabled: isRunningManualTest,
          title: "Select and run a manual test.",
          onClick: () => openManualTestDialog(
            selectedManualTestHistoryEntry?.key ?? "azure-batch",
            "tests:open-runner"
          ),
        }),
      ];
    }

    if (activeTab === "background") {
      return [
        createExecutionControlAction({
          key: "background-cancel",
          label: "Cancel",
          iconKey: "undo",
          loading: backgroundActionKey === getBackgroundActionKey(selectedBackgroundWork, "cancel"),
          disabled: !selectedBackgroundWork?.EventId
            || !isBackgroundEventCancellable(selectedBackgroundWork)
            || (backgroundActionKey !== "" && backgroundActionKey !== getBackgroundActionKey(selectedBackgroundWork, "cancel")),
          title: "Cancel the selected background event.",
          danger: true,
          onClick: () => requestExecutionActionConfirmation(
            "background",
            selectedBackgroundWork,
            "cancel"
          ),
        }),
        createExecutionControlAction({
          key: "background-delete",
          label: "Delete",
          iconKey: "delete",
          loading: backgroundActionKey === getBackgroundActionKey(selectedBackgroundWork, "delete"),
          disabled: !selectedBackgroundWork?.EventId
            || !isBackgroundEventDeletable(selectedBackgroundWork)
            || (backgroundActionKey !== "" && backgroundActionKey !== getBackgroundActionKey(selectedBackgroundWork, "delete")),
          title: "Permanently delete the selected background event.",
          danger: true,
          onClick: () => requestExecutionActionConfirmation(
            "background",
            selectedBackgroundWork,
            "delete"
          ),
        }),
      ];
    }

    if (activeTab === "active-tasks") {
      return [
        createExecutionControlAction({
          key: "cancel-event",
          label: "Cancel Event",
          iconKey: "undo",
          loading: activeTaskActionKey === getActiveTaskActionKey(selectedActiveTask, "cancel-event"),
          disabled: !selectedActiveTask?.EventId
            || Number(selectedActiveTask?.ProcessorType ?? 0) === 8
            || !isAzureBatchWorkEventCancellable(selectedActiveTask)
            || (activeTaskActionKey !== "" && activeTaskActionKey !== getActiveTaskActionKey(selectedActiveTask, "cancel-event")),
          title: "Cancel the linked Convoke event.",
          onClick: () => requestActiveTaskEventCancellation(selectedActiveTask),
        }),
        createExecutionControlAction({
          key: "terminate-job",
          label: "Terminate Job",
          iconKey: "delete",
          loading: activeTaskActionKey === getActiveTaskActionKey(selectedActiveTask, "terminate-job"),
          disabled: !selectedActiveTask?.JobId
            || !isAzureBatchWorkRuntimeActionable(selectedActiveTask)
            || (activeTaskActionKey !== "" && activeTaskActionKey !== getActiveTaskActionKey(selectedActiveTask, "terminate-job")),
          title: "Terminate the linked Azure Batch job.",
          danger: true,
          onClick: () => requestActiveTaskJobTermination(selectedActiveTask),
        }),
        ...(isExecutionControlCenter ? [] : [
          createExecutionControlAction({
            key: "terminate-task",
            label: "Cancel Task",
            iconKey: "delete",
            loading: activeTaskActionKey === getActiveTaskActionKey(selectedActiveTask, "terminate-task"),
            disabled: !selectedActiveTask?.JobId
              || !selectedActiveTask?.TaskId
              || !isAzureBatchWorkRuntimeActionable(selectedActiveTask)
              || (activeTaskActionKey !== "" && activeTaskActionKey !== getActiveTaskActionKey(selectedActiveTask, "terminate-task")),
            title: "Cancel the linked Azure Batch task.",
            danger: true,
            onClick: () => requestExecutionActionConfirmation(
              "active-task",
              selectedActiveTask,
              "terminate-task"
            ),
          }),
        ]),
      ];
    }

    if (activeTab === "scheduled") {
      return [
        createExecutionControlAction({
          key: "run-now",
          label: "Run Now",
          iconKey: "play",
          loading: scheduledActionKey === getScheduledActionKey(selectedScheduledWork, "run-now"),
          disabled: !selectedScheduledWork?.EventId
            || !["scheduled", "paused", "failed"].includes(String(selectedScheduledWork?.EventStatus ?? "").toLowerCase())
            || (scheduledActionKey !== "" && scheduledActionKey !== getScheduledActionKey(selectedScheduledWork, "run-now")),
          title: "Run the selected scheduled event now.",
          onClick: () => void handleScheduledAction(selectedScheduledWork, "run-now"),
        }),
        createExecutionControlAction({
          key: "reschedule",
          label: "Reschedule",
          iconKey: "refresh",
          disabled: !selectedScheduledWork?.EventId
            || !isScheduledWorkReschedulable(selectedScheduledWork)
            || scheduledActionKey !== "",
          title: "Create a new schedule from this completed or cancelled scheduled event.",
          onClick: () => openRescheduleEventDialog(selectedScheduledWork),
        }),
        createExecutionControlAction({
          key: "pause",
          label: "Pause",
          iconKey: "undo",
          loading: scheduledActionKey === getScheduledActionKey(selectedScheduledWork, "pause"),
          disabled: !selectedScheduledWork?.EventId
            || String(selectedScheduledWork?.EventStatus ?? "").toLowerCase() !== "scheduled"
            || !isScheduledWorkScheduleActive(selectedScheduledWork)
            || (scheduledActionKey !== "" && scheduledActionKey !== getScheduledActionKey(selectedScheduledWork, "pause")),
          title: "Pause the selected scheduled event.",
          onClick: () => void handleScheduledAction(selectedScheduledWork, "pause"),
        }),
        createExecutionControlAction({
          key: "resume",
          label: "Resume",
          iconKey: "success",
          loading: scheduledActionKey === getScheduledActionKey(selectedScheduledWork, "resume"),
          disabled: !selectedScheduledWork?.EventId
            || String(selectedScheduledWork?.EventStatus ?? "").toLowerCase() !== "paused"
            || (scheduledActionKey !== "" && scheduledActionKey !== getScheduledActionKey(selectedScheduledWork, "resume")),
          title: "Resume the selected scheduled event.",
          onClick: () => void handleScheduledAction(selectedScheduledWork, "resume"),
        }),
        createExecutionControlAction({
          key: "cancel",
          label: "Cancel",
          iconKey: "delete",
          loading: scheduledActionKey === getScheduledActionKey(selectedScheduledWork, "cancel"),
          disabled: !selectedScheduledWork?.EventId
            || !isScheduledWorkScheduleActive(selectedScheduledWork)
            || ["completed", "cancelled", "failed"].includes(String(selectedScheduledWork?.EventStatus ?? "").toLowerCase())
            || (scheduledActionKey !== "" && scheduledActionKey !== getScheduledActionKey(selectedScheduledWork, "cancel")),
          title: "Cancel the selected scheduled event.",
          danger: true,
          onClick: () => requestExecutionActionConfirmation(
            "scheduled",
            selectedScheduledWork,
            "cancel"
          ),
        }),
      ];
    }

    if (activeTab === "pools") {
      return [
        createExecutionControlAction({
          key: "pool-check",
          label: "Check",
          iconKey: "success",
          loading: activeManualTestActionKey === `pool-check:${selectedPoolProfile?._id}`,
          disabled: !selectedPoolProfile?._id || isRunningManualTest,
          title: "Check the selected Azure Batch pool status.",
          onClick: () => void handlePoolManualTest(selectedPoolProfile),
        }),
        createExecutionControlAction({
          key: "pool-ensure",
          label: "Ensure",
          iconKey: "server",
          loading: poolActionKey === `createupdatebatchpool:${selectedPoolProfile?._id}`,
          disabled: !selectedPoolProfile?._id
            || (poolActionKey !== "" && poolActionKey !== `createupdatebatchpool:${selectedPoolProfile?._id}`),
          title: "Ensure the selected Azure Batch pool exists.",
          onClick: () => void handlePoolAction(selectedPoolProfile, "createupdatebatchpool"),
        }),
      ];
    }

    return [];
  }, [
    activeManualTestActionKey,
    activeTab,
    activeTaskActionKey,
    backgroundActionKey,
    handleActiveTaskAction,
    requestActiveTaskEventCancellation,
    requestActiveTaskJobTermination,
    requestExecutionActionConfirmation,
    handlePoolAction,
    handlePoolManualTest,
    handleScheduledAction,
    isExecutionControlCenter,
    isRouteStubScreen,
    isRunningManualTest,
    openRescheduleEventDialog,
    openManualTestDialog,
    poolActionKey,
    scheduledActionKey,
    selectedActiveTask,
    selectedBackgroundWork,
    selectedManualTestHistoryEntry,
    selectedPoolProfile,
    selectedScheduledWork,
  ]);

  const commandBarActions = useMemo(
    () => [
      ...(!isPoolProfilesScreen ? [createExecutionControlAction({
        key: "refresh",
        label: "Refresh",
        iconKey: "refresh",
        title: "Refresh execution control data.",
        onClick: () => void refreshAllSections(),
      })] : []),
      ...executionControlScreenActions,
      ...(showCommandCenterBatchControls ? [
        ...(!isPoolProfilesScreen ? [createExecutionControlAction({
          key: "auto-refresh",
          label: isAutoRefreshEnabled ? "Pause Auto Refresh" : "Resume Auto Refresh",
          iconKey: isAutoRefreshEnabled ? "undo" : "success",
          title: isAutoRefreshEnabled ? "Pause automatic refresh." : "Resume automatic refresh.",
          onClick: () => setIsAutoRefreshEnabled((current) => !current),
        })] : []),
        createExecutionControlAction({
          key: "check-azure-batch",
          label: headerActionKey === "CheckAzureBatchStatus" ? "Checking..." : "Check Azure Batch",
          iconKey: "server",
          loading: headerActionKey === "CheckAzureBatchStatus",
          disabled: !!headerActionKey,
          title: "Check Azure Batch status.",
          onClick: () => void handleHeaderAction("CheckAzureBatchStatus"),
        }),
      ] : []),
    ],
    [
      executionControlScreenActions,
      handleHeaderAction,
      headerActionKey,
      isAutoRefreshEnabled,
      isPoolProfilesScreen,
      refreshAllSections,
      showCommandCenterBatchControls,
    ]
  );

  const renderExecutionControlCardGridSection = ({
    title,
    description = "",
    countLabel,
    rows,
    selectedKey,
    setSelectedKey,
    rowKey,
    cardFields,
    emptyContent,
    error,
    loading,
    detailsTitle,
    detailsSubtitle,
    detailsFormatterKey = "",
    headerStatusResolver,
    activeFilterEnabled = false,
    activeFilterFieldName = "IsActive",
    activeFilterOptions = EXECUTION_CARD_GRID_ACTIVE_FILTER_OPTIONS,
    activeFilterDefaultMode,
    activeFilterAriaLabel,
    typeFilterEnabled = false,
    typeFilterFieldName,
    typeFilterOptions = [],
    typeFilterDefaultMode,
    typeFilterAriaLabel,
    minCardWidth = "15rem",
    maxColumns = 3,
  }) => {
    const detailHeaderActions = [{
      key: "open-event",
      Title: "Open Event",
      iconKey: "opennewwindow",
      iconSize: "1.25rem",
      Visible: (row) => String(row?.EventId ?? "").trim() !== "",
      ActionCallback: (row) => handleOpenBackgroundEvent(row?.EventId),
    }, {
      key: "view",
      Title: "View details",
      iconKey: "view",
      iconSize: "1.5rem",
      ActionCallback: (row) => openRowDetailsDialog(
        typeof detailsTitle === "function" ? detailsTitle(row) : title,
        typeof detailsSubtitle === "function" ? detailsSubtitle(row) : "",
        row,
        detailsFormatterKey
      ),
    }];

    return (
      <div className="batch-management-table-card convoke-panel batch-management-resource-card-section">
        <div className="table-header convoke-section-title__row convoke-section-title__row--split">
          <div className="batch-management-section-title-copy">
            <h2>{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
        </div>
        {error ? (
          <div className="table-empty">{error}</div>
        ) : loading ? (
          <div className="batch-management-section-loading"><Loading /></div>
        ) : (
          <CardGridControl
            rows={rows}
            cardFields={cardFields}
            rowKey={rowKey}
            selectedRowKeys={selectedKey ? [selectedKey] : []}
            onSelectedRowKeysChange={(keys) => setSelectedKey(String(keys?.[0] ?? ""))}
            headerStatusResolver={headerStatusResolver}
            headerIconActions={detailHeaderActions}
            selectedHeaderIconActions={detailHeaderActions}
            toolbarLeadingContent={(
              <span className="batch-management-card-grid-count">{countLabel}</span>
            )}
            selectable
            searchable
            activeFilterEnabled={activeFilterEnabled}
            activeFilterFieldName={activeFilterFieldName}
            activeFilterOptions={activeFilterOptions}
            activeFilterDefaultMode={activeFilterDefaultMode}
            activeFilterAriaLabel={activeFilterAriaLabel ?? `${title} active filter`}
            typeFilterEnabled={typeFilterEnabled && typeFilterOptions.length > 0}
            typeFilterFieldName={typeFilterFieldName}
            typeFilterOptions={typeFilterOptions}
            typeFilterDefaultMode={typeFilterDefaultMode}
            typeFilterAriaLabel={typeFilterAriaLabel ?? `${title} filter`}
            emptyContent={emptyContent}
            minCardWidth={minCardWidth}
            maxColumns={maxColumns}
            disableInlineEdit
          />
        )}
      </div>
    );
  };

  const renderAzureBatchConnectionPanel = () => (
    <section className="batch-management-panel convoke-panel batch-management-connection-panel">
      <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
        <h2>Azure Batch Connection</h2>
        <span>{loadingState.summary ? "Loading..." : `Checked ${formatDateTime(summary?.CheckedDateTime)}`}</span>
      </div>
      {loadingState.summary || loadingState.pools ? (
        <div className="batch-management-section-loading"><Loading /></div>
      ) : (
        <div className="batch-management-detail-grid batch-management-connection-grid">
          <div className="detail-item detail-item-wide">
            <span className="detail-label">Account URL</span>
            <span className="detail-value is-monospace">
              {formatOverviewValue(summary?.AzureBatchAccountUrl)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Environment</span>
            <span className="detail-value">
              {formatOverviewValue(summary?.AzureBatchEnvironment)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Organization</span>
            <span className="detail-value">
              {formatOverviewValue(summary?.AzureBatchOrgName)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">URL Prefix</span>
            <span className="detail-value is-monospace">
              {formatOverviewValue(summary?.AzureBatchUrlPrefix)}
            </span>
          </div>
          <div className="detail-item detail-item-wide">
            <span className="detail-label">Subscription</span>
            <span className="detail-value is-monospace">
              {formatOverviewValue(summary?.AzureBatchSubscriptionId)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Default Pool Profile</span>
            <span className="detail-value">
              {defaultPoolProfile
                ? `${defaultPoolProfile?.Name ?? "Unnamed"}${defaultPoolProfile?.IsDefault ? " | Default" : ""}`
                : "Not configured"}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Default Pool ID</span>
            <span className="detail-value is-monospace">
              {formatOverviewValue(summary?.AzureBatchDefaultPoolId ?? defaultPoolProfile?.PoolId, "Not configured")}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Pool Type / VM Size</span>
            <span className="detail-value">
              {defaultPoolProfile
                ? `${formatPoolType(defaultPoolProfile?.PoolType)} | ${formatOverviewValue(defaultPoolProfile?.VirtualMachineSize, "Default VM size")}`
                : "Not configured"}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Node Policy</span>
            <span className="detail-value">
              {formatPoolProfileNodes(defaultPoolProfile)}
            </span>
          </div>
          <div className="detail-item detail-item-wide">
            <span className="detail-label">Image / Agent</span>
            <span className="detail-value is-monospace">
              {formatPoolProfileImage(defaultPoolProfile)}
            </span>
          </div>
          <div className="detail-item detail-item-wide">
            <span className="detail-label">Network</span>
            <span className="detail-value is-monospace">
              {formatPoolProfileNetwork(defaultPoolProfile)}
            </span>
          </div>
        </div>
      )}
    </section>
  );

  const executionActionConfirmationScope = executionActionConfirmation?.scope ?? "";
  const executionActionConfirmationAction = executionActionConfirmation?.actionKey ?? "";
  const executionActionConfirmationEntry = executionActionConfirmation?.entry ?? null;
  const isExecutionActionConfirmationBusy = executionActionConfirmationScope === "background"
    ? backgroundActionKey === getBackgroundActionKey(
      executionActionConfirmationEntry,
      executionActionConfirmationAction
    )
    : executionActionConfirmationScope === "scheduled"
      ? scheduledActionKey === getScheduledActionKey(
        executionActionConfirmationEntry,
        executionActionConfirmationAction
      )
      : executionActionConfirmationScope === "active-task"
        ? activeTaskActionKey === getActiveTaskActionKey(
          executionActionConfirmationEntry,
          executionActionConfirmationAction
        )
        : false;
  const executionActionConfirmationTitle = executionActionConfirmationScope === "scheduled"
    ? "Cancel Scheduled Event"
    : executionActionConfirmationScope === "active-task"
      ? "Cancel Task"
      : executionActionConfirmationAction === "delete"
        ? "Delete Event"
        : "Cancel Event";
  const executionActionConfirmationPendingText = executionActionConfirmationScope === "active-task"
    ? "Cancelling Task..."
    : executionActionConfirmationAction === "delete"
      ? "Deleting..."
      : "Cancelling...";
  const executionActionConfirmationOkText = isExecutionActionConfirmationBusy
    ? executionActionConfirmationPendingText
    : executionActionConfirmationTitle;
  const executionActionConfirmationCancelText = executionActionConfirmationAction === "delete"
    ? "Keep Event"
    : executionActionConfirmationScope === "active-task"
      ? "Keep Task"
      : "Keep Event";
  const executionActionConfirmationDescription = executionActionConfirmationScope === "scheduled"
    ? "Cancel the selected scheduled Event? Future scheduled runs will stop."
    : executionActionConfirmationScope === "active-task"
      ? "Cancel the selected Azure Batch task? The linked Event will be refreshed after Azure reaches a terminal state."
      : executionActionConfirmationAction === "delete"
        ? "Permanently delete the selected Event? This cannot be undone."
        : "Cancel the selected background Event? Azure Batch work already running may take a moment to reach its final state.";


  if (!canViewBatchManagement) {
    return (
      <div className="batch-management-screen convoke-surface-context">
        <div className="batch-management-empty">
          <IconControl iconKey="server" />
          <div className="title">{screenCopy.deniedTitle}</div>
          <div className="description">
            {screenCopy.deniedDescription}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="batch-management-screen convoke-surface-context">
      <div className="batch-management-header">
        <div>
          <div className="eyebrow">{screenCopy.eyebrow}</div>
          <h1>{screenCopy.title}</h1>
          <p>{screenCopy.description}</p>
        </div>
        {!isRouteStubScreen && showCommandCenterBatchControls ? (
          <div className="batch-management-header-status" aria-label="Refresh status">
            <span>
              <span className="batch-management-header-status-label">Auto Refresh</span>
              <span>{isAutoRefreshEnabled ? `On (${AUTO_REFRESH_INTERVAL_SECONDS}s)` : "Off"}</span>
            </span>
            <span>
              Last Refresh: {lastRefreshDateTime ? formatDateTime(lastRefreshDateTime) : "Not yet loaded"}
            </span>
            {isAutoRefreshEnabled && isAutoRefreshBlocked ? (
              <span className="is-warning">Paused while an action is running</span>
            ) : null}
          </div>
        ) : null}
      </div>

      {!isRouteStubScreen ? (
        <ConvokeCommandBar
          actions={commandBarActions}
          ariaLabel={`${screenCopy.title} actions`}
          className="batch-management-command-bar convoke-command-bar--screen"
          useSparkButtons
        />
      ) : null}

      <div className="batch-management-content">
        <DialogControl
          className="batch-management-dialog"
          openDialog={!!activeTaskCancelConfirmation}
          title="Cancel Event"
          subTitle={activeTaskCancelConfirmation?.DisplayName ?? activeTaskCancelConfirmation?.JobId ?? ""}
          okText={activeTaskActionKey ? "Cancelling..." : "Cancel Event"}
          cancelText="Keep Event"
          loadingOk={!!activeTaskActionKey}
          disableOk={!!activeTaskActionKey}
          disableCancel={!!activeTaskActionKey}
          disableXButton={!!activeTaskActionKey}
          onOk={confirmActiveTaskEventCancellation}
          onCancel={closeActiveTaskEventCancellation}
          onX={closeActiveTaskEventCancellation}
        >
          <p>Cancel the linked Convoke Event? Any Azure Batch work already running may take a moment to reach its final state.</p>
        </DialogControl>

        <DialogControl
          className="batch-management-dialog"
          openDialog={!!executionActionConfirmation}
          title={executionActionConfirmationTitle}
          subTitle={executionActionConfirmationEntry?.DisplayName
            ?? executionActionConfirmationEntry?.EventDisplayName
            ?? executionActionConfirmationEntry?.JobId
            ?? executionActionConfirmationEntry?.EventId
            ?? ""}
          okText={executionActionConfirmationOkText}
          cancelText={executionActionConfirmationCancelText}
          loadingOk={isExecutionActionConfirmationBusy}
          disableOk={isExecutionActionConfirmationBusy}
          disableCancel={isExecutionActionConfirmationBusy}
          disableXButton={isExecutionActionConfirmationBusy}
          onOk={confirmExecutionAction}
          onCancel={closeExecutionActionConfirmation}
          onX={closeExecutionActionConfirmation}
        >
          <p>{executionActionConfirmationDescription}</p>
        </DialogControl>

        <DialogControl
          className="batch-management-dialog"
          openDialog={isLastActionDialogOpen && !!lastActionResult}
          title={lastActionResult?.Success ? "Operation Completed" : "Operation Error"}
          okText="Close"
          excludeCancel={true}
          onOk={confirmCloseLastActionDialog}
          onCancel={closeLastActionDialog}
          onX={closeLastActionDialog}
        >
          <div
            className={`batch-management-message ${
              lastActionResult?.Success ? "is-ok" : "is-error"
            }`}
          >
            <div className="message-title">{lastActionDisplayMessage}</div>
            {lastActionPayload ? (
              <div className="message-meta">
                {lastActionPayload?.TestName ? (
                  <span>{lastActionPayload.TestName}</span>
                ) : null}
                {getPayloadStatusLabel(lastActionPayload) ? (
                  <span>Status: {getPayloadStatusLabel(lastActionPayload)}</span>
                ) : null}
                {lastActionPayload?.CheckedDateTime ? (
                  <span>Checked: {formatDateTime(lastActionPayload.CheckedDateTime)}</span>
                ) : null}
              </div>
            ) : null}
            {lastActionDetailLines.slice(0, 8).map((entry) => (
              <div key={entry} className="message-detail">
                {entry}
              </div>
            ))}
          </div>
        </DialogControl>

        <DialogControl
          className="batch-management-dialog"
          openDialog={!!activeTaskJobTerminationConfirmation}
          title="Terminate Job"
          subTitle={activeTaskJobTerminationConfirmation?.DisplayName
            ?? activeTaskJobTerminationConfirmation?.JobId
            ?? ""}
          okText={activeTaskActionKey ? "Terminating..." : "Terminate Job"}
          cancelText="Keep Job"
          loadingOk={!!activeTaskActionKey}
          disableOk={!!activeTaskActionKey}
          disableCancel={!!activeTaskActionKey}
          disableXButton={!!activeTaskActionKey}
          onOk={confirmActiveTaskJobTermination}
          onCancel={closeActiveTaskJobTermination}
          onX={closeActiveTaskJobTermination}
        >
          <p>Terminate the selected Azure Batch job? Any running tasks in this job will be stopped.</p>
        </DialogControl>

        <DialogControl
          className="batch-management-dialog"
          openDialog={isRescheduleDialogOpen}
          title="Reschedule Event"
          subTitle={rescheduleEventDraft?.EventDisplayName ?? ""}
          okText={scheduledActionKey.startsWith("reschedule:") ? "Creating..." : "Create Schedule"}
          cancelText="Cancel"
          loadingOk={scheduledActionKey.startsWith("reschedule:")}
          disableOk={scheduledActionKey.startsWith("reschedule:")
            || !!getRescheduleEventValidationMessage(rescheduleEventDraft)}
          disableCancel={scheduledActionKey.startsWith("reschedule:")}
          disableXButton={scheduledActionKey.startsWith("reschedule:")}
          onOk={handleRescheduleEvent}
          onCancel={closeRescheduleEventDialog}
          onX={closeRescheduleEventDialog}
        >
          <p>Create a new scheduled Event using the same operation, data, pool profile, and display name.</p>
          <FormControl
            metadata={RESCHEDULE_EVENT_FORM_METADATA}
            formFields={RESCHEDULE_EVENT_FORM_FIELDS}
            itemData={rescheduleEventDraft ?? {}}
            groupId="rescheduleEvent"
            disableFooter
            disableTabs
            disableGroups
            disableMinWidth
            FormOnlyMode
            disabled={scheduledActionKey.startsWith("reschedule:")}
            onChange={handleRescheduleEventFormChange}
            onSave={() => {}}
          />
          {rescheduleEventError ? (
            <div className="batch-management-message is-error" role="alert">
              <div className="message-title">{rescheduleEventError}</div>
            </div>
          ) : null}
        </DialogControl>

        <DialogControl
          className="batch-management-dialog batch-management-row-details-dialog"
          openDialog={!!rowDetailsDialog}
          title={rowDetailsDialog?.title ?? "Details"}
          subTitle={rowDetailsDialog?.subtitle ?? ""}
          okText="Close"
          excludeCancel={true}
          onOk={confirmCloseRowDetailsDialog}
          onCancel={closeRowDetailsDialog}
          onX={closeRowDetailsDialog}
        >
          {renderExecutionDetails(rowDetailsDialog?.payload, rowDetailsDialog?.formatterKey)}
        </DialogControl>

        {!hideTabs ? (
          <div className="batch-management-tabs">
            {visibleTabs.map((tab) => (
              <ButtonControl
                key={tab.key}
                value={tab.label}
                type="standard"
                className={`batch-management-tab ${activeTab === tab.key ? "is-active" : ""} ${tab.isStub ? "is-stub" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              />
            ))}
          </div>
        ) : null}

      <DialogControl
        className="batch-management-dialog"
        openDialog={isManualTestDialogOpen}
        title="Manual Tests"
        okText={isRunningManualTest ? "Running..." : "Run Test"}
        cancelText="Close"
        loadingOk={isRunningManualTest}
        disableOk={!selectedManualTest || isRunningManualTest}
        disableCancel={isRunningManualTest}
        disableXButton={isRunningManualTest}
        onOk={handleRunManualTest}
        onCancel={closeManualTestDialog}
        onX={closeManualTestDialog}
      >
        <div className="batch-management-test-selector">
          <CardGridControl
            rows={manualTestOptionCardRows}
            cardFields={MANUAL_TEST_OPTION_CARD_FIELDS}
            rowKey={(option) => option.CardKey}
            selectedRowKeys={selectedManualTest ? [selectedManualTest.key] : []}
            onSelectedRowKeysChange={(selectedKeys, option, key) => {
              if (isRunningManualTest) return;
              setSelectedManualTestKey(String(selectedKeys[0] ?? key ?? option?.key ?? "azure-batch"));
            }}
            selectable
            searchable={false}
            activeFilterEnabled={false}
            disabled={isRunningManualTest}
            emptyContent="No manual tests are available."
            minCardWidth="16rem"
            maxColumns={2}
          />
        </div>
      </DialogControl>

      <div className={[
        "batch-management-scroll-content",
        activeTab === "remote-hosts" ? "is-remote-hosts" : "",
        isExecutionControlCenter && activeTab === "active-tasks" ? "is-card-body-scroll" : "",
      ].filter(Boolean).join(" ")}>
      {activeStubTab ? (
        <div className="batch-management-table-card convoke-panel batch-management-stub-panel">
          <div className="batch-management-stub-icon">
            <IconControl iconKey="lock" />
          </div>
          <div>
            <div className="batch-management-stub-kicker">Phase 2 Placeholder</div>
            <h2>{activeStubTab.label}</h2>
            <p>{activeStubTab.description}</p>
            <div className="batch-management-message">
              This section is intentionally disabled in Phase 1. No backend operations are called and no placeholder data is shown.
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "overview" ? (
        <div className="batch-management-overview">
          {sectionErrors.summary ? (
            <div className="batch-management-message is-error">{sectionErrors.summary}</div>
          ) : null}
          <section className="batch-management-panel convoke-panel batch-management-summary-panel">
            <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
              <h2>Execution Summary</h2>
              <span>{loadingState.summary ? "Loading..." : `Checked ${formatDateTime(summary?.CheckedDateTime)}`}</span>
            </div>
            <div className="batch-management-card-grid batch-management-summary-metrics">
            <div className="batch-management-card convoke-panel convoke-metric-card">
              <div className="card-label">Azure Batch</div>
              <div className={`card-value ${getStatusToneClass(summary?.AzureBatchStatus)}`}>
                {loadingState.summary ? "Loading..." : (summary?.AzureBatchStatus ?? "Unknown")}
              </div>
              <div className="card-meta">
                {loadingState.summary
                  ? "Loading Azure Batch status."
                  : (summary?.AzureBatchMessage ?? "No Azure Batch status has been loaded yet.")}
              </div>
            </div>
            <div className="batch-management-card convoke-panel convoke-metric-card">
              <div className="card-label">Configured Pool Profiles</div>
              <div className="card-value">{loadingState.summary ? "..." : (summary?.ConfiguredPoolCount ?? 0)}</div>
              <div className="card-meta">
                {loadingState.pools
                  ? "Loading pool profiles."
                  : defaultPoolProfile
                    ? `Default: ${defaultPoolProfile?.Name ?? defaultPoolProfile?.PoolId ?? "Configured"}`
                    : "No default pool profile is configured."}
              </div>
            </div>
            <div className="batch-management-card convoke-panel convoke-metric-card">
              <div className="card-label">Azure Pools</div>
              <div className="card-value">{loadingState.summary ? "..." : (summary?.AzurePoolCount ?? 0)}</div>
              <div className="card-meta">
                Physical pools returned by the Azure Batch account.
              </div>
            </div>
            {!isExecutionControlCenter ? (
              <>
                <div className="batch-management-card convoke-panel convoke-metric-card">
                  <div className="card-label">Remote Hosts</div>
                  <div className="card-value">{loadingState.summary ? "..." : (summary?.RemoteHostCount ?? 0)}</div>
                  <div className="card-meta">
                    {loadingState.summary
                      ? "Loading remote host summary."
                      : `${summary?.OnlineRemoteHostCount ?? 0} online, ${summary?.StartingRemoteHostCount ?? 0} starting, ${summary?.ErrorRemoteHostCount ?? 0} error.`}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="batch-management-card convoke-panel convoke-metric-card">
                  <div className="card-label">Recent Failures</div>
                  <div className="card-value">{loadingState.summary ? "..." : (summary?.BackgroundFailedLast24HoursCount ?? 0)}</div>
                  <div className="card-meta">
                    Failed background events recorded in the last 24 hours.
                  </div>
                </div>
                <div className="batch-management-card convoke-panel convoke-metric-card">
                  <div className="card-label">Scheduled Events</div>
                  <div className="card-value">{loadingState.scheduled ? "..." : scheduledWork.length}</div>
                  <div className="card-meta">
                    Currently visible scheduled Azure Batch events.
                  </div>
                </div>
              </>
            )}
            <div className="batch-management-card convoke-panel convoke-metric-card">
              <div className="card-label">Background Events</div>
              <div className="card-value">{loadingState.summary ? "..." : (summary?.BackgroundInProgressCount ?? 0)}</div>
              <div className="card-meta">
                {loadingState.summary
                  ? "Loading background summary."
                  : `${summary?.BackgroundScheduledCount ?? 0} scheduled, ${summary?.BackgroundFailedLast24HoursCount ?? 0} failed in the last 24 hours.`}
              </div>
            </div>
            <div className="batch-management-card convoke-panel convoke-metric-card">
              <div className="card-label">{isExecutionControlCenter ? EXECUTION_CONTROL_AZURE_BATCH_WORK_LABEL : "Active Tasks"}</div>
              <div className="card-value">{loadingState.summary ? "..." : (summary?.ActiveTaskCount ?? 0)}</div>
              <div className="card-meta">
                {isExecutionControlCenter
                  ? "Azure Batch-backed background event estimate for the current scope."
                  : "Combined active host and background task estimate for the current scope."}
              </div>
            </div>
            {!isExecutionControlCenter ? (
              <>
                <div className="batch-management-card convoke-panel convoke-metric-card">
                  <div className="card-label">Last Remote Heartbeat</div>
                  <div className="card-value">
                    {loadingState.summary ? "Loading..." : formatDateTime(summary?.LastRemoteHeartbeatDateTime)}
                  </div>
                  <div className="card-meta">
                    {loadingState.summary
                      ? "Loading heartbeat status."
                      : `${summary?.StaleRemoteHostCount ?? 0} host(s) currently have a stale heartbeat.`}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="batch-management-card convoke-panel convoke-metric-card">
                  <div className="card-label">Loaded Background Rows</div>
                  <div className="card-value">{loadingState.background ? "..." : backgroundProcesses.length}</div>
                  <div className="card-meta">
                    Recent/current background events from LoadBatchBackgroundProcesses.
                  </div>
                </div>
                <div className="batch-management-card convoke-panel convoke-metric-card">
                  <div className="card-label">Configured Pool Profiles Loaded</div>
                  <div className="card-value">{loadingState.pools ? "..." : poolProfiles.length}</div>
                  <div className="card-meta">
                    Configured pool profiles loaded from LoadBatchPools.
                  </div>
                </div>
              </>
            )}
            </div>
          </section>

          <div className="batch-management-panel-grid">
            {!isExecutionControlCenter ? (
              <>
            <section className="batch-management-panel convoke-panel">
              <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
                <h2>Remote Host Snapshot</h2>
                <span>{loadingState.remoteHosts ? "Loading..." : `${remoteHosts.length} host(s)`}</span>
              </div>
              {sectionErrors.remoteHosts ? (
                <div className="table-empty">{sectionErrors.remoteHosts}</div>
              ) : loadingState.remoteHosts ? (
                <div className="batch-management-section-loading"><Loading /></div>
              ) : (
              <div className="mini-list convoke-record-list">
                {remoteHosts.slice(0, 5).map((host) => (
                  <div key={host?._id} className="mini-list-row convoke-record-row">
                    <div>
                      <div className="mini-list-title convoke-record-row__title">
                        {host?.Name ?? "Remote Batch Host"}
                      </div>
                      <div className="mini-list-detail convoke-record-row__detail">
                        {host?.ClientShortName ?? "DEFAULT"} |{" "}
                        {host?.Endpoint ?? "No endpoint"}
                      </div>
                    </div>
                    <div className={`mini-list-status convoke-record-row__status ${getStatusToneClass(getRemoteHostStateLabel(host))}`}>
                      {getRemoteHostStateLabel(host)}
                    </div>
                  </div>
                ))}
                {remoteHosts.length === 0 ? (
                  <div className="mini-list-empty convoke-record-list__empty">
                    No remote host registrations were found.
                  </div>
                ) : null}
              </div>
              )}
            </section>

              </>
            ) : (
              <>
            <section className="batch-management-panel convoke-panel">
              <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
                <h2>Azure Batch Runtime Snapshot</h2>
                <span>{loadingState.activeTasks ? "Loading..." : `${activeTasks.length} row(s)`}</span>
              </div>
              {sectionErrors.activeTasks ? (
                <div className="table-empty">{sectionErrors.activeTasks}</div>
              ) : loadingState.activeTasks ? (
                <div className="batch-management-section-loading"><Loading /></div>
              ) : (
              <div className="mini-list convoke-record-list">
                {activeTasks.slice(0, 5).map((entry, index) => (
                  <OverviewEventSnapshotRow
                    key={`${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? index}`}
                    eventId={entry?.EventId}
                    onOpen={handleOpenBackgroundEvent}
                    title="Open Azure Batch runtime event"
                  >
                    <div>
                      <div className="mini-list-title convoke-record-row__title">
                        {entry?.DisplayName ?? EXECUTION_CONTROL_AZURE_BATCH_WORK_LABEL}
                      </div>
                      <div className="mini-list-detail convoke-record-row__detail">
                        {entry?.ProcessorLabel ?? "Unknown processor"} | {entry?.RuntimeStatus ?? "Unknown"}
                      </div>
                    </div>
                    <div className="mini-list-status convoke-record-row__status">
                      {entry?.PoolId ?? "-"}
                    </div>
                  </OverviewEventSnapshotRow>
                ))}
                {activeTasks.length === 0 ? (
                  <div className="mini-list-empty convoke-record-list__empty">
                    No active Azure Batch tasks are currently visible.
                  </div>
                ) : null}
              </div>
              )}
            </section>

            <section className="batch-management-panel convoke-panel">
              <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
                <h2>Scheduled Events Snapshot</h2>
                <span>{loadingState.scheduled ? "Loading..." : `${scheduledWork.length} item(s)`}</span>
              </div>
              {sectionErrors.scheduled ? (
                <div className="table-empty">{sectionErrors.scheduled}</div>
              ) : loadingState.scheduled ? (
                <div className="batch-management-section-loading"><Loading /></div>
              ) : (
              <div className="mini-list convoke-record-list">
                {scheduledWork.slice(0, 5).map((entry, index) => (
                  <OverviewEventSnapshotRow
                    key={`${entry?.EventId ?? "scheduled"}:${index}`}
                    eventId={entry?.EventId}
                    onOpen={handleOpenBackgroundEvent}
                    title="Open scheduled event"
                  >
                    <div>
                      <div className="mini-list-title convoke-record-row__title">
                        {entry?.DisplayName ?? "Scheduled Events"}
                      </div>
                      <div className="mini-list-detail convoke-record-row__detail">
                        {entry?.ProcessorLabel ?? "Scheduled"} | {entry?.ScheduleStatus ?? entry?.EventStatus ?? "Unknown"}
                      </div>
                    </div>
                    <div className="mini-list-status convoke-record-row__status">
                      {formatDateTime(entry?.NextRunDateTime ?? entry?.ScheduledStartDate)}
                    </div>
                  </OverviewEventSnapshotRow>
                ))}
                {scheduledWork.length === 0 ? (
                  <div className="mini-list-empty convoke-record-list__empty">
                    No scheduled Azure Batch work is currently visible.
                  </div>
                ) : null}
              </div>
              )}
            </section>
              </>
            )}

            <section className="batch-management-panel convoke-panel">
              <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
                <h2>Background Event Snapshot</h2>
                <span>{loadingState.background ? "Loading..." : `${backgroundSnapshotEntries.length} item(s)`}</span>
              </div>
              {sectionErrors.background ? (
                <div className="table-empty">{sectionErrors.background}</div>
              ) : loadingState.background ? (
                <div className="batch-management-section-loading"><Loading /></div>
              ) : (
              <div className="mini-list convoke-record-list">
                {backgroundSnapshotEntries.map((entry) => (
                  <OverviewEventSnapshotRow
                    key={entry?._id ?? entry?.EventId}
                    eventId={entry?.EventId ?? entry?._id}
                    onOpen={handleOpenBackgroundEvent}
                    title="Open background event"
                  >
                    <div>
                      <div className="mini-list-title convoke-record-row__title">
                        {entry?.DisplayName ?? "Background Events"}
                      </div>
                      <div className="mini-list-detail convoke-record-row__detail">
                        {PROCESSOR_TYPE_LABELS[Number(entry?.ProcessorType ?? 0)] ??
                          "Unknown"}{" "}
                        |{" "}
                        {EVENT_STATUS_LABELS[Number(entry?.EventStatus ?? -1)] ??
                          "Unknown"}
                      </div>
                    </div>
                    <div className="mini-list-status convoke-record-row__status">
                      {formatDateTime(entry?.RunDate ?? entry?.CreatedDate)}
                    </div>
                  </OverviewEventSnapshotRow>
                ))}
                {backgroundSnapshotEntries.length === 0 ? (
                  <div className="mini-list-empty convoke-record-list__empty">
                    No background activity was found in the last 7 days.
                  </div>
                ) : null}
              </div>
              )}
            </section>
          </div>
        </div>
      ) : null}

      {activeTab === "connection" ? (
        <div className="batch-management-overview batch-management-connection-screen">
          {sectionErrors.summary ? (
            <div className="batch-management-message is-error">{sectionErrors.summary}</div>
          ) : null}
          {sectionErrors.pools ? (
            <div className="batch-management-message is-error">{sectionErrors.pools}</div>
          ) : null}
          {renderAzureBatchConnectionPanel()}
        </div>
      ) : null}

      {activeTab === "active-tasks" ? (
        renderExecutionControlCardGridSection({
          title: isExecutionControlCenter ? EXECUTION_CONTROL_AZURE_BATCH_WORK_LABEL : "Active Tasks",
          description: isExecutionControlCenter
            ? "Cards show jobs returned by the Azure Batch account. Current contains non-completed jobs, History contains successful completed jobs, and Failed contains completed jobs with failed tasks. Linked Convoke Events can be opened from the Event icon."
            : "Cards show active Azure Batch jobs and tasks with their current status, pool, and start time.",
          countLabel: `${activeTaskCardRows.length} ${isExecutionControlCenter ? "row(s)" : "task(s)"}`,
          rows: activeTaskCardRows,
          selectedKey: selectedActiveTaskKey,
          setSelectedKey: setSelectedActiveTaskKey,
          rowKey: (entry) => entry.CardKey,
          cardFields: ACTIVE_TASK_CARD_FIELDS,
          emptyContent: isExecutionControlCenter
            ? "No Azure Batch work rows are currently visible."
            : "No active tasks are currently visible.",
          error: sectionErrors.activeTasks,
          loading: loadingState.activeTasks,
          detailsTitle: (row) => row?.DisplayName ?? EXECUTION_CONTROL_AZURE_BATCH_WORK_LABEL,
          detailsSubtitle: (row) => [row?.ScopeType, row?.JobId, row?.TaskId].filter(Boolean).join(" | "),
          headerStatusResolver: (row) => getExecutionCardHeaderStatus(row?.CardHeaderStatus),
          activeFilterEnabled: false,
          typeFilterEnabled: true,
          typeFilterFieldName: "CardActivity",
          typeFilterOptions: AZURE_BATCH_WORK_FILTER_OPTIONS,
          typeFilterDefaultMode: "current",
          typeFilterAriaLabel: "Azure Batch job filter",
          minCardWidth: "16rem",
          maxColumns: 3,
        })
      ) : null}

      {activeTab === "remote-hosts" ? (
        <div className="batch-management-table-card convoke-panel is-remote-hosts">
          <div className="table-header convoke-section-title__row convoke-section-title__row--split">
            <h2>Remote Hosts</h2>
            <span>{loadingState.remoteHosts ? "Loading..." : `${remoteHosts.length} host(s)`}</span>
          </div>
          {sectionErrors.remoteHosts ? (
            <div className="table-empty">{sectionErrors.remoteHosts}</div>
          ) : loadingState.remoteHosts ? (
            <div className="batch-management-section-loading"><Loading /></div>
          ) : (
          <>
            {remoteHosts.length > 0 ? (
              <div className="batch-management-filter-row">
                <label className="batch-management-filter-label" htmlFor="remote-host-selector">
                  Remote Host
                </label>
                <select
                  id="remote-host-selector"
                  className="batch-management-inline-select"
                  value={selectedRemoteHost?._id ?? ""}
                  onChange={(event) => setSelectedRemoteHostId(event.target.value)}
                >
                  {remoteHosts.map((host) => (
                    <option key={host?._id} value={host?._id ?? ""}>
                      {`${host?.Name ?? "Remote Batch Host"}${host?.IsDebugLocal ? " (Debug Local)" : ""}`}
                    </option>
                  ))}
                </select>
                <span className="batch-management-filter-meta">
                  Showing 1 selected host out of {remoteHosts.length}
                </span>
              </div>
            ) : null}
            <div className="batch-management-table-scroll">
              <table className="batch-management-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Route</th>
                    <th>Status</th>
                    <th>Endpoint</th>
                    <th>Batch</th>
                    <th>Heartbeat / Uptime</th>
                    <th>Lifecycle Detail</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRemoteHosts.map((host) => (
                    <tr
                      key={host?._id}
                      className={selectedRemoteHost?._id === host?._id ? "is-selected-row" : ""}
                      onClick={() => setSelectedRemoteHostId(host?._id ?? "")}
                    >
                    <td>
                      <div>{host?.Name ?? "Remote Batch Host"}</div>
                      <div className="table-secondary">
                        {host?.ClientShortName ?? "DEFAULT"}
                        {host?.IsDebugLocal ? " | Debug Local" : ""}
                        {host?.Environment ? ` | ${host.Environment}` : ""}
                      </div>
                    </td>
                    <td>
                      <div>{getRemoteHostRouteLabel(host)}</div>
                      <div className="table-secondary">
                        {host?.HostInstanceId ? `Instance ${host.HostInstanceId}` : "No active instance"}
                      </div>
                    </td>
                    <td>
                      <span className={`table-status-chip ${getStatusToneClass(getRemoteHostStateLabel(host))}`}>
                        {getRemoteHostDisplayStateLabel(host)}
                      </span>
                    </td>
                    <td>
                      <div>{host?.Endpoint ?? "-"}</div>
                      <div className="table-secondary">
                        {host?.Endpoint ? "Direct host endpoint" : "No endpoint registered"}
                      </div>
                    </td>
                    <td>
                      <div className="is-monospace">{host?.PoolId ?? "-"}</div>
                      <div className="table-secondary is-monospace">
                        Job: {host?.CurrentJobId ?? "-"} | Task: {host?.CurrentTaskId ?? "-"}
                      </div>
                    </td>
                    <td>
                      <div>{formatDateTime(host?.LastHeartbeatDateTime)}</div>
                      <div className="table-secondary">
                        {getRemoteHostUptimeLabel(host) || "Not started"}
                      </div>
                    </td>
                    <td>
                      <div>{formatDateTime(host?.LastErrorDateTime ?? host?.LastStartupRequestedDateTime)}</div>
                      <div className="table-secondary">
                        {getRemoteHostLifecycleDetail(host)}
                      </div>
                    </td>
                    <td>
                      <div className="row-actions">
                        {REMOTE_HOST_ACTIONS.map((action) => {
                          const actionKey = `${action.operationName ?? action.label}:${host?._id}`;
                          const effectiveActionKey = getEffectiveHostActionKey(host, action.operationName);
                          const isHostActionLoading = !action.isManualTest && effectiveActionKey === actionKey;
                          const isManualTestLoading = action.isManualTest && activeManualTestActionKey === `host-test:${host?._id}`;
                          return (
                            <button
                              key={action.operationName ?? action.label}
                              className="row-action-button"
                              type="button"
                              disabled={
                                action.isManualTest
                                  ? (isRunningManualTest || isRemoteHostStartupPending(host))
                                  : action.operationName
                                    ? (
                                      effectiveActionKey !== ""
                                      || (action.operationName !== "StartRemoteBatchHost" && isRemoteHostStartupPending(host))
                                    )
                                    : false
                              }
                              onClick={(event) => {
                                event.stopPropagation();

                                if (isHostActionLoading || isManualTestLoading) {
                                  return;
                                }

                                if (action.isManualTest) {
                                  openManualTestDialog(`host:${host?._id}`, `host-test:${host?._id}`);
                                  return;
                                }

                                void handleHostAction(host?._id, action.operationName);
                              }}
                            >
                              {isManualTestLoading ? (
                                <span className="row-action-content is-loading">
                                  <span className="row-action-spinner" />
                                  <span>{action.label}</span>
                                </span>
                              ) : isHostActionLoading ? (
                                <span className="row-action-content is-loading">
                                  <span className="row-action-spinner" />
                                  <span>{action.label}</span>
                                </span>
                              ) : (
                                action.label
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredRemoteHosts.length === 0 ? (
                <div className="table-empty">No remote host registrations were found.</div>
              ) : null}
            </div>
            {selectedRemoteHost ? (
              <div className="batch-management-detail-panel-container">
              <div className="batch-management-detail-panel">
              <div className="detail-panel-header">
                <div>
                  <h3>{selectedRemoteHost?.Name ?? "Remote Batch Host"}</h3>
                  <div className="detail-panel-subtitle">
                    {selectedRemoteHost?.ClientShortName ?? "DEFAULT"}
                    {selectedRemoteHost?.Environment ? ` | ${selectedRemoteHost.Environment}` : ""}
                    {selectedRemoteHost?.IsDebugLocal ? " | Debug Local" : ""}
                  </div>
                </div>
                <div className={`table-status-chip ${getStatusToneClass(getRemoteHostStateLabel(selectedRemoteHost))}`}>
                  {getRemoteHostDisplayStateLabel(selectedRemoteHost)}
                </div>
              </div>
              <div className="batch-management-detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Route</span>
                  <span className="detail-value">{getRemoteHostRouteLabel(selectedRemoteHost)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Endpoint</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.Endpoint ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Pool Profile</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.PoolProfileId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Pool Id</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.PoolId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Job Id</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.CurrentJobId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Task Id</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.CurrentTaskId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Host Instance</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.HostInstanceId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Started</span>
                  <span className="detail-value">{formatDateTime(selectedRemoteHost?.LastStartedDateTime)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Startup Request</span>
                  <span className="detail-value">{formatDateTime(selectedRemoteHost?.LastStartupRequestedDateTime)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Online</span>
                  <span className="detail-value">{formatDateTime(selectedRemoteHost?.LastOnlineDateTime)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Heartbeat</span>
                  <span className="detail-value">{formatDateTime(selectedRemoteHost?.LastHeartbeatDateTime)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Uptime</span>
                  <span className="detail-value">{getRemoteHostUptimeLabel(selectedRemoteHost) || "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Startup Failures</span>
                  <span className="detail-value">{selectedRemoteHost?.StartupFailureCount ?? 0}</span>
                </div>
                <div className="detail-item detail-item-wide">
                  <span className="detail-label">Status Detail</span>
                  <span className="detail-value">{selectedRemoteHost?.LastStatusDetail ?? "No lifecycle detail recorded."}</span>
                </div>
                <div className="detail-item detail-item-wide">
                  <span className="detail-label">Last Error</span>
                  <span className="detail-value">
                    {selectedRemoteHost?.LastErrorMessage
                      ? `${formatDateTime(selectedRemoteHost?.LastErrorDateTime)} | ${selectedRemoteHost.LastErrorMessage}`
                      : "No recent host error."}
                  </span>
                </div>
              </div>
              {selectedRemoteHost?.IsDebugLocal ? (
                <div className="detail-subpanel">
                  <div className="detail-subpanel-header">
                    <h4>Debug Local Command</h4>
                    <button
                      className="row-action-button"
                      type="button"
                      onClick={() => {
                        void handleCopyDebugLocalCommand();
                      }}
                    >
                      {copiedDebugLocalCommandHostId === selectedRemoteHost?._id ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <div className="detail-subpanel-meta">
                    Run this from the repo root to start the local host with the same explicit assignment Azure Batch would pass into the task.
                  </div>
                  <pre className="detail-command-block">{selectedRemoteHostDebugLocalCommand || "No debug-local command is available for this host."}</pre>
                </div>
              ) : null}
              <div className="detail-subpanel">
                <div className="detail-subpanel-header">
                  <h4>Startup Feed</h4>
                  <span>
                    {selectedRemoteHostStartupFeed?.isStreaming
                      ? "Live"
                      : selectedRemoteHostStartupFeed?.isComplete
                        ? "Complete"
                        : "Idle"}
                  </span>
                </div>
                {selectedRemoteHostStartupFeed?.eventId ? (
                  <div className="detail-subpanel-meta">
                    Event Id: <span className="is-monospace">{selectedRemoteHostStartupFeed.eventId}</span>
                  </div>
                ) : null}
                {!selectedRemoteHostStartupFeed?.messages?.length ? (
                  <div className="table-empty">No startup activity has been recorded for this host yet.</div>
                ) : (
                  <div className="detail-subpanel-scroll batch-management-startup-feed-scroll">
                    <div className="batch-management-startup-feed">
                      {selectedRemoteHostStartupFeed.messages.map((entry) => (
                        <div key={entry.id} className={`batch-management-startup-feed-entry is-${entry.uiMessageType ?? "status"}`}>
                          <div className="batch-management-startup-feed-header">
                            <span className={`table-status-chip ${getStatusToneClass(entry.uiMessageType ?? entry.stage ?? "status")}`}>
                              {entry.stage || entry.uiMessageType || "status"}
                            </span>
                            <span className="batch-management-startup-feed-time">{formatDateTime(entry.timestamp)}</span>
                          </div>
                          <div className="batch-management-startup-feed-message">{entry.message}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="detail-subpanel">
                <div className="detail-subpanel-header">
                  <h4>Related Active Tasks</h4>
                  <span>{`${selectedRemoteHostTasks.length} task(s)`}</span>
                </div>
                {selectedRemoteHostTasks.length === 0 ? (
                  <div className="table-empty">No active task rows currently match this host.</div>
                ) : (
                  <div className="detail-subpanel-scroll">
                  <div className="batch-management-table-scroll">
                    <table className="batch-management-table detail-subtable">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Job</th>
                          <th>Task</th>
                          <th>Event</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRemoteHostTasks.map((entry, index) => (
                          <tr key={`${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? index}`}>
                            <td>
                              <div>{entry?.DisplayName ?? "Task"}</div>
                              {entry?.Details ? (
                                <div className="table-secondary">{entry.Details}</div>
                              ) : null}
                            </td>
                            <td>
                              <span className={`table-status-chip ${getStatusToneClass(entry?.RuntimeStatus ?? entry?.SourceStatus)}`}>
                                {entry?.RuntimeStatus ?? entry?.SourceStatus ?? "-"}
                              </span>
                            </td>
                            <td className="is-monospace">{entry?.JobId ?? "-"}</td>
                            <td className="is-monospace">{entry?.TaskId ?? entry?.NodeId ?? "-"}</td>
                            <td className="is-monospace">{entry?.EventId ?? "-"}</td>
                            <td>
                              <div className="row-actions">
                                <button
                                  className="row-action-button"
                                  type="button"
                                  disabled={!entry?.EventId}
                                  onClick={() => handleOpenBackgroundEvent(entry?.EventId)}
                                >
                                  Open Event
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </div>
                )}
              </div>
              </div>
              </div>
            ) : null}
          </>
          )}
        </div>
      ) : null}

      {activeTab === "background" ? (
        renderExecutionControlCardGridSection({
          title: isExecutionControlCenter ? "Background Events" : "Background",
          description: "Cards show recent background events, processor mode, status, and last run timing. Use the Event icon on a card to open its Event panel.",
          countLabel: `${backgroundCardRows.length} item(s)`,
          rows: backgroundCardRows,
          selectedKey: selectedBackgroundKey,
          setSelectedKey: setSelectedBackgroundKey,
          rowKey: (entry) => entry.CardKey,
          cardFields: BACKGROUND_CARD_FIELDS,
          emptyContent: "No background events are currently visible.",
          error: sectionErrors.background,
          loading: loadingState.background,
          detailsTitle: (row) => row?.DisplayName ?? "Background Events",
          detailsSubtitle: (row) => [row?.CardStatus, row?.ItemType, row?.EventId].filter(Boolean).join(" | "),
          headerStatusResolver: (row) => getExecutionCardHeaderStatus(row?.CardStatus),
          activeFilterEnabled: hasExecutionFilterField(backgroundCardRows, "IsActive"),
          typeFilterEnabled: true,
          typeFilterFieldName: "CardStatus",
          typeFilterOptions: backgroundStatusFilterOptions,
          typeFilterAriaLabel: "Background event status filter",
          minCardWidth: "15rem",
          maxColumns: 3,
        })
      ) : null}

      {activeTab === "scheduled" ? (
        renderExecutionControlCardGridSection({
          title: "Scheduled Events",
          description: "Cards show scheduled background events with event status, schedule status, and next run time. Use the Event icon to open its panel; select a card to run, pause, resume, or cancel it.",
          countLabel: `${scheduledCardRows.length} item(s)`,
          rows: scheduledCardRows,
          selectedKey: selectedScheduledKey,
          setSelectedKey: setSelectedScheduledKey,
          rowKey: (entry) => entry.CardKey,
          cardFields: SCHEDULED_CARD_FIELDS,
          emptyContent: "No scheduled events are currently visible.",
          error: sectionErrors.scheduled,
          loading: loadingState.scheduled,
          detailsTitle: (row) => row?.DisplayName ?? "Scheduled Events",
          detailsSubtitle: (row) => [row?.EventStatus, row?.ScheduleStatus, row?.JobScheduleId].filter(Boolean).join(" | "),
          headerStatusResolver: (row) => getExecutionCardHeaderStatus(row?.EventStatus),
          activeFilterEnabled: hasExecutionFilterField(scheduledCardRows, "IsActive"),
          typeFilterEnabled: true,
          typeFilterFieldName: "EventStatus",
          typeFilterOptions: scheduledStatusFilterOptions,
          typeFilterAriaLabel: "Scheduled event status filter",
          minCardWidth: "15rem",
          maxColumns: 3,
        })
      ) : null}

      {activeTab === "pools" ? (
        renderExecutionControlCardGridSection({
          title: isExecutionControlCenter ? "Pool Profiles" : "Pools",
          description: "Configured and Profile Disabled describe the saved Pool Profile. Azure status on each card describes the live pool; an online pool with zero nodes remains Online.",
          countLabel: `${poolProfileCardRows.length} profile(s)`,
          rows: poolProfileCardRows,
          selectedKey: selectedPoolProfileKey,
          setSelectedKey: setSelectedPoolProfileKey,
          rowKey: (entry) => entry.CardKey,
          cardFields: POOL_PROFILE_CARD_FIELDS,
          emptyContent: "No pool profiles match the selected configuration filter.",
          error: sectionErrors.pools,
          loading: loadingState.pools,
          detailsTitle: (row) => row?.Name ?? "Pool Profile",
          detailsSubtitle: (row) => [row?.AzureStatus, row?.PoolId, row?.IsDefault ? "Default" : "", row?.Version ? `v${row.Version}` : ""].filter(Boolean).join(" | "),
          detailsFormatterKey: "pool-profile",
          headerStatusResolver: (row) => getPoolProfileCardHeaderStatus(row),
          activeFilterEnabled: true,
          activeFilterOptions: POOL_PROFILE_CONFIG_FILTER_OPTIONS,
          activeFilterAriaLabel: "Pool profile configuration filter",
          typeFilterEnabled: true,
          typeFilterFieldName: "IsDefault",
          typeFilterOptions: poolProfileDefaultFilterOptions,
          typeFilterAriaLabel: "Pool profile default filter",
          minCardWidth: "15rem",
          maxColumns: 3,
        })
      ) : null}

      {activeTab === "tests" ? (
        <div className="batch-management-table-card convoke-panel batch-management-resource-card-section">
          <div className="table-header convoke-section-title__row convoke-section-title__row--split">
            <h2>Manual Test History</h2>
            <span>{`${manualTestHistory.length} result(s)`}</span>
          </div>
          <CardGridControl
            rows={manualTestHistoryCardRows}
            cardFields={MANUAL_TEST_HISTORY_CARD_FIELDS}
            rowKey={(entry) => entry.CardKey}
            onRowClick={(entry) => {
              setSelectedManualTestHistoryId(entry.id);
              openRowDetailsDialog(
                entry.DisplayName,
                [entry.CardScope, entry.CardChecked].filter(Boolean).join(" | "),
                entry,
                "manual-test-history"
              );
            }}
            headerStatusResolver={(entry) => getManualTestCardHeaderStatus(entry.CardStatus)}
            headerIconActions={[{
              key: "open-event",
              Title: "Open Event",
              iconKey: "opennewwindow",
              iconSize: "1.25rem",
              Visible: (entry) => String(entry?.eventId ?? "").trim() !== "",
              ActionCallback: (entry) => handleOpenBackgroundEvent(entry?.eventId),
            }]}
            selectedHeaderIconActions={[{
              key: "open-event",
              Title: "Open Event",
              iconKey: "opennewwindow",
              iconSize: "1.25rem",
              Visible: (entry) => String(entry?.eventId ?? "").trim() !== "",
              ActionCallback: (entry) => handleOpenBackgroundEvent(entry?.eventId),
            }]}
            selectable
            searchable
            activeFilterEnabled={false}
            emptyContent="No manual tests have been recorded yet. Use Run Manual Test to run one."
            minCardWidth="16rem"
            maxColumns={3}
          />
        </div>
      ) : null}

      {activeTab === "diagnostics" ? (
        <>
          <section className="batch-management-panel convoke-panel batch-management-summary-panel">
            <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
              <h2>Diagnostics Summary</h2>
              <span>{lastRefreshDateTime ? `Last refresh ${formatDateTime(lastRefreshDateTime)}` : "Not refreshed yet"}</span>
            </div>
            <div className="batch-management-card-grid batch-management-summary-metrics">
              <div className="batch-management-card convoke-panel convoke-metric-card">
                <div className="card-label">Last Refresh</div>
                <div className="card-value">
                  {lastRefreshDateTime ? formatDateTime(lastRefreshDateTime) : "-"}
                </div>
                <div className="card-meta">Latest full screen refresh</div>
              </div>
              {!isExecutionControlCenter ? (
                <>
                  <div className="batch-management-card convoke-panel convoke-metric-card">
                    <div className="card-label">Stale Hosts</div>
                    <div className="card-value">{diagnosticsOverview.staleHostCount}</div>
                    <div className="card-meta">Heartbeat older than {REMOTE_HOST_STALE_SECONDS}s</div>
                  </div>
                  <div className="batch-management-card convoke-panel convoke-metric-card">
                    <div className="card-label">Draining Hosts</div>
                    <div className="card-value">{diagnosticsOverview.drainingHostCount}</div>
                    <div className="card-meta">Hosts blocking new remote work</div>
                  </div>
                  <div className="batch-management-card convoke-panel convoke-metric-card">
                    <div className="card-label">Startup Failures</div>
                    <div className="card-value">{diagnosticsOverview.startupFailureCount}</div>
                    <div className="card-meta">Recorded startup failures across visible hosts</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="batch-management-card convoke-panel convoke-metric-card">
                    <div className="card-label">Background Failures</div>
                    <div className="card-value">{summary?.BackgroundFailedLast24HoursCount ?? 0}</div>
                    <div className="card-meta">Failed background events in the last 24 hours</div>
                  </div>
                  <div className="batch-management-card convoke-panel convoke-metric-card">
                    <div className="card-label">Azure Batch Work Rows</div>
                    <div className="card-value">{activeTasks.length}</div>
                    <div className="card-meta">Rows from LoadBatchActiveTasks, including fallback background event rows.</div>
                  </div>
                  <div className="batch-management-card convoke-panel convoke-metric-card">
                    <div className="card-label">Scheduled Rows</div>
                    <div className="card-value">{scheduledWork.length}</div>
                    <div className="card-meta">Rows loaded from LoadBatchScheduledWork</div>
                  </div>
                  <div className="batch-management-card convoke-panel convoke-metric-card">
                    <div className="card-label">Configured Pool Profiles</div>
                    <div className="card-value">{poolProfiles.length}</div>
                    <div className="card-meta">Rows loaded from LoadBatchPools</div>
                  </div>
                  <div className="batch-management-card convoke-panel convoke-metric-card">
                    <div className="card-label">Azure Pools</div>
                    <div className="card-value">{loadingState.summary ? "..." : (summary?.AzurePoolCount ?? 0)}</div>
                    <div className="card-meta">Physical pools returned by Azure Batch</div>
                  </div>
                </>
              )}
            </div>
          </section>

          {!isExecutionControlCenter ? (
            <div className="batch-management-panel-grid">
            {!isExecutionControlCenter ? (
              <>
            <section className="batch-management-panel convoke-panel">
              <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
                <h2>Remote Host Startup Diagnostics</h2>
                <span>{`${startupDiagnosticHosts.length} host(s)`}</span>
              </div>
              <div className="mini-list convoke-record-list">
                {startupDiagnosticHosts.map((host) => (
                  <div key={host?._id} className="mini-list-row convoke-record-row">
                    <div>
                      <div className="mini-list-title convoke-record-row__title">{host?.Name ?? "Remote Batch Host"}</div>
                      <div className="mini-list-detail convoke-record-row__detail">
                        {host?.ClientShortName ?? "DEFAULT"}
                        {host?.LastStartupRequestedDateTime ? ` | Requested ${formatDateTime(host.LastStartupRequestedDateTime)}` : ""}
                        {host?.LastOnlineDateTime ? ` | Online ${formatDateTime(host.LastOnlineDateTime)}` : ""}
                      </div>
                      <div className="mini-list-detail convoke-record-row__detail">
                        {host?.LastStatusDetail ?? host?.LastErrorMessage ?? "No startup detail recorded."}
                      </div>
                    </div>
                    <div className={`mini-list-status convoke-record-row__status ${getStatusToneClass(getRemoteHostStateLabel(host))}`}>
                      {getRemoteHostStateLabel(host)}
                      {host?.StartupFailureCount ? ` (${host.StartupFailureCount})` : ""}
                    </div>
                  </div>
                ))}
                {startupDiagnosticHosts.length === 0 ? (
                  <div className="mini-list-empty convoke-record-list__empty">No startup diagnostics are currently recorded.</div>
                ) : null}
              </div>
            </section>

            <section className="batch-management-panel convoke-panel">
              <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
                <h2>Stale Remote Hosts</h2>
                <span>{`${staleRemoteHosts.length} host(s)`}</span>
              </div>
              <div className="mini-list convoke-record-list">
                {staleRemoteHosts.map((host) => (
                  <div key={host?._id} className="mini-list-row convoke-record-row">
                    <div>
                      <div className="mini-list-title convoke-record-row__title">{host?.Name ?? "Remote Batch Host"}</div>
                      <div className="mini-list-detail convoke-record-row__detail">
                        {host?.ClientShortName ?? "DEFAULT"} | {formatDateTime(host?.LastHeartbeatDateTime)}
                      </div>
                    </div>
                    <div className="mini-list-status is-warn">Stale</div>
                  </div>
                ))}
                {staleRemoteHosts.length === 0 ? (
                  <div className="mini-list-empty convoke-record-list__empty">No stale remote hosts are currently visible.</div>
                ) : null}
              </div>
            </section>
              </>
            ) : null}

            </div>
          ) : null}

          <section className="batch-management-panel convoke-panel batch-management-diagnostics-snapshots">
            <div className="panel-header convoke-section-title__row convoke-section-title__row--split">
              <h2>Diagnostics Snapshots</h2>
              <span>{`${diagnosticSnapshotTabs.length} snapshot(s)`}</span>
            </div>
            <div className="batch-management-diagnostics-tabs form-wrapper form-wrapper-tabs-top">
              <div className="form-tabs-container form-tabs-container-top" role="tablist" aria-label="Diagnostics snapshots">
                {diagnosticSnapshotTabs.map((tab) => {
                  const isActive = activeDiagnosticSnapshot?.key === tab.key;
                  return (
                    <button
                      key={tab.key}
                      id={`diagnostics-snapshot-${tab.key}-tab`}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`diagnostics-snapshot-${tab.key}-panel`}
                      className={`form-tab-rows ${isActive ? "form-tab-row-active" : ""}`}
                      onClick={() => setActiveDiagnosticSnapshotTab(tab.key)}
                    >
                      <span className="form-tab">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
              {activeDiagnosticSnapshot ? (
                <div
                  id={`diagnostics-snapshot-${activeDiagnosticSnapshot.key}-panel`}
                  role="tabpanel"
                  aria-labelledby={`diagnostics-snapshot-${activeDiagnosticSnapshot.key}-tab`}
                  className="batch-management-diagnostics-tab-panel form-fields-tab"
                >
                  <div className="panel-header convoke-section-title__row convoke-section-title__row--split batch-management-diagnostics-tab-title">
                    <h3>{activeDiagnosticSnapshot.title}</h3>
                    <span>{activeDiagnosticSnapshot.meta}</span>
                  </div>
                  {renderDiagnosticSnapshotPayload(activeDiagnosticSnapshot.payload, {
                    mergeIdentifiersIntoSummary: activeDiagnosticSnapshot.key === "summary",
                    isAzureBatchWorkSnapshot: activeDiagnosticSnapshot.key === "batch-resources",
                  })}
                </div>
              ) : null}
            </div>
          </section>
        </>
      ) : null}
      </div>
      </div>
    </div>
  );
};

export default BatchManagement;
